import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { PAYMENT_METHODS_INCASSO, PAYMENT_METHODS_USCITA } from '../constants/paymentMethods';
import MutuiClientSection from '../components/clients/MutuiClientSection';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MultiSelect } from '../components/ui/multi-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, User, Phone, Mail, MapPin, Building2, FileImage, File, Eye, Upload, X, Home, Car, Briefcase, FileText, Shield, ChevronRight, ChevronLeft, CheckCircle, RotateCcw, Settings, AlertTriangle, AlertCircle, PauseCircle, Replace, Ban, PlayCircle, ArrowUpDown, ArrowUp, ArrowDown, MessageCircle, RefreshCw, Archive, DollarSign, CreditCard, Banknote, Clock, Check, ShieldPlus, UserPlus, Copy, Loader2, FolderOpen, ZoomIn, ZoomOut, Download, Printer } from 'lucide-react';

// WeChat icon component (custom SVG)
const WeChatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.045.247.247 0 0 0 .241-.245c0-.06-.024-.12-.04-.177l-.327-1.233a.49.49 0 0 1 .177-.554C23.036 18.295 24 16.612 24 14.79c0-3.325-2.872-5.98-6.062-5.932zm-2.745 2.793c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.842 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982z"/>
  </svg>
);

// MutuiClientSection is now imported from components/clients/MutuiClientSection.js

// Google Maps API Key
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Mapping Province italiane (nome completo -> sigla)
const PROVINCE_MAPPING = {
  'Metropolitan City of Florence': 'FI', 'Città Metropolitana di Firenze': 'FI', 'Florence': 'FI', 'Firenze': 'FI',
  'Metropolitan City of Milan': 'MI', 'Città Metropolitana di Milano': 'MI', 'Milan': 'MI', 'Milano': 'MI',
  'Metropolitan City of Rome': 'RM', 'Città Metropolitana di Roma': 'RM', 'Rome': 'RM', 'Roma': 'RM',
  'Metropolitan City of Naples': 'NA', 'Città Metropolitana di Napoli': 'NA', 'Naples': 'NA', 'Napoli': 'NA',
  'Metropolitan City of Turin': 'TO', 'Città Metropolitana di Torino': 'TO', 'Turin': 'TO', 'Torino': 'TO',
  'Metropolitan City of Genoa': 'GE', 'Città Metropolitana di Genova': 'GE', 'Genoa': 'GE', 'Genova': 'GE',
  'Metropolitan City of Bologna': 'BO', 'Città Metropolitana di Bologna': 'BO', 'Bologna': 'BO',
  'Metropolitan City of Venice': 'VE', 'Città Metropolitana di Venezia': 'VE', 'Venice': 'VE', 'Venezia': 'VE',
  'Metropolitan City of Bari': 'BA', 'Città Metropolitana di Bari': 'BA', 'Bari': 'BA',
  'Metropolitan City of Palermo': 'PA', 'Città Metropolitana di Palermo': 'PA', 'Palermo': 'PA',
  'Metropolitan City of Catania': 'CT', 'Città Metropolitana di Catania': 'CT', 'Catania': 'CT',
  'Metropolitan City of Cagliari': 'CA', 'Città Metropolitana di Cagliari': 'CA', 'Cagliari': 'CA',
  'Metropolitan City of Messina': 'ME', 'Città Metropolitana di Messina': 'ME', 'Messina': 'ME',
  'Metropolitan City of Reggio Calabria': 'RC', 'Città Metropolitana di Reggio Calabria': 'RC', 'Reggio Calabria': 'RC',
  'Province of Prato': 'PO', 'Provincia di Prato': 'PO', 'Prato': 'PO',
  'Province of Pistoia': 'PT', 'Provincia di Pistoia': 'PT', 'Pistoia': 'PT',
  'Province of Lucca': 'LU', 'Provincia di Lucca': 'LU', 'Lucca': 'LU',
  'Province of Pisa': 'PI', 'Provincia di Pisa': 'PI', 'Pisa': 'PI',
  'Province of Livorno': 'LI', 'Provincia di Livorno': 'LI', 'Livorno': 'LI',
  'Province of Siena': 'SI', 'Provincia di Siena': 'SI', 'Siena': 'SI',
  'Province of Arezzo': 'AR', 'Provincia di Arezzo': 'AR', 'Arezzo': 'AR',
  'Province of Grosseto': 'GR', 'Provincia di Grosseto': 'GR', 'Grosseto': 'GR',
  'Province of Massa-Carrara': 'MS', 'Provincia di Massa-Carrara': 'MS', 'Massa-Carrara': 'MS',
  'Province of Bergamo': 'BG', 'Provincia di Bergamo': 'BG', 'Bergamo': 'BG',
  'Province of Brescia': 'BS', 'Provincia di Brescia': 'BS', 'Brescia': 'BS',
  'Province of Como': 'CO', 'Provincia di Como': 'CO', 'Como': 'CO',
  'Province of Varese': 'VA', 'Provincia di Varese': 'VA', 'Varese': 'VA',
  'Province of Monza and Brianza': 'MB', 'Provincia di Monza e Brianza': 'MB', 'Monza e Brianza': 'MB',
  'Province of Padua': 'PD', 'Provincia di Padova': 'PD', 'Padova': 'PD', 'Padua': 'PD',
  'Province of Verona': 'VR', 'Provincia di Verona': 'VR', 'Verona': 'VR',
  'Province of Vicenza': 'VI', 'Provincia di Vicenza': 'VI', 'Vicenza': 'VI',
  'Province of Treviso': 'TV', 'Provincia di Treviso': 'TV', 'Treviso': 'TV',
  'Province of Perugia': 'PG', 'Provincia di Perugia': 'PG', 'Perugia': 'PG',
  'Province of Terni': 'TR', 'Provincia di Terni': 'TR', 'Terni': 'TR'
};

// Address Autocomplete component using Google Places API
const AddressAutocomplete = ({ onPlaceSelect, placeholder = "Cerca indirizzo...", defaultValue = "", value = "" }) => {
  const inputRef = useRef(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [inputValue, setInputValue] = useState(value || defaultValue || '');
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['address_components', 'formatted_address', 'geometry'],
      componentRestrictions: { country: 'it' }, // Restrict to Italy
      types: ['address']
    };

    const autoCompleteInstance = new places.Autocomplete(inputRef.current, options);
    setAutocomplete(autoCompleteInstance);

    // Set default value if provided
    if (defaultValue && inputRef.current) {
      inputRef.current.value = defaultValue;
    }

    // CRITICAL: Prevent pac-container clicks from closing modal
    // Only stop propagation, do NOT prevent default - that would break Google Places selection
    const preventModalClose = (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      // DO NOT call e.preventDefault() - it blocks Google Places API from processing click selections
      setIsSelecting(true);
      // Reset selecting state after a short delay
      setTimeout(() => setIsSelecting(false), 500);
    };

    // Fix z-index and attach event blockers to pac-container
    const fixPacContainer = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        container.style.zIndex = '99999';
        container.style.pointerEvents = 'auto';
        
        // Remove and re-add listeners to prevent duplicates
        container.removeEventListener('mousedown', preventModalClose, true);
        container.removeEventListener('click', preventModalClose, true);
        container.removeEventListener('touchstart', preventModalClose, true);
        container.removeEventListener('pointerdown', preventModalClose, true);
        
        container.addEventListener('mousedown', preventModalClose, true);
        container.addEventListener('click', preventModalClose, true);
        container.addEventListener('touchstart', preventModalClose, true);
        container.addEventListener('pointerdown', preventModalClose, true);
      });
    };
    
    // Apply fix on focus and input events
    const handleFocus = () => {
      setTimeout(fixPacContainer, 50);
      setTimeout(fixPacContainer, 200);
      setTimeout(fixPacContainer, 500);
    };
    
    const handleInput = () => {
      setTimeout(fixPacContainer, 50);
      setTimeout(fixPacContainer, 200);
    };
    
    inputRef.current.addEventListener('focus', handleFocus);
    inputRef.current.addEventListener('input', handleInput);

    // Initial fix
    setTimeout(fixPacContainer, 300);

    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autoCompleteInstance);
      }
      // Cleanup
      if (inputRef.current) {
        inputRef.current.removeEventListener('focus', handleFocus);
        inputRef.current.removeEventListener('input', handleInput);
      }
    };
  }, [places, defaultValue]);

  useEffect(() => {
    if (!autocomplete) return;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      console.log('Google Places - place_changed triggered:', place);
      
      if (place && place.address_components) {
        const addressData = {
          address: '',
          address_number: '',
          city: '',
          city_provincia: '',
          postal_code: '',
          latitude: null,
          longitude: null
        };

        // Extract coordinates (geometry)
        if (place.geometry && place.geometry.location) {
          addressData.latitude = place.geometry.location.lat();
          addressData.longitude = place.geometry.location.lng();
          console.log('Extracted coordinates:', addressData.latitude, addressData.longitude);
        }

        // Extract route (street name)
        const route = place.address_components.find(c => c.types.includes('route'));
        if (route) {
          addressData.address = route.long_name;
        }

        // Extract street number
        const streetNumber = place.address_components.find(c => c.types.includes('street_number'));
        if (streetNumber) {
          addressData.address_number = streetNumber.long_name;
        }

        // Extract city (locality or administrative_area_level_3)
        const locality = place.address_components.find(c => c.types.includes('locality'));
        const adminArea3 = place.address_components.find(c => c.types.includes('administrative_area_level_3'));
        if (locality) {
          addressData.city = locality.long_name;
        } else if (adminArea3) {
          addressData.city = adminArea3.long_name;
        }

        // Extract province (administrative_area_level_2)
        const province = place.address_components.find(c => c.types.includes('administrative_area_level_2'));
        if (province) {
          // Check if short_name is already 2 letters (sigla)
          if (province.short_name && province.short_name.length === 2) {
            addressData.city_provincia = province.short_name.toUpperCase();
          } else {
            // Try mapping from long_name or short_name
            const mapped = PROVINCE_MAPPING[province.long_name] || PROVINCE_MAPPING[province.short_name];
            if (mapped) {
              addressData.city_provincia = mapped;
            } else {
              // Fallback: use short_name if available
              addressData.city_provincia = province.short_name || '';
            }
          }
        }

        // Extract postal code
        const postalCode = place.address_components.find(c => c.types.includes('postal_code'));
        if (postalCode) {
          addressData.postal_code = postalCode.long_name;
        }

        console.log('Extracted address data:', addressData);
        
        // Update local input value to show ONLY the street name (number goes to separate field)
        const streetOnly = addressData.address || '';
        setInputValue(streetOnly);
        if (inputRef.current) {
          inputRef.current.value = streetOnly;
        }
        
        // Callback with extracted data
        if (onPlaceSelect) {
          onPlaceSelect(addressData);
        }
      } else {
        console.log('No address_components in place - user may have typed manually:', place);
        // If user typed manually without selecting, just update the address field
        if (inputRef.current && onPlaceSelect) {
          setInputValue(inputRef.current.value);
          onPlaceSelect({ 
            address: inputRef.current.value, 
            address_number: '', 
            city: '', 
            city_provincia: '', 
            postal_code: '' 
          });
        }
      }
    });

    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [autocomplete, onPlaceSelect]);

  // Sync external value prop changes
  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
      if (inputRef.current) {
        inputRef.current.value = value;
      }
    }
  }, [value]);

  // Prevent form submission when pressing Enter in the autocomplete field
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submit while selecting from dropdown
    }
  };

  // Handle manual input changes
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      data-testid="address-autocomplete"
    />
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyClient = {
  last_name: '',
  first_name: '',
  gender: '',
  birth_date: '',
  birth_abroad: true,  // Default: Nato all'estero ATTIVO
  birth_comune: '',
  birth_provincia: '',
  birth_country: 'Cina',  // Default: Cina pre-compilato
  email: '',
  phone: '',
  whatsapp_contact: '',
  wechat_contact: '',
  fiscal_code: '',
  address: '',
  address_number: '',
  city: '',
  city_provincia: '',
  postal_code: '',
  is_company: false,
  company_name: '',
  vat_number: '',
  pec: '',
  // Company address fields (sede legale)
  company_address: '',
  company_address_number: '',
  company_city: '',
  company_provincia: '',
  company_postal_code: '',
  company_latitude: null,
  company_longitude: null,
  agent_source: 'JV SERVICE', // Default pre-compilato
  notes: '',
  tags: []
};

// Province italiane
const PROVINCE = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", "BR", "BS", "BT", "BZ",
  "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ", "EN", "FC", "FE", "FG", "FI", "FM", "FR",
  "GE", "GO", "GR", "IM", "IS", "KR", "LC", "LE", "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT",
  "NA", "NO", "NU", "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ",
  "RA", "RC", "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SV",
  "TA", "TE", "TN", "TO", "TP", "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

// Nazioni estere più comuni
const NAZIONI = [
  "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", "Belgio", "Bolivia", "Bosnia Erzegovina", "Brasile",
  "Bulgaria", "Camerun", "Canada", "Cile", "Cina", "Colombia", "Corea del Sud", "Costa d'Avorio", "Croazia", "Cuba",
  "Danimarca", "Ecuador", "Egitto", "El Salvador", "Emirati Arabi Uniti", "Estonia", "Etiopia", "Filippine", "Finlandia", "Francia",
  "Germania", "Ghana", "Giappone", "Grecia", "Guatemala", "Honduras", "India", "Indonesia", "Iran", "Iraq",
  "Irlanda", "Israele", "Kenya", "Kosovo", "Lettonia", "Libano", "Libia", "Lituania", "Lussemburgo",
  "Macedonia del Nord", "Marocco", "Messico", "Moldavia", "Monaco", "Montenegro", "Nigeria", "Norvegia", "Nuova Zelanda", "Paesi Bassi",
  "Pakistan", "Paraguay", "Perù", "Polonia", "Portogallo", "Regno Unito", "Repubblica Ceca", "Repubblica Dominicana", "Romania",
  "Russia", "San Marino", "Senegal", "Serbia", "Siria", "Slovacchia", "Slovenia", "Spagna", "Sri Lanka", "Stati Uniti",
  "Sudafrica", "Svezia", "Svizzera", "Thailandia", "Tunisia", "Turchia", "Ucraina", "Ungheria", "Uruguay", "Venezuela", "Vietnam"
];

const Clients = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState(null);
  const [filterAgentSource, setFilterAgentSource] = useState([]); // Cambiato a array per multi-select
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyClient);
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docDescription, setDocDescription] = useState('');
  const [docDescriptionGestione, setDocDescriptionGestione] = useState('');
  const [availableCaps, setAvailableCaps] = useState([]);
  const [capLoading, setCapLoading] = useState(false);
  // Company (Azienda) CAP state
  const [companyAvailableCaps, setCompanyAvailableCaps] = useState([]);
  const [companyCapLoading, setCompanyCapLoading] = useState(false);
  const [agents, setAgents] = useState([]);  // Lista agenti/fonti per dropdown
  const [agentSourcesDialogOpen, setAgentSourcesDialogOpen] = useState(false);  // Gestione fonti
  const [newSourceName, setNewSourceName] = useState('');  // Nome nuova fonte
  const [deleteConfirmSource, setDeleteConfirmSource] = useState(null);  // Fonte da eliminare con conferma
  const [deleteClientConfirm, setDeleteClientConfirm] = useState(null);  // Cliente da eliminare con conferma
  const [deleteStep, setDeleteStep] = useState(1);  // Step 1: conferma base, Step 2: conferma vincoli
  
  // Controllo duplicati Codice Fiscale
  const [duplicateCfClient, setDuplicateCfClient] = useState(null);  // Cliente esistente con stesso CF
  const [checkingCf, setCheckingCf] = useState(false);  // Loading check CF
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');  // Default: data creazione
  const [sortDirection, setSortDirection] = useState('desc');  // Default: decrescente (più recenti prima)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const CLIENTS_PER_PAGE = 30;
  
  // Accounting state for policy management
  const [accountingAction, setAccountingAction] = useState(null); // 'incasso' | 'uscita' | 'provv_entrata' | 'provv_uscita'
  const [accountingFormData, setAccountingFormData] = useState({
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'contanti_drop',
    commission_in: 0,  // Default 0, sempre salvabile
    commission_out: 0, // Default 0, sempre salvabile
    commission_in_status: 'da_definire', // da_definire, incassato
    commission_out_status: 'da_definire', // da_definire, saldato
    notes: ''
  });
  const [policyPayments, setPolicyPayments] = useState([]); // Pagamenti della polizza corrente
  
  // Centro Pagamenti e Provvigioni modal state
  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
  const [paymentsModalTab, setPaymentsModalTab] = useState('unificato'); // 'unificato' | 'incasso' | 'uscita' | 'provv_entrata' | 'provv_uscita'
  const [editingPayment, setEditingPayment] = useState(null); // Payment being edited
  const [resetPaymentsConfirmOpen, setResetPaymentsConfirmOpen] = useState(false); // Confirm modal per azzerare movimenti
  const [resettingPayments, setResettingPayments] = useState(false); // Loading state per reset
  
  // City autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [citySearching, setCitySearching] = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  
  // Agencies & Insurance Companies management
  const [agencies, setAgencies] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [agenciesDialogOpen, setAgenciesDialogOpen] = useState(false);
  const [companiesDialogOpen, setCompaniesDialogOpen] = useState(false);
  const [newAgencyName, setNewAgencyName] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  // Edit mode for agencies/companies
  const [editingAgencyId, setEditingAgencyId] = useState(null);
  const [editingAgencyName, setEditingAgencyName] = useState('');
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [editingCompanyName, setEditingCompanyName] = useState('');
  
  // Assets state
  const [clientAssets, setClientAssets] = useState([]);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetType, setAssetType] = useState('');
  const [assetFormData, setAssetFormData] = useState({});

  // Comuni autocomplete state (per Salute - persone assicurate)
  const [comuniSuggestions, setComuniSuggestions] = useState({});  // {personaIndex: [{nome, provincia, codice_belfiore}]}
  const [showComuniDropdown, setShowComuniDropdown] = useState({});  // {personaIndex: boolean}

  // Comuni autocomplete state (per Anagrafica Cliente - Comune di Nascita)
  const [birthComuneSuggestions, setBirthComuneSuggestions] = useState([]);
  const [showBirthComuneDropdown, setShowBirthComuneDropdown] = useState(false);

  // Asset Details + Quotes state
  const [assetDetailsOpen, setAssetDetailsOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetQuotes, setAssetQuotes] = useState([]);
  const [assetPolicies, setAssetPolicies] = useState([]);
  const [policyClaims, setPolicyClaims] = useState({}); // Mappa policy_id -> { claims_count, last_claim_date, ... }
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);  // Quote being edited
  const [quoteFormData, setQuoteFormData] = useState({
    agent: '', company: '', premium: '', coverage_types: [], payment_frequency: 'annuale', driving_type: 'esperta', notes: ''
  });

  // Policy editing state
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyFormData, setPolicyFormData] = useState({
    policy_number: '', company: '', agent: '', payment_date: '', start_date: '', end_date: '', 
    premium_amount: '', coverage_details: '', status: 'attiva'
  });

  // Policy Management State (Gestione Polizza)
  const [policyManagementOpen, setPolicyManagementOpen] = useState(false);
  const [managingPolicy, setManagingPolicy] = useState(null);
  const [managementAction, setManagementAction] = useState(null); // 'sinistro', 'sospensione', 'sostituzione', 'interruzione'
  // Sinistro form
  const [claimFormData, setClaimFormData] = useState({ claim_date: '', description: '' });
  // Sospensione form
  const [suspendFormData, setSuspendFormData] = useState({ suspension_start: '', suspension_end: '' });
  // Sostituzione form
  const [replaceFormData, setReplaceFormData] = useState({ replace_type: 'targa', new_targa: '', new_asset_id: '' });
  // Interruzione form
  const [interruptFormData, setInterruptFormData] = useState({ interruption_date: '', refund_amount: '' });
  // Veicoli disponibili per sostituzione
  const [availableVehicles, setAvailableVehicles] = useState([]);

  // Dialog Riattivazione Polizza (con aggiornamento scadenza)
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivatingPolicy, setReactivatingPolicy] = useState(null);
  const [newEndDateForReactivation, setNewEndDateForReactivation] = useState('');

  // Storico Sospensioni Asset
  const [assetSuspensions, setAssetSuspensions] = useState([]);
  const [suspensionDialogOpen, setSuspensionDialogOpen] = useState(false);
  const [editingSuspension, setEditingSuspension] = useState(null);
  const [suspensionFormData, setSuspensionFormData] = useState({
    data_richiesta: '', data_decorrenza: '', data_fine: ''
  });

  // Conversion to Policy Modal
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [quoteToConvert, setQuoteToConvert] = useState(null);
  const [conversionStartDate, setConversionStartDate] = useState('');

  const emptyAssetCasa = { indirizzo: '', numero_civico: '', city: '', provincia: '', cap: '', dati_catastali: '', notes: '' };
  const emptyAssetAttivita = { ragione_sociale: '', partita_iva: '', tipo_attivita: '', indirizzo: '', numero_civico: '', city: '', provincia: '', cap: '', notes: '' };
  const emptyAssetAuto = { targa: '', data_immatricolazione: '', data_acquisto: '', scadenza_copertura: '', alimentazione: '', marca: '', modello: '', vehicle_status: 'in_possesso', notes: '' };
  const emptyAssetSalute = { persone_assicurate: [], notes: '' };
  const emptyPersonaAssicurata = { nome: '', cognome: '', data_nascita: '', sesso: '', codice_fiscale: '', luogo_nascita: '', provincia_nascita: '' };

  // Asset CAP lookup state
  const [assetAvailableCaps, setAssetAvailableCaps] = useState([]);
  const [assetCapLoading, setAssetCapLoading] = useState(false);

  // Auto marca/modello autocomplete state
  const [marcaSuggestions, setMarcaSuggestions] = useState([]);
  const [modelloSuggestions, setModelloSuggestions] = useState([]);
  const [showMarcaDropdown, setShowMarcaDropdown] = useState(false);
  const [showModelloDropdown, setShowModelloDropdown] = useState(false);

  // Coverage types per asset type
  const COVERAGE_TYPES = {
    casa: ['Responsabilità civile', 'Tutela legale', 'Fabbricato', 'Contenuto', 'Furto e rapina', 'Catastrofi naturali', 'Animali domestici'],
    attivita: ['Responsabilità civile', 'Tutela legale', 'Fabbricato', 'Contenuto', 'Furto e rapina', 'Catastrofi naturali'],
    auto: ['RC Auto', 'Furto', 'Incendio', 'Kasko', 'Cristalli', 'Atti Vandalici', 'Carrozzeria Convenzionata', 'Assistenza Stradale', 'Tutela Legale', 'Infortuni Conducente', 'Eventi Naturali'],
    salute: ['Spese mediche', 'Diaria da ricovero', 'Invalidità permanente']
  };

  // Helper to get all coverages as flat array (for backwards compatibility)
  const getCoveragesForAssetType = (assetType) => {
    const coverageData = COVERAGE_TYPES[assetType];
    if (!coverageData) return [];
    if (Array.isArray(coverageData)) return coverageData;
    if (coverageData.groups) {
      return coverageData.groups.flatMap(g => g.coverages);
    }
    return [];
  };

  const ALIMENTAZIONI = ['Benzina', 'Diesel', 'GPL', 'Metano', 'Elettrico', 'Ibrido Benzina', 'Ibrido Diesel'];

  useEffect(() => {
    fetchClients();
  }, [searchTerm, filterCompany, filterAgentSource]);

  // Deep linking: apre automaticamente cliente e/o bene in base ai parametri URL
  useEffect(() => {
    const openClientId = searchParams.get('open');
    const openAssetId = searchParams.get('asset');
    
    if (openClientId && clients.length > 0) {
      const client = clients.find(c => c.id === openClientId);
      if (client) {
        // Apri il modal dettagli cliente
        setSelectedClient(client);
        setDetailsOpen(true);
        fetchClientAssets(client.id);
        
        // Se c'è anche un asset_id, aprilo dopo un breve delay
        if (openAssetId) {
          setTimeout(async () => {
            try {
              const response = await axios.get(`${API}/assets/${openAssetId}`);
              if (response.data) {
                setSelectedAsset(response.data);
                setAssetDetailsOpen(true);
                // Fetch policies and quotes for the asset
                const policiesRes = await axios.get(`${API}/policies?asset_id=${openAssetId}`);
                setAssetPolicies(policiesRes.data);
                // Fetch claims summary for all policies
                if (policiesRes.data.length > 0) {
                  const policyIds = policiesRes.data.map(p => p.id).join(',');
                  try {
                    const claimsRes = await axios.get(`${API}/claims/summary/by-policies?policy_ids=${policyIds}`);
                    setPolicyClaims(claimsRes.data);
                  } catch (e) {
                    console.error('Error loading claims summary:', e);
                    setPolicyClaims({});
                  }
                } else {
                  setPolicyClaims({});
                }
                const quotesRes = await axios.get(`${API}/quotes?asset_id=${openAssetId}`);
                setAssetQuotes(quotesRes.data);
              }
            } catch (error) {
              console.error('Error loading asset:', error);
            }
          }, 500);
        }
        
        // Pulisci i parametri URL dopo l'apertura
        setSearchParams({});
      }
    }
  }, [clients, searchParams]);

  // Fetch assets for selected client
  const fetchClientAssets = async (clientId) => {
    try {
      const response = await axios.get(`${API}/assets?client_id=${clientId}`);
      setClientAssets(response.data);
    } catch (error) {
      setClientAssets([]);
    }
  };

  // Fetch CAP when city or provincia changes
  const fetchCaps = async (city, provincia) => {
    if (!city || !provincia) {
      setAvailableCaps([]);
      return;
    }
    setCapLoading(true);
    try {
      const response = await axios.get(`${API}/cap?city=${encodeURIComponent(city)}&provincia=${encodeURIComponent(provincia)}`);
      setAvailableCaps(response.data.caps || []);
      // Auto-fill if single CAP
      if (response.data.single && response.data.auto_cap) {
        setFormData(prev => ({ ...prev, postal_code: response.data.auto_cap }));
      } else if (response.data.caps.length > 1) {
        // Clear postal_code if multiple CAPs available - user must select
        setFormData(prev => ({ ...prev, postal_code: '' }));
      }
    } catch (error) {
      setAvailableCaps([]);
    } finally {
      setCapLoading(false);
    }
  };

  // City autocomplete search
  const searchCities = async (query) => {
    if (query.length < 2) {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      return;
    }
    setCitySearching(true);
    try {
      const response = await axios.get(`${API}/comuni/search?q=${encodeURIComponent(query)}&limit=10`);
      setCitySuggestions(response.data || []);
      setShowCitySuggestions(response.data.length > 0);
    } catch (error) {
      setCitySuggestions([]);
    } finally {
      setCitySearching(false);
    }
  };

  // Debounce city search
  const handleCityInputChange = (value) => {
    setFormData(prev => ({ ...prev, city: value, postal_code: '' }));
    setAvailableCaps([]);
    // Debounced search
    const timeoutId = setTimeout(() => {
      searchCities(value);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  // Select city from suggestions
  const selectCity = (suggestion) => {
    setFormData(prev => ({ 
      ...prev, 
      city: suggestion.name, 
      city_provincia: suggestion.provincia,
      postal_code: '' 
    }));
    setShowCitySuggestions(false);
    setCitySuggestions([]);
    // Fetch CAP for the selected city
    fetchCaps(suggestion.name, suggestion.provincia);
  };

  // Auto marca/modello autocomplete
  const searchMarche = async (query) => {
    try {
      const response = await axios.get(`${API}/auto/marche?query=${encodeURIComponent(query)}&limit=10`);
      setMarcaSuggestions(response.data.marche || []);
      setShowMarcaDropdown(response.data.marche?.length > 0);
    } catch (error) {
      setMarcaSuggestions([]);
    }
  };

  const searchModelli = async (marca, query = '') => {
    if (!marca) {
      setModelloSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/auto/modelli?marca=${encodeURIComponent(marca)}&query=${encodeURIComponent(query)}&limit=15`);
      setModelloSuggestions(response.data.modelli || []);
      setShowModelloDropdown(response.data.modelli?.length > 0);
    } catch (error) {
      setModelloSuggestions([]);
    }
  };

  const handleMarcaInputChange = (value) => {
    setAssetFormData(prev => ({ ...prev, marca: value, modello: '' }));
    setModelloSuggestions([]);
    if (value.length >= 1) {
      const timeoutId = setTimeout(() => searchMarche(value), 200);
      return () => clearTimeout(timeoutId);
    } else {
      setMarcaSuggestions([]);
      setShowMarcaDropdown(false);
    }
  };

  const selectMarca = (marca) => {
    setAssetFormData(prev => ({ ...prev, marca: marca, modello: '' }));
    setShowMarcaDropdown(false);
    setMarcaSuggestions([]);
    // Automatically load models for the selected brand
    searchModelli(marca);
  };

  const handleModelloInputChange = (value) => {
    setAssetFormData(prev => ({ ...prev, modello: value }));
    if (assetFormData.marca && value.length >= 1) {
      const timeoutId = setTimeout(() => searchModelli(assetFormData.marca, value), 200);
      return () => clearTimeout(timeoutId);
    }
  };

  const selectModello = (modello) => {
    setAssetFormData(prev => ({ ...prev, modello: modello }));
    setShowModelloDropdown(false);
    setModelloSuggestions([]);
  };

  const handleCityChange = (newCity) => {
    setFormData(prev => ({ ...prev, city: newCity, postal_code: '' }));
    if (newCity && formData.city_provincia) {
      fetchCaps(newCity, formData.city_provincia);
    }
  };

  const handleCityProvinciaChange = (newProvincia) => {
    setFormData(prev => ({ ...prev, city_provincia: newProvincia, postal_code: '' }));
    if (formData.city && newProvincia) {
      fetchCaps(formData.city, newProvincia);
    }
  };

  // Ricerca inversa: da CAP a Città/Provincia
  const handleCapChange = async (newCap) => {
    setFormData(prev => ({ ...prev, postal_code: newCap }));
    
    // Se il CAP ha 5 cifre, esegui la ricerca inversa
    if (newCap && newCap.length === 5 && /^\d{5}$/.test(newCap)) {
      try {
        const response = await axios.get(`${API}/cap/lookup?cap=${newCap}`);
        if (response.data.found) {
          if (response.data.single) {
            // CAP univoco - compila automaticamente città e provincia
            setFormData(prev => ({ 
              ...prev, 
              city: response.data.city,
              city_provincia: response.data.provincia,
              postal_code: newCap
            }));
            // Aggiorna anche la lista CAP disponibili
            setAvailableCaps([newCap]);
          } else if (response.data.multiple) {
            // CAP condiviso da più città - mostra suggerimento
            const options = response.data.multiple;
            if (options.length > 0) {
              // Prendi la prima opzione come default ma permetti modifica
              setFormData(prev => ({ 
                ...prev, 
                city: options[0].city,
                city_provincia: options[0].provincia,
                postal_code: newCap
              }));
              setAvailableCaps([newCap]);
              // Mostra toast informativo se ci sono più opzioni
              if (options.length > 1) {
                toast.info(`CAP condiviso da ${options.length} comuni. Verifica la città.`);
              }
            }
          }
        }
      } catch (error) {
        // Silently ignore - user can still enter manually
        console.log('CAP lookup error:', error);
      }
    }
  };

  // Asset CAP lookup functions
  const fetchAssetCaps = async (city, provincia) => {
    if (!city || !provincia) {
      setAssetAvailableCaps([]);
      return;
    }
    setAssetCapLoading(true);
    try {
      const response = await axios.get(`${API}/cap?city=${encodeURIComponent(city)}&provincia=${encodeURIComponent(provincia)}`);
      setAssetAvailableCaps(response.data.caps || []);
      // Auto-fill if single CAP
      if (response.data.single && response.data.auto_cap) {
        setAssetFormData(prev => ({ ...prev, cap: response.data.auto_cap }));
      } else if (response.data.caps.length > 1) {
        // Clear cap if multiple CAPs available - user must select
        setAssetFormData(prev => ({ ...prev, cap: '' }));
      }
    } catch (error) {
      setAssetAvailableCaps([]);
    } finally {
      setAssetCapLoading(false);
    }
  };

  const handleAssetCityChange = (newCity) => {
    setAssetFormData(prev => ({ ...prev, city: newCity, cap: '' }));
    if (newCity && assetFormData.provincia) {
      fetchAssetCaps(newCity, assetFormData.provincia);
    }
  };

  const handleAssetProvinciaChange = (newProvincia) => {
    setAssetFormData(prev => ({ ...prev, provincia: newProvincia, cap: '' }));
    if (assetFormData.city && newProvincia) {
      fetchAssetCaps(assetFormData.city, newProvincia);
    }
  };

  // Company (Azienda) CAP lookup functions
  const fetchCompanyCaps = async (city, provincia) => {
    if (!city || !provincia) {
      setCompanyAvailableCaps([]);
      return;
    }
    setCompanyCapLoading(true);
    try {
      const response = await axios.get(`${API}/cap?city=${encodeURIComponent(city)}&provincia=${encodeURIComponent(provincia)}`);
      setCompanyAvailableCaps(response.data.caps || []);
      // Auto-fill if single CAP
      if (response.data.single && response.data.auto_cap) {
        setFormData(prev => ({ ...prev, company_postal_code: response.data.auto_cap }));
      } else if (response.data.caps.length > 1) {
        // Clear cap if multiple CAPs available - user must select
        setFormData(prev => ({ ...prev, company_postal_code: '' }));
      }
    } catch (error) {
      setCompanyAvailableCaps([]);
    } finally {
      setCompanyCapLoading(false);
    }
  };

  const handleCompanyCityChange = (newCity) => {
    setFormData(prev => ({ ...prev, company_city: newCity, company_postal_code: '' }));
    if (newCity && formData.company_provincia) {
      fetchCompanyCaps(newCity, formData.company_provincia);
    }
  };

  const handleCompanyProvinciaChange = (newProvincia) => {
    setFormData(prev => ({ ...prev, company_provincia: newProvincia, company_postal_code: '' }));
    if (formData.company_city && newProvincia) {
      fetchCompanyCaps(formData.company_city, newProvincia);
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      // Se già ordinato per questo campo, inverti direzione
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nuovo campo: imposta direzione appropriata
      setSortField(field);
      // Per data, default desc (più recenti prima); per nome, default asc (A-Z)
      setSortDirection(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-emerald-600" /> : 
      <ArrowDown className="w-4 h-4 text-emerald-600" />;
  };

  // Sort clients with multi-field support (secondary sort by name when dates are equal)
  const sortedClients = [...clients].sort((a, b) => {
    let comparison = 0;
    
    if (sortField === 'created_at') {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      comparison = dateA - dateB;
      
      // Secondary sort by name if dates are equal
      if (comparison === 0) {
        const nameA = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
        const nameB = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
        comparison = nameA.localeCompare(nameB, 'it');
      }
    } else if (sortField === 'name') {
      const nameA = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
      const nameB = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
      comparison = nameA.localeCompare(nameB, 'it');
      
      // Secondary sort by created_at if names are equal
      if (comparison === 0) {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        comparison = dateB - dateA; // più recenti prima
      }
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Pagination logic
  const totalClients = sortedClients.length;
  const totalPages = Math.ceil(totalClients / CLIENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * CLIENTS_PER_PAGE;
  const endIndex = startIndex + CLIENTS_PER_PAGE;
  const paginatedClients = sortedClients.slice(startIndex, endIndex);
  
  // Reset to page 1 when clients list changes (from search/filter)
  useEffect(() => {
    setCurrentPage(1);
  }, [clients.length, searchTerm, filterCompany, filterAgentSource]);

  const fetchClients = async () => {
    try {
      let url = `${API}/clients`;
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterCompany !== null) params.append('is_company', filterCompany);
      // Multi-select: passa array come parametri separati o usa join
      if (filterAgentSource && filterAgentSource.length > 0) {
        params.append('agent_sources', filterAgentSource.join(','));
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setClients(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agent-sources`);
      setAgents(response.data);
    } catch (error) {
      console.log('Could not load agent sources');
    }
  };

  // Fetch agencies list
  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agencies`);
      setAgencies(response.data);
    } catch (error) {
      console.log('Could not load agencies');
    }
  };

  // Fetch insurance companies list
  const fetchInsuranceCompanies = async () => {
    try {
      const response = await axios.get(`${API}/insurance-companies`);
      setInsuranceCompanies(response.data);
    } catch (error) {
      console.log('Could not load insurance companies');
    }
  };

  // Add new agency
  const handleAddAgency = async () => {
    if (!newAgencyName.trim()) return;
    try {
      await axios.post(`${API}/agencies`, { name: newAgencyName.trim() });
      toast.success('Agenzia aggiunta');
      setNewAgencyName('');
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Delete agency
  const handleDeleteAgency = async (agencyId) => {
    try {
      await axios.delete(`${API}/agencies/${agencyId}`);
      toast.success('Agenzia eliminata');
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Add new insurance company
  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      await axios.post(`${API}/insurance-companies`, { name: newCompanyName.trim() });
      toast.success('Compagnia aggiunta');
      setNewCompanyName('');
      fetchInsuranceCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Delete insurance company
  const handleDeleteCompany = async (companyId) => {
    try {
      await axios.delete(`${API}/insurance-companies/${companyId}`);
      toast.success('Compagnia eliminata');
      fetchInsuranceCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Edit Agency with cascade update
  const handleEditAgency = async (agencyId, newName) => {
    if (!newName.trim()) {
      toast.error('Il nome non può essere vuoto');
      return;
    }
    try {
      await axios.put(`${API}/agencies/${agencyId}`, { name: newName.trim() });
      toast.success('Agenzia aggiornata');
      setEditingAgencyId(null);
      setEditingAgencyName('');
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  // Edit Company with cascade update
  const handleEditCompany = async (companyId, newName) => {
    if (!newName.trim()) {
      toast.error('Il nome non può essere vuoto');
      return;
    }
    try {
      await axios.put(`${API}/insurance-companies/${companyId}`, { name: newName.trim() });
      toast.success('Compagnia aggiornata');
      setEditingCompanyId(null);
      setEditingCompanyName('');
      fetchInsuranceCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  const handleAddSource = async () => {
    if (!newSourceName.trim()) return;
    try {
      await axios.post(`${API}/agent-sources`, { name: newSourceName.trim() });
      toast.success('Fonte aggiunta');
      setNewSourceName('');
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteSource = async (source, confirmed = false) => {
    try {
      const response = await axios.delete(`${API}/agent-sources/${source.id}${confirmed ? '?confirm=true' : ''}`);
      if (response.data.requires_confirmation) {
        setDeleteConfirmSource({ ...source, clients_count: response.data.clients_count });
      } else {
        toast.success(`Fonte eliminata${response.data.clients_cleared > 0 ? ` (${response.data.clients_cleared} clienti aggiornati)` : ''}`);
        setDeleteConfirmSource(null);
        fetchAgents();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchAgencies();
    fetchInsuranceCompanies();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Blocca se c'è un duplicato CF
    if (duplicateCfClient && !editingClient) {
      toast.error('Impossibile salvare: Codice Fiscale già esistente');
      return;
    }
    
    try {
      if (editingClient) {
        // Aggiorna cliente esistente
        const response = await axios.put(`${API}/clients/${editingClient.id}`, formData);
        const updatedClient = response.data;
        
        toast.success('Salvataggio completato');
        
        // Chiudi il dialog di modifica
        setDialogOpen(false);
        setEditingClient(null);
        setFormData(emptyClient);
        setDuplicateCfClient(null);
        setAvailableCaps([]);
        setCompanyAvailableCaps([]);
        
        // Aggiorna la lista clienti in background
        fetchClients();
        
        // Aggiorna selectedClient con i nuovi dati e riapri i Dettagli Cliente
        setSelectedClient(updatedClient);
        fetchClientAssets(updatedClient.id);
        setDetailsOpen(true);  // Riapri il modal Dettagli Cliente
        
      } else {
        // Crea nuovo cliente e ottieni i dati completi
        const response = await axios.post(`${API}/clients`, formData);
        const newClient = response.data;
        toast.success('Cliente creato');
        setDialogOpen(false);
        setFormData(emptyClient);
        setDuplicateCfClient(null);
        
        // Aggiorna la lista clienti
        await fetchClients();
        
        // Apri automaticamente il modal Dettagli Cliente dopo un breve delay
        // per assicurarsi che il DOM sia aggiornato
        setTimeout(() => {
          setSelectedClient(newClient);
          setClientAssets([]);
          setDetailsOpen(true);  // Apri il modal Dettagli Cliente
        }, 100);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    const newFormData = {
      last_name: client.last_name || '',
      first_name: client.first_name || '',
      gender: client.gender || '',
      birth_date: client.birth_date || '',
      birth_abroad: client.birth_abroad || false,
      birth_comune: client.birth_comune || '',
      birth_provincia: client.birth_provincia || '',
      birth_country: client.birth_country || '',
      email: client.email || '',
      phone: client.phone || '',
      whatsapp_contact: client.whatsapp_contact || '',
      wechat_contact: client.wechat_contact || '',
      fiscal_code: client.fiscal_code || '',
      address: client.address || '',
      address_number: client.address_number || '',
      city: client.city || '',
      city_provincia: client.city_provincia || '',
      postal_code: client.postal_code || '',
      is_company: client.is_company,
      company_name: client.company_name || '',
      vat_number: client.vat_number || '',
      pec: client.pec || '',
      // Company address fields
      company_address: client.company_address || '',
      company_address_number: client.company_address_number || '',
      company_city: client.company_city || '',
      company_provincia: client.company_provincia || '',
      company_postal_code: client.company_postal_code || '',
      company_latitude: client.company_latitude || null,
      company_longitude: client.company_longitude || null,
      agent_source: client.agent_source || '',
      notes: client.notes || '',
      tags: client.tags || []
    };
    setFormData(newFormData);
    // Fetch available CAPs for the client's city
    if (client.city && client.city_provincia) {
      fetchCaps(client.city, client.city_provincia);
    } else {
      setAvailableCaps([]);
    }
    setDialogOpen(true);
  };

  const calculateFiscalCode = async () => {
    if (!formData.last_name || !formData.first_name || !formData.birth_date || !formData.gender) {
      toast.error('Compila Cognome, Nome, Sesso e Data di nascita');
      return;
    }
    if (!formData.birth_abroad && (!formData.birth_comune || !formData.birth_provincia)) {
      toast.error('Compila Comune e Provincia di nascita');
      return;
    }
    if (formData.birth_abroad && !formData.birth_country) {
      toast.error('Seleziona la Nazione di nascita');
      return;
    }
    try {
      const response = await axios.post(`${API}/calculate-fiscal-code`, {
        surname: formData.last_name,
        name: formData.first_name,
        birth_date: formData.birth_date,
        gender: formData.gender,
        birth_abroad: formData.birth_abroad,
        birth_comune: formData.birth_comune,
        birth_provincia: formData.birth_provincia,
        birth_country: formData.birth_country
      });
      setFormData({ ...formData, fiscal_code: response.data.fiscal_code });
      toast.success('Codice Fiscale calcolato');
      // Verifica duplicati dopo il calcolo
      checkFiscalCodeDuplicate(response.data.fiscal_code);
    } catch (error) {
      toast.error('Errore nel calcolo del Codice Fiscale');
    }
  };

  // Verifica duplicati Codice Fiscale in tempo reale
  const checkFiscalCodeDuplicate = async (fiscalCode) => {
    if (!fiscalCode || fiscalCode.length < 10) {
      setDuplicateCfClient(null);
      return;
    }
    
    setCheckingCf(true);
    try {
      const excludeId = editingClient?.id || null;
      const response = await axios.get(`${API}/clients/check-fiscal-code?fiscal_code=${encodeURIComponent(fiscalCode)}${excludeId ? `&exclude_id=${excludeId}` : ''}`);
      if (response.data.exists) {
        setDuplicateCfClient(response.data.client);
      } else {
        setDuplicateCfClient(null);
      }
    } catch (error) {
      console.error('Errore verifica CF:', error);
    } finally {
      setCheckingCf(false);
    }
  };

  // Handler per cambio Codice Fiscale con debounce
  const handleFiscalCodeChange = (value) => {
    const upperValue = value.toUpperCase();
    setFormData({ ...formData, fiscal_code: upperValue });
    
    // Debounce per evitare troppe chiamate
    if (upperValue.length >= 16) {
      const timeoutId = setTimeout(() => checkFiscalCodeDuplicate(upperValue), 500);
      return () => clearTimeout(timeoutId);
    } else {
      setDuplicateCfClient(null);
    }
  };

  // Apri cliente esistente da avviso duplicato
  const openDuplicateClient = async () => {
    if (!duplicateCfClient) return;
    
    try {
      // Chiudi il form di creazione
      setDialogOpen(false);
      setFormData(emptyClient);
      setDuplicateCfClient(null);
      
      // Cerca il cliente esistente
      const response = await axios.get(`${API}/clients?search=${duplicateCfClient.fiscal_code}`);
      if (response.data.length > 0) {
        const existingClient = response.data.find(c => c.id === duplicateCfClient.id) || response.data[0];
        setSelectedClient(existingClient);
        setDetailsOpen(true);
        
        // Carica gli asset del cliente
        const assetsRes = await axios.get(`${API}/assets?client_id=${existingClient.id}`);
        setClientAssets(assetsRes.data);
      }
    } catch (error) {
      toast.error('Errore nell\'apertura del cliente');
    }
  };

  const handleDelete = async (clientId, confirmed = false) => {
    try {
      const response = await axios.delete(`${API}/clients/${clientId}${confirmed ? '?confirm=true' : ''}`);
      if (response.data.requires_confirmation) {
        setDeleteClientConfirm({
          clientId,
          linked_items: response.data.linked_items,
          total_linked: response.data.total_linked,
          documents_count: response.data.documents_count || 0
        });
        setDeleteStep(2);  // Vai allo step 2 con i dettagli
      } else {
        toast.success('Cliente e tutti i dati collegati eliminati definitivamente');
        setDeleteClientConfirm(null);
        setDeleteStep(1);
        setDetailsOpen(false);
        setSelectedClient(null);
        fetchClients();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const initiateDeleteClient = (clientId) => {
    setDeleteClientConfirm({ clientId });
    setDeleteStep(1);  // Inizia dallo step 1
  };

  const handleEditFromDetails = () => {
    if (selectedClient) {
      handleEdit(selectedClient);
      setDetailsOpen(false);
    }
  };

  const handleDeleteFromDetails = () => {
    if (selectedClient) {
      initiateDeleteClient(selectedClient.id);
    }
  };

  const handleViewDetails = async (client) => {
    try {
      const response = await axios.get(`${API}/clients/${client.id}`);
      setSelectedClient(response.data);
      fetchClientAssets(client.id);
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Asset management functions
  const openAddAssetDialog = (type) => {
    setAssetType(type);
    setEditingAsset(null);
    setAssetAvailableCaps([]); // Reset CAP options
    if (type === 'casa') setAssetFormData(emptyAssetCasa);
    else if (type === 'attivita') setAssetFormData(emptyAssetAttivita);
    else if (type === 'auto') setAssetFormData(emptyAssetAuto);
    else if (type === 'salute') setAssetFormData({ ...emptyAssetSalute, persone_assicurate: [] });
    setAssetDialogOpen(true);
  };

  const openEditAssetDialog = (asset) => {
    setAssetType(asset.asset_type);
    setEditingAsset(asset);
    setAssetAvailableCaps([]); // Reset CAP options
    setAssetFormData({
      indirizzo: asset.indirizzo || '',
      city: asset.city || '',
      provincia: asset.provincia || '',
      cap: asset.cap || '',
      dati_catastali: asset.dati_catastali || '',
      ragione_sociale: asset.ragione_sociale || '',
      partita_iva: asset.partita_iva || '',
      tipo_attivita: asset.tipo_attivita || '',
      targa: asset.targa || '',
      data_immatricolazione: asset.data_immatricolazione || '',
      data_acquisto: asset.data_acquisto || '',
      scadenza_copertura: asset.scadenza_copertura || '',
      alimentazione: asset.alimentazione || '',
      marca: asset.marca || '',
      modello: asset.modello || '',
      vehicle_status: asset.vehicle_status || 'in_possesso',
      persone_assicurate: asset.persone_assicurate || [],
      notes: asset.notes || ''
    });
    // Load CAP options if city and provincia are set
    if (asset.city && asset.provincia) {
      fetchAssetCaps(asset.city, asset.provincia);
    }
    setAssetDialogOpen(true);
  };

  const handleSaveAsset = async () => {
    if (!selectedClient) return;
    try {
      const payload = {
        client_id: selectedClient.id,
        asset_type: assetType,
        ...assetFormData
      };
      if (editingAsset) {
        await axios.put(`${API}/assets/${editingAsset.id}`, payload);
        toast.success('Bene aggiornato');
      } else {
        await axios.post(`${API}/assets`, payload);
        toast.success('Bene aggiunto');
      }
      setAssetDialogOpen(false);
      fetchClientAssets(selectedClient.id);
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Eliminare questo bene?')) return;
    try {
      await axios.delete(`${API}/assets/${assetId}`);
      toast.success('Bene eliminato');
      fetchClientAssets(selectedClient.id);
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Ricerca comuni italiani (database locale ISTAT - NO Google Maps)
  const searchComuniItaliani = async (query, personaIndex) => {
    if (!query || query.length < 2) {
      setComuniSuggestions(prev => ({ ...prev, [personaIndex]: [] }));
      setShowComuniDropdown(prev => ({ ...prev, [personaIndex]: false }));
      return;
    }
    
    try {
      const response = await axios.get(`${API}/utils/comuni/search`, {
        params: { q: query, limit: 8 }
      });
      setComuniSuggestions(prev => ({ ...prev, [personaIndex]: response.data }));
      setShowComuniDropdown(prev => ({ ...prev, [personaIndex]: response.data.length > 0 }));
    } catch (error) {
      console.error('Errore ricerca comuni:', error);
    }
  };

  // Seleziona comune e auto-compila provincia
  const selectComune = (comune, personaIndex) => {
    const updated = [...assetFormData.persone_assicurate];
    updated[personaIndex] = {
      ...updated[personaIndex],
      luogo_nascita: comune.nome,
      provincia_nascita: comune.provincia
    };
    setAssetFormData({ ...assetFormData, persone_assicurate: updated });
    setShowComuniDropdown(prev => ({ ...prev, [personaIndex]: false }));
    setComuniSuggestions(prev => ({ ...prev, [personaIndex]: [] }));
  };

  // Ricerca comuni per Anagrafica Cliente (Comune di Nascita) - NO Google Maps
  const searchBirthComune = async (query) => {
    if (!query || query.length < 2) {
      setBirthComuneSuggestions([]);
      setShowBirthComuneDropdown(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/utils/comuni/search`, {
        params: { q: query, limit: 8 }
      });
      setBirthComuneSuggestions(response.data);
      setShowBirthComuneDropdown(response.data.length > 0);
    } catch (error) {
      console.error('Errore ricerca comuni nascita:', error);
    }
  };

  // Seleziona comune nascita e auto-compila provincia (Anagrafica Cliente)
  const selectBirthComune = (comune) => {
    setFormData({ 
      ...formData, 
      birth_comune: comune.nome,
      birth_provincia: comune.provincia
    });
    setShowBirthComuneDropdown(false);
    setBirthComuneSuggestions([]);
  };

  const getAssetIcon = (type) => {
    if (type === 'casa') return <Home className="w-5 h-5 text-blue-600" />;
    if (type === 'attivita') return <Briefcase className="w-5 h-5 text-amber-600" />;
    if (type === 'auto') return <Car className="w-5 h-5 text-emerald-600" />;
    if (type === 'salute') return <ShieldPlus className="w-5 h-5 text-rose-600" />;
    return null;
  };

  // Helper function to generate client display name
  // Per aziende: [Ragione Sociale] - [Cognome Nome]
  // Per privati: [Cognome Nome]
  const getClientDisplayName = (client, uppercase = false) => {
    if (!client) return '';
    
    const cognome = (client.last_name || '').trim();
    const nome = (client.first_name || '').trim();
    let fullName = `${cognome} ${nome}`.trim();
    
    const isCompany = client.is_company;
    const companyName = (client.company_name || '').trim();
    
    let displayName;
    if (isCompany && companyName) {
      displayName = fullName ? `${companyName} - ${fullName}` : companyName;
    } else {
      displayName = fullName;
    }
    
    return uppercase ? displayName.toUpperCase() : displayName;
  };

  // Funzione per ottenere il nome cliente per l'asset_label (denominazione bene)
  // Per aziende: usa SOLO la Ragione Sociale (senza il nome del referente)
  // Per privati: usa Cognome Nome
  const getClientNameForAssetLabel = (client, uppercase = false) => {
    if (!client) return '';
    
    const isCompany = client.is_company;
    const companyName = (client.company_name || '').trim();
    
    // Per aziende: usa solo la ragione sociale
    if (isCompany && companyName) {
      return uppercase ? companyName.toUpperCase() : companyName;
    }
    
    // Per privati: usa cognome + nome
    const cognome = (client.last_name || '').trim();
    const nome = (client.first_name || '').trim();
    const fullName = `${cognome} ${nome}`.trim();
    return uppercase ? fullName.toUpperCase() : fullName;
  };

  const getAssetLabel = (asset, client = null) => {
    // Usa il client passato o selectedClient
    const clientData = client || selectedClient;
    
    if (asset.asset_type === 'casa') {
      const location = [asset.city, asset.provincia].filter(Boolean).join(' ');
      return asset.indirizzo ? `${asset.indirizzo}${location ? `, ${location}` : ''}` : 'Casa';
    }
    if (asset.asset_type === 'attivita') return asset.ragione_sociale || 'Attività';
    if (asset.asset_type === 'salute') {
      const persone = asset.persone_assicurate || [];
      if (persone.length > 0) {
        return `Salute (${persone.length} ${persone.length === 1 ? 'persona' : 'persone'})`;
      }
      return 'Salute';
    }
    if (asset.asset_type === 'auto' || asset.asset_type === 'moto') {
      // Per aziende: usa SOLO Ragione Sociale + Targa (senza nome referente)
      const clientName = getClientNameForAssetLabel(clientData, true);
      const targa = (asset.targa || '').toUpperCase();
      
      if (targa && clientName) {
        return `${clientName} ${targa}`;
      } else if (targa) {
        return targa;
      } else if (clientName) {
        return clientName;
      }
      return 'Veicolo';
    }
    return 'Bene';
  };

  // Asset Details + Quotes functions
  const openAssetDetails = async (asset) => {
    setSelectedAsset(asset);
    setAssetDetailsOpen(true);
    // Fetch quotes for this asset
    try {
      const quotesRes = await axios.get(`${API}/quotes?asset_id=${asset.id}`);
      setAssetQuotes(quotesRes.data);
    } catch (e) {
      setAssetQuotes([]);
    }
    // Fetch policies for this asset
    try {
      const policiesRes = await axios.get(`${API}/policies?asset_id=${asset.id}`);
      setAssetPolicies(policiesRes.data);
      // Fetch claims summary for all policies
      if (policiesRes.data.length > 0) {
        const policyIds = policiesRes.data.map(p => p.id).join(',');
        try {
          const claimsRes = await axios.get(`${API}/claims/summary/by-policies?policy_ids=${policyIds}`);
          setPolicyClaims(claimsRes.data);
        } catch (e) {
          console.error('Error loading claims summary:', e);
          setPolicyClaims({});
        }
      } else {
        setPolicyClaims({});
      }
    } catch (e) {
      setAssetPolicies([]);
      setPolicyClaims({});
    }
    // Fetch suspensions for this asset (solo per auto)
    if (asset.asset_type === 'auto') {
      try {
        const suspensionsRes = await axios.get(`${API}/assets/${asset.id}/suspensions`);
        setAssetSuspensions(suspensionsRes.data);
      } catch (e) {
        setAssetSuspensions([]);
      }
    }
  };

  // Storico Sospensioni Asset functions
  const openAddSuspensionDialog = () => {
    setEditingSuspension(null);
    setSuspensionFormData({ data_richiesta: '', data_decorrenza: '', data_fine: '' });
    setSuspensionDialogOpen(true);
  };

  const openEditSuspensionDialog = (suspension) => {
    setEditingSuspension(suspension);
    setSuspensionFormData({
      data_richiesta: suspension.data_richiesta || '',
      data_decorrenza: suspension.data_decorrenza || '',
      data_fine: suspension.data_fine || ''
    });
    setSuspensionDialogOpen(true);
  };

  const handleSaveSuspension = async () => {
    if (!selectedAsset) return;
    try {
      if (editingSuspension) {
        await axios.put(`${API}/assets/${selectedAsset.id}/suspensions/${editingSuspension.id}`, suspensionFormData);
        toast.success('Sospensione aggiornata');
      } else {
        await axios.post(`${API}/assets/${selectedAsset.id}/suspensions`, suspensionFormData);
        toast.success('Sospensione registrata');
      }
      // Refresh suspensions list
      const suspensionsRes = await axios.get(`${API}/assets/${selectedAsset.id}/suspensions`);
      setAssetSuspensions(suspensionsRes.data);
      setSuspensionDialogOpen(false);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteSuspension = async (suspensionId) => {
    if (!selectedAsset) return;
    if (!confirm('Eliminare questa sospensione?')) return;
    try {
      await axios.delete(`${API}/assets/${selectedAsset.id}/suspensions/${suspensionId}`);
      toast.success('Sospensione eliminata');
      const suspensionsRes = await axios.get(`${API}/assets/${selectedAsset.id}/suspensions`);
      setAssetSuspensions(suspensionsRes.data);
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const openAddQuoteDialog = () => {
    setEditingQuote(null);
    // Preselezione RC Auto solo per veicoli
    const defaultCoverages = selectedAsset?.asset_type === 'auto' ? ['RC Auto'] : [];
    setQuoteFormData({
      agent: '', company: '', premium: '', coverage_types: defaultCoverages, payment_frequency: 'annuale', driving_type: 'esperta', notes: ''
    });
    setQuoteDialogOpen(true);
  };

  const openEditQuoteDialog = (quote) => {
    setEditingQuote(quote);
    // Load the first option's data into the form
    const firstOption = quote.options?.[0] || {};
    setQuoteFormData({
      agent: firstOption.agent || '',
      company: firstOption.company || '',
      premium: firstOption.premium?.toString() || '',
      coverage_types: firstOption.coverage_types || [],
      payment_frequency: quote.payment_frequency || 'annuale',
      driving_type: quote.driving_type || 'esperta',
      notes: firstOption.notes || quote.notes || ''
    });
    setQuoteDialogOpen(true);
  };

  const handleSaveQuote = async () => {
    if (!selectedAsset || !selectedClient) return;
    try {
      const payload = {
        client_id: selectedClient.id,
        asset_id: selectedAsset.id,
        quote_type: selectedAsset.asset_type,
        options: [{
          agent: quoteFormData.agent,
          company: quoteFormData.company,
          premium: parseFloat(quoteFormData.premium) || 0,
          coverage_types: quoteFormData.coverage_types,
          notes: quoteFormData.notes
        }],
        payment_frequency: quoteFormData.payment_frequency,
        driving_type: quoteFormData.driving_type,
        status: editingQuote?.status || 'bozza',
        notes: quoteFormData.notes
      };

      if (editingQuote) {
        // Update existing quote
        await axios.put(`${API}/quotes/${editingQuote.id}`, payload);
        toast.success('Preventivo aggiornato');
      } else {
        // Create new quote
        await axios.post(`${API}/quotes`, payload);
        toast.success('Preventivo creato');
      }
      
      setQuoteDialogOpen(false);
      setEditingQuote(null);
      // Refresh quotes
      const quotesRes = await axios.get(`${API}/quotes?asset_id=${selectedAsset.id}`);
      setAssetQuotes(quotesRes.data);
    } catch (error) {
      toast.error(editingQuote ? 'Errore nell\'aggiornamento del preventivo' : 'Errore nella creazione del preventivo');
    }
  };

  // Open conversion modal
  const openConversionDialog = (quote) => {
    setQuoteToConvert(quote);
    // Pre-fill with today's date
    setConversionStartDate(new Date().toISOString().split('T')[0]);
    setConversionDialogOpen(true);
  };

  const handleConvertToPolicy = async () => {
    if (!quoteToConvert) return;
    try {
      await axios.post(`${API}/quotes/${quoteToConvert.id}/convert-to-policy`, { 
        option_indices: [0],
        start_date: conversionStartDate
      });
      toast.success('Polizza creata con successo');
      setConversionDialogOpen(false);
      setQuoteToConvert(null);
      // Refresh quotes, policies and asset data (scadenza_copertura may have been reset)
      const quotesRes = await axios.get(`${API}/quotes?asset_id=${selectedAsset.id}`);
      setAssetQuotes(quotesRes.data);
      const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
      setAssetPolicies(policiesRes.data);
      // Re-fetch asset to reflect scadenza_copertura reset
      const assetRes = await axios.get(`${API}/assets/${selectedAsset.id}`);
      setSelectedAsset(assetRes.data);
      // Re-fetch all client assets to update the list
      if (selectedClient) {
        fetchClientAssets(selectedClient.id);
      }
    } catch (error) {
      toast.error('Errore nella conversione');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Eliminare questo preventivo?')) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      toast.success('Preventivo eliminato');
      const quotesRes = await axios.get(`${API}/quotes?asset_id=${selectedAsset.id}`);
      setAssetQuotes(quotesRes.data);
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleRevertConversion = async (quoteId) => {
    if (!window.confirm('Sei sicuro di voler annullare l\'emissione della polizza e ripristinare il preventivo?\n\nLa polizza verrà eliminata e il preventivo tornerà in stato "Bozza".')) return;
    try {
      const response = await axios.post(`${API}/quotes/${quoteId}/revert-conversion`);
      toast.success(response.data.message || 'Emissione annullata');
      // Refresh quotes and policies
      const quotesRes = await axios.get(`${API}/quotes?asset_id=${selectedAsset.id}`);
      setAssetQuotes(quotesRes.data);
      const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
      setAssetPolicies(policiesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'annullamento');
    }
  };

  // Policy editing functions
  const openEditPolicyDialog = (policy) => {
    setEditingPolicy(policy);
    setPolicyFormData({
      policy_number: policy.policy_number || '',
      company: policy.company || '',
      agent: policy.agent || '',
      payment_date: policy.payment_date || '',
      start_date: policy.start_date || '',
      end_date: policy.end_date || '',
      premium_amount: policy.premium_amount || '',
      coverage_details: policy.coverage_details || '',
      status: policy.status || 'attiva'
    });
    setPolicyDialogOpen(true);
  };

  const handleSavePolicy = async () => {
    if (!editingPolicy) return;
    try {
      const payload = {
        client_id: editingPolicy.client_id,
        asset_id: editingPolicy.asset_id,
        quote_id: editingPolicy.quote_id,
        policy_type: editingPolicy.policy_type,
        policy_number: policyFormData.policy_number,
        company: policyFormData.company,
        agent: policyFormData.agent,
        payment_date: policyFormData.payment_date,
        start_date: policyFormData.start_date,
        end_date: policyFormData.end_date,
        premium_amount: parseFloat(policyFormData.premium_amount) || 0,
        coverage_details: policyFormData.coverage_details,
        status: policyFormData.status
      };
      await axios.put(`${API}/policies/${editingPolicy.id}`, payload);
      toast.success('Polizza aggiornata');
      setPolicyDialogOpen(false);
      setEditingPolicy(null);
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  // Gestione Polizza Functions
  const openPolicyManagement = async (policy) => {
    setManagingPolicy(policy);
    setManagementAction(null);
    setAccountingAction(null);
    setClaimFormData({ claim_date: '', description: '' });
    setSuspendFormData({ suspension_start: '', suspension_end: '' });
    setReplaceFormData({ replace_type: 'targa', new_targa: '', new_asset_id: '' });
    setInterruptFormData({ interruption_date: '', refund_amount: '' });
    
    // Carica pagamenti della polizza
    fetchPolicyPayments(policy.id);
    
    // Carica veicoli disponibili per sostituzione (escluso quello attuale)
    if (selectedClient) {
      try {
        const assetsRes = await axios.get(`${API}/assets?client_id=${selectedClient.id}`);
        const vehicles = assetsRes.data.filter(a => a.asset_type === 'auto' && a.id !== policy.asset_id);
        setAvailableVehicles(vehicles);
      } catch (e) {
        setAvailableVehicles([]);
      }
    }
    
    setPolicyManagementOpen(true);
  };

  const handleCreateClaimFromPolicy = async () => {
    if (!managingPolicy || !claimFormData.claim_date) {
      toast.error('Inserisci la data del sinistro');
      return;
    }
    try {
      const payload = {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        claim_date: claimFormData.claim_date,
        description: claimFormData.description || '',  // Facoltativo
        status: 'aperto'
      };
      await axios.post(`${API}/claims`, payload);
      toast.success('Sinistro creato con successo');
      setPolicyManagementOpen(false);
      setManagementAction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    }
  };

  const handleSuspendPolicy = async () => {
    if (!managingPolicy || !suspendFormData.suspension_start || !suspendFormData.suspension_end) {
      toast.error('Compila le date di sospensione');
      return;
    }
    try {
      await axios.post(`${API}/policies/${managingPolicy.id}/suspend`, suspendFormData);
      toast.success('Polizza sospesa');
      setPolicyManagementOpen(false);
      setManagementAction(null);
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la sospensione');
    }
  };

  // Funzione helper per calcolare giorni tra due date
  const calculateDaysBetween = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Funzione per calcolare la nuova data di scadenza aggiungendo i giorni di sospensione
  const calculateNewEndDate = (policy) => {
    if (!policy?.suspension_start || !policy?.end_date) return '';
    const suspensionStart = new Date(policy.suspension_start);
    const today = new Date();
    const suspensionDays = Math.ceil((today - suspensionStart) / (1000 * 60 * 60 * 24));
    const oldEndDate = new Date(policy.end_date);
    const newEndDate = new Date(oldEndDate.getTime() + suspensionDays * 24 * 60 * 60 * 1000);
    return newEndDate.toISOString().split('T')[0];
  };

  // Apre il dialog per riattivare la polizza con aggiornamento scadenza
  const openReactivateDialog = (policy) => {
    setReactivatingPolicy(policy);
    setNewEndDateForReactivation(calculateNewEndDate(policy));
    setReactivateDialogOpen(true);
  };

  // Riattiva la polizza con la nuova data scadenza
  const handleReactivatePolicy = async () => {
    if (!reactivatingPolicy) return;
    try {
      // Aggiorna la data di scadenza e lo stato (la registrazione nello storico è automatica)
      await axios.patch(`${API}/policies/${reactivatingPolicy.id}/status`, {
        new_end_date: newEndDateForReactivation,
        suspension_status: 'gestito'
      });
      
      toast.success('Polizza riattivata e scadenza aggiornata');
      setReactivateDialogOpen(false);
      setReactivatingPolicy(null);
      setNewEndDateForReactivation('');
      
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
        // Refresh anche lo storico sospensioni
        if (selectedAsset.asset_type === 'auto') {
          const suspensionsRes = await axios.get(`${API}/assets/${selectedAsset.id}/suspensions`);
          setAssetSuspensions(suspensionsRes.data);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la riattivazione');
    }
  };

  const handleReplaceVehicle = async () => {
    if (!managingPolicy) return;
    if (replaceFormData.replace_type === 'targa' && !replaceFormData.new_targa) {
      toast.error('Inserisci la nuova targa');
      return;
    }
    if (replaceFormData.replace_type === 'veicolo' && !replaceFormData.new_asset_id) {
      toast.error('Seleziona il nuovo veicolo');
      return;
    }
    try {
      await axios.post(`${API}/policies/${managingPolicy.id}/replace`, replaceFormData);
      toast.success(replaceFormData.replace_type === 'targa' ? 'Targa aggiornata' : 'Veicolo sostituito');
      setPolicyManagementOpen(false);
      setManagementAction(null);
      // Refresh assets and policies
      if (selectedClient) {
        await fetchClientAssets(selectedClient.id);
      }
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la sostituzione');
    }
  };

  const handleInterruptPolicy = async () => {
    if (!managingPolicy || !interruptFormData.interruption_date) {
      toast.error('Inserisci la data di interruzione');
      return;
    }
    try {
      const payload = {
        interruption_date: interruptFormData.interruption_date,
        refund_amount: interruptFormData.refund_amount ? parseFloat(interruptFormData.refund_amount) : null
      };
      await axios.post(`${API}/policies/${managingPolicy.id}/interrupt`, payload);
      toast.success('Polizza interrotta');
      setPolicyManagementOpen(false);
      setManagementAction(null);
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'interruzione');
    }
  };

  // Rinnova Polizza - crea nuova polizza con stessi dati ma date aggiornate
  const handleRenewPolicy = async () => {
    if (!managingPolicy) return;
    try {
      const response = await axios.post(`${API}/policies/${managingPolicy.id}/renew`);
      toast.success(`Polizza rinnovata! Nuovo numero: ${response.data.policy_number}`);
      setPolicyManagementOpen(false);
      setManagementAction(null);
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il rinnovo');
    }
  };

  // Archivia Polizza - cambia stato a archiviata
  const handleArchivePolicy = async () => {
    if (!managingPolicy) return;
    try {
      await axios.patch(`${API}/policies/${managingPolicy.id}/status`, { status: 'archiviata' });
      toast.success('Polizza archiviata');
      setPolicyManagementOpen(false);
      setManagementAction(null);
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'archiviazione');
    }
  };

  // Elimina Polizza - CASCADE DELETE con conferma dettagliata
  const handleDeletePolicy = async () => {
    if (!managingPolicy) return;
    
    try {
      // Verifica sinistri collegati
      const claimsRes = await axios.get(`${API}/claims?policy_id=${managingPolicy.id}`);
      const linkedClaims = claimsRes.data;
      
      // Verifica pagamenti collegati
      const paymentsRes = await axios.get(`${API}/payments?policy_id=${managingPolicy.id}`);
      const linkedPayments = paymentsRes.data;
      
      // Conta i tipi di pagamenti
      const incassi = linkedPayments.filter(p => p.type === 'incasso');
      const versamenti = linkedPayments.filter(p => p.type === 'pagamento');
      const provvigioni = linkedPayments.filter(p => p.type === 'provvigione');
      
      // Costruisci messaggio di conferma dettagliato
      let confirmMessage = `⚠️ ATTENZIONE: Eliminazione a Cascata\n\n`;
      confirmMessage += `Sei sicuro di voler eliminare definitivamente la polizza ${managingPolicy.policy_number}?\n\n`;
      confirmMessage += `Questa azione eliminerà PERMANENTEMENTE:\n`;
      confirmMessage += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      
      // Preventivo di origine
      if (managingPolicy.quote_id) {
        confirmMessage += `📋 Preventivo di origine\n`;
      }
      
      // Sinistri
      if (linkedClaims.length > 0) {
        confirmMessage += `🚨 ${linkedClaims.length} Sinistro/i:\n`;
        linkedClaims.forEach(c => {
          confirmMessage += `   • ${new Date(c.claim_date).toLocaleDateString('it-IT')} - €${c.amount_claimed || 0}\n`;
        });
      }
      
      // Pagamenti
      if (linkedPayments.length > 0) {
        confirmMessage += `💰 Record Finanziari:\n`;
        if (incassi.length > 0) {
          const totIncassi = incassi.reduce((s, p) => s + (p.amount || 0), 0);
          confirmMessage += `   • ${incassi.length} Incasso/i (Tot: €${totIncassi.toFixed(2)})\n`;
        }
        if (versamenti.length > 0) {
          const totVersamenti = versamenti.reduce((s, p) => s + (p.amount || 0), 0);
          confirmMessage += `   • ${versamenti.length} Versamento/i alla compagnia (Tot: €${totVersamenti.toFixed(2)})\n`;
        }
        if (provvigioni.length > 0) {
          const totProvvigioni = provvigioni.reduce((s, p) => s + (p.amount || 0), 0);
          confirmMessage += `   • ${provvigioni.length} Provvigione/i (Tot: €${totProvvigioni.toFixed(2)})\n`;
        }
      }
      
      confirmMessage += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      confirmMessage += `\n❌ QUESTA AZIONE È IRREVERSIBILE.\n\nConfermi l'eliminazione?`;
      
      if (!window.confirm(confirmMessage)) return;
      
      const response = await axios.delete(`${API}/policies/${managingPolicy.id}`);
      
      // Mostra toast con riepilogo eliminazione
      const cascade = response.data.cascade_deleted;
      let successMsg = 'Polizza eliminata';
      const deletedItems = [];
      if (cascade?.quote) deletedItems.push('preventivo');
      if (cascade?.claims > 0) deletedItems.push(`${cascade.claims} sinistri`);
      if (cascade?.payments > 0) deletedItems.push(`${cascade.payments} pagamenti`);
      if (deletedItems.length > 0) {
        successMsg += ` + ${deletedItems.join(', ')}`;
      }
      toast.success(successMsg);
      
      setPolicyManagementOpen(false);
      setManagementAction(null);
      // Refresh policies
      if (selectedAsset) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  // Fetch pagamenti per la polizza corrente
  const fetchPolicyPayments = async (policyId) => {
    try {
      const response = await axios.get(`${API}/payments?policy_id=${policyId}`);
      setPolicyPayments(response.data);
    } catch (error) {
      console.error('Error fetching policy payments:', error);
      setPolicyPayments([]);
    }
  };

  // Calcola lo stato di pagamento della polizza
  const getPolicyPaymentStatus = () => {
    if (!managingPolicy) return { status: 'unknown', icon: '⚪', label: 'N/D' };
    
    const totalIncassi = policyPayments
      .filter(p => p.type === 'incasso')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    
    const premium = managingPolicy.premium_amount || 0;
    
    if (totalIncassi === 0) {
      return { status: 'pending', icon: '🔴', label: 'Da Incassare', color: 'text-red-600 bg-red-50' };
    } else if (totalIncassi < premium) {
      return { status: 'partial', icon: '🟡', label: `Parziale (€${totalIncassi.toLocaleString('it-IT')} / €${premium.toLocaleString('it-IT')})`, color: 'text-amber-600 bg-amber-50' };
    } else {
      return { status: 'paid', icon: '🟢', label: 'Incassata', color: 'text-emerald-600 bg-emerald-50' };
    }
  };

  // Apri form incasso
  const openIncassoForm = () => {
    setAccountingAction('incasso');
    setAccountingFormData({
      amount: managingPolicy?.premium_amount || 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'contanti_drop',
      commission_in: 0,
      commission_out: 0,
      notes: ''
    });
  };

  // Apri form uscita
  const openUscitaForm = () => {
    setAccountingAction('uscita');
    setAccountingFormData({
      amount: managingPolicy?.premium_amount || 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bonifico',
      commission_in: 0,
      commission_out: 0,
      notes: ''
    });
  };

  // Registra incasso cliente
  const handleRegisterIncasso = async () => {
    if (!managingPolicy || !accountingFormData.amount) {
      toast.error('Inserisci un importo valido');
      return;
    }
    try {
      await axios.post(`${API}/payments`, {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        amount: parseFloat(accountingFormData.amount),
        payment_date: accountingFormData.payment_date,
        payment_method: accountingFormData.payment_method,
        type: 'incasso',
        commission_in: parseFloat(accountingFormData.commission_in) || 0,
        commission_out: parseFloat(accountingFormData.commission_out) || 0,
        notes: accountingFormData.notes
      });
      toast.success('Incasso registrato con successo');
      setAccountingAction(null);
      fetchPolicyPayments(managingPolicy.id);
      // Aggiorna le polizze per sincronizzare lo stato pagamento
      if (selectedAsset?.id) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    }
  };

  // Registra pagamento fornitore
  const handleRegisterUscita = async () => {
    if (!managingPolicy || !accountingFormData.amount) {
      toast.error('Inserisci un importo valido');
      return;
    }
    try {
      await axios.post(`${API}/payments`, {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        amount: parseFloat(accountingFormData.amount),
        payment_date: accountingFormData.payment_date,
        payment_method: accountingFormData.payment_method,
        type: 'uscita',
        commission_in: parseFloat(accountingFormData.commission_in) || 0,
        commission_out: parseFloat(accountingFormData.commission_out) || 0,
        agency: managingPolicy.agent || '',
        notes: accountingFormData.notes
      });
      toast.success('Pagamento fornitore registrato');
      setAccountingAction(null);
      fetchPolicyPayments(managingPolicy.id);
      // Aggiorna le polizze per sincronizzare lo stato pagamento
      if (selectedAsset?.id) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    }
  };

  // Registra provvigione entrata
  const handleRegisterProvvigioneEntrata = async () => {
    if (!managingPolicy || !accountingFormData.commission_in) {
      toast.error('Inserisci un importo valido');
      return;
    }
    try {
      await axios.post(`${API}/payments`, {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        amount: 0,
        payment_date: accountingFormData.payment_date,
        payment_method: 'provvigione',
        type: 'provvigione_entrata',
        commission_in: parseFloat(accountingFormData.commission_in) || 0,
        commission_out: 0,
        commission_status: accountingFormData.commission_in_status || 'da_definire',
        agency: managingPolicy.agent || '',
        notes: accountingFormData.notes
      });
      toast.success('Provvigione entrata registrata');
      setPaymentsModalTab('unificato');
      resetAccountingForm();
      fetchPolicyPayments(managingPolicy.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    }
  };

  // Registra provvigione uscita
  const handleRegisterProvvigioneUscita = async () => {
    if (!managingPolicy || !accountingFormData.commission_out) {
      toast.error('Inserisci un importo valido');
      return;
    }
    try {
      await axios.post(`${API}/payments`, {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        amount: 0,
        payment_date: accountingFormData.payment_date,
        payment_method: 'provvigione',
        type: 'provvigione_uscita',
        commission_in: 0,
        commission_out: parseFloat(accountingFormData.commission_out) || 0,
        commission_status: accountingFormData.commission_out_status || 'da_definire',
        agency: managingPolicy.agent || '',
        collaboratore: accountingFormData.collaboratore || '',
        notes: accountingFormData.notes
      });
      toast.success('Provvigione uscita registrata');
      setPaymentsModalTab('unificato');
      resetAccountingForm();
      fetchPolicyPayments(managingPolicy.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    }
  };

  // Registrazione unificata (one-shot)
  const handleRegisterUnificato = async () => {
    if (!managingPolicy) return;
    
    const promises = [];
    const results = [];
    
    // Ottieni l'agente/fonte del cliente per la provvigione uscita (NON MODIFICABILE)
    const clientAgentSource = selectedClient?.agent_source || '';
    // Ottieni l'agenzia dalla polizza per la provvigione entrata (NON MODIFICABILE)
    const policyAgency = managingPolicy?.agent || '';
    
    // Incasso Cliente
    if (parseFloat(accountingFormData.amount) > 0) {
      promises.push(
        axios.post(`${API}/payments`, {
          policy_id: managingPolicy.id,
          client_id: managingPolicy.client_id,
          amount: parseFloat(accountingFormData.amount),
          payment_date: accountingFormData.payment_date,
          payment_method: accountingFormData.payment_method,
          type: 'incasso',
          commission_in: 0,
          commission_out: 0,
          agency: policyAgency,
          notes: accountingFormData.notes
        }).then(() => results.push('Incasso'))
      );
    }
    
    // Pagamento Fornitore
    if (parseFloat(accountingFormData.uscita_amount) > 0) {
      promises.push(
        axios.post(`${API}/payments`, {
          policy_id: managingPolicy.id,
          client_id: managingPolicy.client_id,
          amount: parseFloat(accountingFormData.uscita_amount),
          payment_date: accountingFormData.payment_date,
          payment_method: accountingFormData.uscita_method || 'bonifico',
          type: 'uscita',
          commission_in: 0,
          commission_out: 0,
          agency: policyAgency,
          notes: accountingFormData.notes
        }).then(() => results.push('Uscita'))
      );
    }
    
    // Provvigione Entrata - SEMPRE salvata (anche con importo 0)
    // L'agenzia è fissa dalla polizza, non modificabile
    promises.push(
      axios.post(`${API}/payments`, {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        amount: 0,
        payment_date: accountingFormData.payment_date,
        payment_method: 'provvigione',
        type: 'provvigione_entrata',
        commission_in: parseFloat(accountingFormData.commission_in) || 0,
        commission_out: 0,
        commission_status: accountingFormData.commission_in_status || 'da_definire',
        // Collegamento FISSO all'Agenzia/Fornitore della polizza
        agency: policyAgency,
        notes: accountingFormData.notes
      }).then(() => results.push('Provv. Entrata'))
    );
    
    // Provvigione Uscita - SEMPRE salvata (anche con importo 0)
    // Il collaboratore è fisso dall'agente/fonte del cliente, non modificabile
    promises.push(
      axios.post(`${API}/payments`, {
        policy_id: managingPolicy.id,
        client_id: managingPolicy.client_id,
        amount: 0,
        payment_date: accountingFormData.payment_date,
        payment_method: 'provvigione',
        type: 'provvigione_uscita',
        commission_in: 0,
        commission_out: parseFloat(accountingFormData.commission_out) || 0,
        commission_status: accountingFormData.commission_out_status || 'da_definire',
        agency: policyAgency,
        // Collegamento FISSO all'Agente/Fonte del cliente
        collaboratore: clientAgentSource,
        notes: accountingFormData.notes
      }).then(() => results.push('Provv. Uscita'))
    );
    
    try {
      await Promise.all(promises);
      toast.success(`Pagamento registrato correttamente (${results.join(', ')})`);
      resetAccountingForm();
      fetchPolicyPayments(managingPolicy.id);
      // Aggiorna le polizze del bene per sincronizzare lo stato pagamento
      if (selectedAsset?.id) {
        const policiesRes = await axios.get(`${API}/policies?asset_id=${selectedAsset.id}`);
        setAssetPolicies(policiesRes.data);
      }
      // Chiudi automaticamente il modal dopo il salvataggio
      setPaymentsModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    }
  };

  // Reset form contabilità
  const resetAccountingForm = () => {
    setAccountingFormData({
      amount: managingPolicy?.premium_amount || 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'contanti_drop',
      // Pre-compila uscita con il premio della polizza
      uscita_amount: managingPolicy?.premium_amount || 0,
      uscita_method: 'bonifico',
      commission_in: 0,  // Default 0
      commission_out: 0, // Default 0
      commission_in_status: 'da_definire',
      commission_out_status: 'da_definire',
      notes: ''
    });
    setEditingPayment(null);
  };

  // Apri modal Centro Pagamenti
  const openPaymentsModal = () => {
    setPaymentsModalOpen(true);
    setPaymentsModalTab('unificato');
    // Pre-compila form con dati polizza
    setAccountingFormData({
      amount: managingPolicy?.premium_amount || 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'contanti_drop',
      uscita_amount: managingPolicy?.premium_amount || 0,
      uscita_method: 'bonifico',
      commission_in: 0,  // Default 0
      commission_out: 0, // Default 0
      commission_in_status: 'da_definire',
      commission_out_status: 'da_definire',
      notes: ''
    });
    setEditingPayment(null);
    if (managingPolicy) {
      fetchPolicyPayments(managingPolicy.id);
    }
  };

  // Elimina pagamento
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo movimento?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Movimento eliminato');
      fetchPolicyPayments(managingPolicy.id);
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Modifica pagamento
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setAccountingFormData({
      amount: payment.amount || 0,
      payment_date: payment.payment_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      payment_method: payment.payment_method || 'contanti',
      commission_in: payment.commission_in || 0,
      commission_out: payment.commission_out || 0,
      collaboratore: payment.collaboratore || '',
      notes: payment.notes || ''
    });
    // Imposta il tab corretto
    if (payment.type === 'incasso') setPaymentsModalTab('incasso');
    else if (payment.type === 'uscita') setPaymentsModalTab('uscita');
    else if (payment.type === 'provvigione_entrata') setPaymentsModalTab('provv_entrata');
    else if (payment.type === 'provvigione_uscita') setPaymentsModalTab('provv_uscita');
  };

  // Salva modifica pagamento
  const handleUpdatePayment = async () => {
    if (!editingPayment) return;
    try {
      await axios.put(`${API}/payments/${editingPayment.id}`, {
        ...editingPayment,
        amount: parseFloat(accountingFormData.amount) || 0,
        payment_date: accountingFormData.payment_date,
        payment_method: accountingFormData.payment_method,
        commission_in: parseFloat(accountingFormData.commission_in) || 0,
        commission_out: parseFloat(accountingFormData.commission_out) || 0,
        collaboratore: accountingFormData.collaboratore || '',
        notes: accountingFormData.notes
      });
      toast.success('Movimento aggiornato');
      setEditingPayment(null);
      resetAccountingForm();
      fetchPolicyPayments(managingPolicy.id);
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  // Azzera tutti i movimenti della polizza (Reset Pagamenti)
  const handleResetAllPayments = async () => {
    if (!managingPolicy) return;
    setResettingPayments(true);
    try {
      const response = await axios.delete(`${API}/policies/${managingPolicy.id}/payments`);
      const deletedCount = response.data.deleted_count || 0;
      
      // Svuota la lista movimenti immediatamente nella UI
      setPolicyPayments([]);
      
      // Reset form ai valori predefiniti
      resetAccountingForm();
      
      // Chiudi il confirm modal
      setResetPaymentsConfirmOpen(false);
      
      toast.success(`Azzerati ${deletedCount} movimenti`, {
        description: 'Lo stato della polizza è tornato a "Da Incassare"'
      });
    } catch (error) {
      toast.error('Errore durante l\'azzeramento dei movimenti');
    } finally {
      setResettingPayments(false);
    }
  };

  const toggleCoverage = (coverage) => {
    setQuoteFormData(prev => ({
      ...prev,
      coverage_types: prev.coverage_types.includes(coverage)
        ? prev.coverage_types.filter(c => c !== coverage)
        : [...prev.coverage_types, coverage]
    }));
  };

  const handleUploadDocument = async (e, category = 'anagrafico') => {
    const file = e.target.files[0];
    if (!file || !selectedClient) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', category === 'gestione' ? docDescriptionGestione : docDescription);
    formData.append('category', category);
    
    try {
      await axios.post(`${API}/clients/${selectedClient.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento caricato');
      if (category === 'gestione') {
        setDocDescriptionGestione('');
      } else {
        setDocDescription('');
      }
      const response = await axios.get(`${API}/clients/${selectedClient.id}`);
      setSelectedClient(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!selectedClient) return;
    try {
      await axios.delete(`${API}/clients/${selectedClient.id}/documents/${docId}`);
      toast.success('Documento eliminato');
      const response = await axios.get(`${API}/clients/${selectedClient.id}`);
      setSelectedClient(response.data);
    } catch (error) {
      toast.error('Errore');
    }
  };

  const [documentPreviews, setDocumentPreviews] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleOpenPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewZoom(1);
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/documents/${doc.id}`, { responseType: 'blob' });
      const mimeType = response.headers['content-type'] || doc.file_type || 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
      // Update doc file_type if it was empty
      if (!doc.file_type) {
        doc.file_type = mimeType;
      }
    } catch (error) {
      toast.error('Errore caricamento anteprima');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (previewBlobUrl) window.URL.revokeObjectURL(previewBlobUrl);
    setPreviewDoc(null);
    setPreviewBlobUrl(null);
    setPreviewZoom(1);
  };

  const handleDownloadDoc = () => {
    if (!previewBlobUrl || !previewDoc) return;
    const a = document.createElement('a');
    a.href = previewBlobUrl;
    a.download = previewDoc.filename || 'documento';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDirectDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}`, { responseType: 'blob' });
      const mimeType = response.headers['content-type'] || doc.file_type || 'application/octet-stream';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'documento';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch (error) {
      toast.error('Errore download');
    }
  };

  const handlePrintDoc = () => {
    if (!previewBlobUrl) return;
    const printWindow = window.open(previewBlobUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => { printWindow.print(); });
    }
  };

  const loadDocumentPreview = async (docId) => {
    if (documentPreviews[docId] !== undefined) return;
    try {
      const response = await axios.get(`${API}/documents/${docId}/preview`);
      if (response.data?.data) {
        setDocumentPreviews(prev => ({ ...prev, [docId]: response.data }));
      } else {
        setDocumentPreviews(prev => ({ ...prev, [docId]: { type: 'none', data: null } }));
      }
    } catch (error) {
      setDocumentPreviews(prev => ({ ...prev, [docId]: { type: 'error', data: null } }));
    }
  };

  // Load previews when client details are opened
  useEffect(() => {
    if (selectedClient?.documents && detailsOpen) {
      selectedClient.documents.forEach(doc => {
        loadDocumentPreview(doc.id);
      });
    }
  }, [selectedClient, detailsOpen]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Anagrafica Clienti
          </h1>
          <p className="text-slate-600 mt-1">Gestisci tutti i clienti del CRM</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => { setEditingClient(null); setFormData(emptyClient); }} data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />Nuovo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent 
            className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
            onInteractOutside={(e) => {
              // Prevent dialog from closing when clicking on Google Maps pac-container
              const target = e.target;
              if (target?.closest && (target.closest('.pac-container') || target.closest('.pac-item'))) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onPointerDownOutside={(e) => {
              // Prevent dialog from closing when clicking on Google Maps pac-container
              const target = e.target;
              if (target?.closest && (target.closest('.pac-container') || target.closest('.pac-item'))) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Modifica Cliente' : 'Nuovo Cliente'}</DialogTitle>
              <DialogDescription>Inserisci i dati del cliente</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Company Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Cliente Aziendale</Label>
                  <p className="text-sm text-slate-500">Attiva per inserire dati aziendali</p>
                </div>
                <Switch
                  checked={formData.is_company}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_company: checked })}
                  data-testid="is-company-switch"
                />
              </div>

              {/* Company Fields */}
              {formData.is_company && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Ragione Sociale</Label>
                      <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} data-testid="company-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>P.IVA</Label>
                      <Input value={formData.vat_number} onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} data-testid="vat-number" />
                    </div>
                    <div className="space-y-2">
                      <Label>PEC</Label>
                      <Input type="email" value={formData.pec} onChange={(e) => setFormData({ ...formData, pec: e.target.value })} data-testid="pec" />
                    </div>
                  </div>
                  
                  {/* Sede Legale - Company Address with Google Maps */}
                  <div className="pt-3 border-t border-blue-200">
                    <p className="text-sm font-medium text-blue-800 mb-3">Sede Legale</p>
                    
                    {/* Indirizzo con Google Maps Autocomplete + Numero Civico */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="space-y-2 col-span-3">
                        <Label>Indirizzo <span className="text-xs text-blue-600">(ricerca Google Maps)</span></Label>
                        {GOOGLE_MAPS_API_KEY ? (
                          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                            <AddressAutocomplete
                              value={formData.company_address}
                              defaultValue={formData.company_address}
                              onPlaceSelect={(addressData) => {
                                console.log('Company onPlaceSelect called with:', addressData);
                                // Populate all company address fields
                                setFormData(prev => ({
                                  ...prev,
                                  company_address: addressData.address || prev.company_address,
                                  company_address_number: addressData.address_number || prev.company_address_number,
                                  company_city: addressData.city || prev.company_city,
                                  company_provincia: addressData.city_provincia || prev.company_provincia,
                                  company_postal_code: addressData.postal_code || prev.company_postal_code,
                                  company_latitude: addressData.latitude || prev.company_latitude,
                                  company_longitude: addressData.longitude || prev.company_longitude
                                }));
                                
                                // Toast confirmation
                                const fullAddress = addressData.address_number 
                                  ? `${addressData.address}, ${addressData.address_number}`
                                  : addressData.address;
                                if (addressData.city || addressData.postal_code) {
                                  toast.success('Sede legale compilata automaticamente', {
                                    description: `${fullAddress}${addressData.city ? `, ${addressData.city}` : ''}${addressData.city_provincia ? ` (${addressData.city_provincia})` : ''}`
                                  });
                                }
                              }}
                              placeholder="es. Via Roma, Piazza Duomo..."
                            />
                          </APIProvider>
                        ) : (
                          <Input 
                            value={formData.company_address} 
                            onChange={(e) => setFormData({ ...formData, company_address: e.target.value })} 
                            placeholder="es. Via Roma"
                            data-testid="company-address" 
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>N. Civico</Label>
                        <Input 
                          value={formData.company_address_number} 
                          onChange={(e) => setFormData({ ...formData, company_address_number: e.target.value })} 
                          placeholder="es. 10/A"
                          data-testid="company-address-number"
                        />
                      </div>
                    </div>
                    
                    {/* Città, Provincia, CAP */}
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>Città</Label>
                        <Input 
                          value={formData.company_city} 
                          onChange={(e) => handleCompanyCityChange(e.target.value)} 
                          placeholder="es. Milano"
                          data-testid="company-city" 
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Provincia</Label>
                        <Select value={formData.company_provincia} onValueChange={handleCompanyProvinciaChange}>
                          <SelectTrigger data-testid="company-provincia-select"><SelectValue placeholder="Prov." /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {PROVINCE.map((prov) => (
                              <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>CAP {companyCapLoading && <span className="text-slate-400 text-xs">(caricamento...)</span>}</Label>
                        {companyAvailableCaps.length > 1 ? (
                          <Select value={formData.company_postal_code} onValueChange={(v) => setFormData({ ...formData, company_postal_code: v })}>
                            <SelectTrigger data-testid="company-postal-code-select"><SelectValue placeholder="Seleziona CAP" /></SelectTrigger>
                            <SelectContent className="max-h-60">
                              {companyAvailableCaps.map((cap) => (
                                <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input 
                            value={formData.company_postal_code} 
                            onChange={(e) => setFormData({ ...formData, company_postal_code: e.target.value })} 
                            placeholder="es. 20100"
                            maxLength={5}
                            data-testid="company-postal-code" 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cognome</Label>
                  <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} data-testid="last-name" />
                </div>
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} data-testid="first-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sesso</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                    <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Maschio</SelectItem>
                      <SelectItem value="F">Femmina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data di Nascita</Label>
                  <DateInput value={formData.birth_date} onChange={(val) => setFormData({ ...formData, birth_date: val })} data-testid="birth-date" placeholder="gg/mm/aaaa" />
                </div>
              </div>
              
              {/* Birth Place - Italia/Estero */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Nato all'Estero</Label>
                    <p className="text-sm text-slate-500">Attiva se il cliente è nato fuori dall'Italia</p>
                  </div>
                  <Switch
                    checked={formData.birth_abroad}
                    onCheckedChange={(checked) => setFormData({ 
                      ...formData, 
                      birth_abroad: checked,
                      birth_comune: '',
                      birth_provincia: '',
                      birth_country: ''
                    })}
                    data-testid="birth-abroad-switch"
                  />
                </div>
                
                {!formData.birth_abroad ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 relative">
                      <Label>Comune di Nascita</Label>
                      <Input 
                        value={formData.birth_comune} 
                        onChange={(e) => {
                          setFormData({ ...formData, birth_comune: e.target.value });
                          // Ricerca comuni nel database locale ISTAT (NO Google Maps)
                          searchBirthComune(e.target.value);
                        }}
                        onFocus={() => {
                          if (birthComuneSuggestions.length > 0) {
                            setShowBirthComuneDropdown(true);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowBirthComuneDropdown(false), 200);
                        }}
                        onKeyDown={(e) => {
                          if (!showBirthComuneDropdown || birthComuneSuggestions.length === 0) return;
                          
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const idx = birthComuneSuggestions.findIndex(s => s.nome === formData.birth_comune);
                            const next = idx < birthComuneSuggestions.length - 1 ? idx + 1 : 0;
                            setFormData({ ...formData, birth_comune: birthComuneSuggestions[next].nome });
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            const idx = birthComuneSuggestions.findIndex(s => s.nome === formData.birth_comune);
                            const prev = idx > 0 ? idx - 1 : birthComuneSuggestions.length - 1;
                            setFormData({ ...formData, birth_comune: birthComuneSuggestions[prev].nome });
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const selected = birthComuneSuggestions.find(s => s.nome === formData.birth_comune);
                            if (selected) {
                              selectBirthComune(selected);
                            } else if (birthComuneSuggestions.length > 0) {
                              selectBirthComune(birthComuneSuggestions[0]);
                            }
                          } else if (e.key === 'Escape') {
                            setShowBirthComuneDropdown(false);
                          }
                        }}
                        placeholder="es. Roma" 
                        data-testid="birth-comune"
                        autoComplete="off"
                      />
                      {/* Dropdown suggerimenti comuni (database locale ISTAT - NO Google Maps) */}
                      {showBirthComuneDropdown && birthComuneSuggestions.length > 0 && (
                        <div 
                          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
                          data-testid="birth-comuni-dropdown"
                        >
                          {birthComuneSuggestions.map((comune, i) => (
                            <div
                              key={`${comune.nome}-${comune.provincia}-${i}`}
                              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex justify-between items-center text-sm ${
                                formData.birth_comune === comune.nome ? 'bg-blue-100' : ''
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectBirthComune(comune);
                              }}
                              data-testid={`birth-comune-option-${i}`}
                            >
                              <span className="font-medium">{comune.nome}</span>
                              <span className="text-slate-500 text-xs">({comune.provincia})</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Provincia (sigla)</Label>
                      <Input 
                        value={formData.birth_provincia} 
                        onChange={(e) => setFormData({ ...formData, birth_provincia: e.target.value.toUpperCase() })}
                        placeholder="es. RM"
                        maxLength={2}
                        data-testid="birth-provincia-input"
                        className="uppercase"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Nazione di Nascita</Label>
                    <Select value={formData.birth_country} onValueChange={(v) => setFormData({ ...formData, birth_country: v })}>
                      <SelectTrigger data-testid="birth-country-select"><SelectValue placeholder="Seleziona nazione" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {NAZIONI.map((nazione) => (
                          <SelectItem key={nazione} value={nazione}>{nazione}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Codice Fiscale {checkingCf && <span className="text-slate-400 text-xs">(verifica...)</span>}</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.fiscal_code} 
                    onChange={(e) => handleFiscalCodeChange(e.target.value)} 
                    className={`flex-1 ${duplicateCfClient ? 'border-red-500 bg-red-50' : ''}`} 
                    data-testid="fiscal-code" 
                  />
                  <Button type="button" variant="outline" onClick={calculateFiscalCode} data-testid="calc-fiscal-code-btn">
                    Calcola CF
                  </Button>
                </div>
                
                {/* Avviso duplicato CF */}
                {duplicateCfClient && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">
                          Codice Fiscale già esistente!
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          Questo CF è già associato a: <strong>{duplicateCfClient.name}</strong>
                        </p>
                        <Button 
                          type="button"
                          variant="outline" 
                          size="sm" 
                          className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                          onClick={openDuplicateClient}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Apri scheda cliente esistente
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* BLOCCO INDIRIZZO - Spostato subito dopo Codice Fiscale */}
              {/* Indirizzo con Google Maps Autocomplete e Numero Civico */}
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2 col-span-3">
                  <Label>Indirizzo <span className="text-xs text-blue-600">(ricerca Google Maps)</span></Label>
                  {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <AddressAutocomplete
                        value={formData.address}
                        defaultValue={formData.address}
                        onPlaceSelect={(addressData) => {
                          console.log('Cliente onPlaceSelect called with:', addressData);
                          // SEPARARE: address va nel campo Indirizzo, address_number va nel campo N. Civico
                          setFormData(prev => ({
                            ...prev,
                            address: addressData.address || prev.address,  // Solo la via, SENZA numero civico
                            address_number: addressData.address_number || prev.address_number,  // Numero civico separato
                            city: addressData.city || prev.city,
                            city_provincia: addressData.city_provincia || prev.city_provincia,
                            postal_code: addressData.postal_code || prev.postal_code
                          }));
                          
                          // Toast con indirizzo completo
                          const fullAddress = addressData.address_number 
                            ? `${addressData.address}, ${addressData.address_number}`
                            : addressData.address;
                          if (addressData.city || addressData.postal_code) {
                            toast.success('Indirizzo compilato automaticamente', {
                              description: `${fullAddress}${addressData.city ? `, ${addressData.city}` : ''}${addressData.city_provincia ? ` (${addressData.city_provincia})` : ''}`
                            });
                          }
                        }}
                        placeholder="es. Via Roma, Piazza Duomo..."
                      />
                    </APIProvider>
                  ) : (
                    <Input 
                      value={formData.address} 
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                      placeholder="es. Via Roma"
                      data-testid="address" 
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>N. Civico</Label>
                  <Input 
                    value={formData.address_number} 
                    onChange={(e) => setFormData({ ...formData, address_number: e.target.value })} 
                    placeholder="es. 10/A"
                    data-testid="address-number"
                  />
                </div>
              </div>
              
              {/* Città con Autocomplete */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 relative">
                  <Label>Città {citySearching && <span className="text-slate-400 text-xs">(ricerca...)</span>}</Label>
                  <Input 
                    value={formData.city} 
                    onChange={(e) => handleCityInputChange(e.target.value)} 
                    onFocus={() => formData.city.length >= 2 && setShowCitySuggestions(citySuggestions.length > 0)}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                    placeholder="Digita per cercare..."
                    autoComplete="off"
                    data-testid="city" 
                  />
                  {/* Dropdown suggerimenti città */}
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {citySuggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                          onMouseDown={() => selectCity(suggestion)}
                        >
                          <span className="font-medium">{suggestion.name}</span>
                          <Badge variant="outline" className="text-xs">{suggestion.provincia}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Provincia</Label>
                  <Select value={formData.city_provincia} onValueChange={handleCityProvinciaChange}>
                    <SelectTrigger data-testid="city-provincia-select"><SelectValue placeholder="Prov." /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {PROVINCE.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>CAP {capLoading && <span className="text-slate-400 text-xs">(caricamento...)</span>}</Label>
                  {availableCaps.length > 1 ? (
                    <Select value={formData.postal_code} onValueChange={(v) => setFormData({ ...formData, postal_code: v })}>
                      <SelectTrigger data-testid="postal-code-select"><SelectValue placeholder="Seleziona CAP" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {availableCaps.map((cap) => (
                          <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input 
                      value={formData.postal_code} 
                      onChange={(e) => handleCapChange(e.target.value)} 
                      placeholder="Inserisci CAP (es. 59100)"
                      maxLength={5}
                      data-testid="postal-code" 
                    />
                  )}
                  {availableCaps.length > 1 && (
                    <p className="text-xs text-amber-600">Seleziona il CAP corretto ({availableCaps.length} disponibili)</p>
                  )}
                  {formData.postal_code && formData.postal_code.length === 5 && !formData.city && (
                    <p className="text-xs text-blue-600">💡 Inserisci CAP per auto-compilare Città e Provincia</p>
                  )}
                </div>
              </div>

              {/* BLOCCO CONTATTI - Ora sotto il blocco indirizzo */}
              <div className="space-y-2">
                <Label>Cellulare</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="phone" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4 text-green-500" /> WhatsApp
                  </Label>
                  <Input 
                    value={formData.whatsapp_contact} 
                    onChange={(e) => setFormData({ ...formData, whatsapp_contact: e.target.value })} 
                    placeholder="es. +393401234567"
                    data-testid="whatsapp-contact" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <WeChatIcon className="w-4 h-4 text-green-600" /> WeChat ID
                  </Label>
                  <Input 
                    value={formData.wechat_contact} 
                    onChange={(e) => setFormData({ ...formData, wechat_contact: e.target.value })} 
                    placeholder="es. myWeChatID"
                    data-testid="wechat-contact" 
                  />
                </div>
              </div>
              
              {/* Agente / Fonte */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Agente / Fonte</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                    onClick={() => setAgentSourcesDialogOpen(true)}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Gestione
                  </Button>
                </div>
                <Select 
                  value={formData.agent_source || "__none__"} 
                  onValueChange={(v) => setFormData({ ...formData, agent_source: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="agent-source-select">
                    <SelectValue placeholder="Seleziona agente o fonte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuno</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.name}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Chi ha presentato o acquisito questo cliente</p>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="notes" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setAvailableCaps([]); }}>Annulla</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-client-btn">
                  {editingClient ? 'Salva' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome, email, codice fiscale, codice cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients"
              />
            </div>
            <MultiSelect
              options={agents.map(a => ({ value: a.name, label: a.name }))}
              value={filterAgentSource}
              onValueChange={setFilterAgentSource}
              placeholder="Agente/Fonte"
              className="w-48"
            />
            <div className="flex gap-2">
              <Button variant={filterCompany === null ? 'default' : 'outline'} size="sm" onClick={() => setFilterCompany(null)}>Tutti</Button>
              <Button variant={filterCompany === false ? 'default' : 'outline'} size="sm" onClick={() => setFilterCompany(false)}>Privati</Button>
              <Button variant={filterCompany === true ? 'default' : 'outline'} size="sm" onClick={() => setFilterCompany(true)}>Aziende</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessun cliente trovato</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Codice</TableHead>
                    <TableHead className="font-semibold">
                      <button 
                        onClick={() => handleSort('name')} 
                        className="flex items-center gap-1 hover:text-emerald-600 transition-colors"
                        data-testid="sort-name"
                      >
                        Cliente {getSortIcon('name')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold">Contatti</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">
                      <button 
                        onClick={() => handleSort('created_at')} 
                        className="flex items-center gap-1 hover:text-emerald-600 transition-colors"
                        data-testid="sort-created"
                      >
                        Data Creazione {getSortIcon('created_at')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold">Documenti</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedClients.map((client) => (
                    <TableRow key={client.id} className="cursor-pointer hover:bg-slate-50" onClick={() => handleViewDetails(client)} data-testid={`client-row-${client.id}`}>
                      <TableCell className="font-mono text-sm text-blue-600">{client.client_code}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${client.is_company ? 'bg-blue-100' : 'bg-slate-100'}`}>
                            {client.is_company ? <Building2 className="w-5 h-5 text-blue-600" /> : <User className="w-5 h-5 text-slate-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{getClientDisplayName(client)}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-3 h-3" />{client.email || '-'}</div>
                          <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-3 h-3" />{client.phone || '-'}</div>
                          <div className="flex items-center gap-2">
                            {client.whatsapp_contact ? (
                              <a 
                                href={`https://wa.me/${client.whatsapp_contact.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title={`WhatsApp: ${client.whatsapp_contact}`}
                                className="text-green-500 hover:text-green-600 transition-colors"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </a>
                            ) : (
                              <MessageCircle className="w-4 h-4 text-slate-300" title="WhatsApp non disponibile" />
                            )}
                            {client.wechat_contact ? (
                              <span 
                                title={`WeChat: ${client.wechat_contact}`}
                                className="text-green-600 cursor-pointer"
                              >
                                <WeChatIcon className="w-4 h-4" />
                              </span>
                            ) : (
                              <WeChatIcon className="w-4 h-4 text-slate-300" title="WeChat non disponibile" />
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {client.is_company ? (
                          <Badge className="bg-blue-100 text-blue-700">Azienda</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Privato</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {client.created_at ? new Date(client.created_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{client.documents?.length || 0} doc</Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`client-menu-${client.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetails(client)}><Eye className="w-4 h-4 mr-2" />Dettagli</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(client)}><Pencil className="w-4 h-4 mr-2" />Modifica</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(client.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Elimina</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50 rounded-b-lg">
                  <div className="text-sm text-slate-600">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalClients)} di {totalClients} clienti
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first, last, current, and pages around current
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, idx, arr) => {
                          // Add ellipsis if there's a gap
                          const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsisBefore && (
                                <span className="px-2 text-slate-400">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-slate-900' : ''}`}
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Single page indicator */}
              {totalPages === 1 && totalClients > 0 && (
                <div className="text-sm text-slate-500 text-center py-2">
                  Visualizzando {totalClients} client{totalClients === 1 ? 'e' : 'i'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={(open) => { if (!previewDoc) setDetailsOpen(open); }}>
        <DialogContent 
          className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (previewDoc) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (previewDoc) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedClient?.is_company ? <Building2 className="w-5 h-5 text-blue-600" /> : <User className="w-5 h-5" />}
              {getClientDisplayName(selectedClient)}
              <Badge className="ml-2 font-mono">{selectedClient?.client_code}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-6 mt-4">
              {/* Denominazione */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-slate-500">Denominazione:</p>
                <p className="font-bold text-amber-800 text-lg">
                  {getClientDisplayName(selectedClient, true)}
                </p>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* DATI AZIENDA - Mostrato PRIMA se è un'azienda */}
                {selectedClient.is_company && (
                  <>
                    <div className="space-y-1 col-span-2 md:col-span-3">
                      <p className="text-sm font-semibold text-blue-600">Dati Azienda</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">Ragione Sociale</p>
                      <p className="font-medium">{selectedClient.company_name || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">P.IVA</p>
                      <p className="font-medium font-mono">{selectedClient.vat_number || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">PEC</p>
                      <p className="font-medium">{selectedClient.pec || '-'}</p>
                    </div>
                    {/* Sede Legale */}
                    {(selectedClient.company_address || selectedClient.company_city) && (
                      <>
                        <div className="space-y-1 col-span-2 md:col-span-3 border-t pt-3 mt-2">
                          <p className="text-sm font-semibold text-blue-600">Sede Legale</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-slate-500">Indirizzo</p>
                          <p className="font-medium">{selectedClient.company_address || '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-slate-500">N. Civico</p>
                          <p className="font-medium">{selectedClient.company_address_number || '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-slate-500">Città</p>
                          <p className="font-medium">{selectedClient.company_city || '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-slate-500">Provincia</p>
                          <p className="font-medium">{selectedClient.company_provincia || '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-slate-500">CAP</p>
                          <p className="font-medium">{selectedClient.company_postal_code || '-'}</p>
                        </div>
                      </>
                    )}
                    {/* Separatore visivo per il Referente */}
                    <div className="space-y-1 col-span-2 md:col-span-3 border-t pt-3 mt-2">
                      <p className="text-sm font-semibold text-blue-600">Referente</p>
                    </div>
                  </>
                )}
                
                {/* DATI REFERENTE (o persona fisica se non è azienda) */}
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Cognome</p>
                  <p className="font-medium">{selectedClient.last_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Nome</p>
                  <p className="font-medium">{selectedClient.first_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Sesso</p>
                  <p className="font-medium">{selectedClient.gender === 'M' ? 'Maschio' : selectedClient.gender === 'F' ? 'Femmina' : '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Data di Nascita</p>
                  <p className="font-medium">{selectedClient.birth_date ? new Date(selectedClient.birth_date).toLocaleDateString('it-IT') : '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Luogo di Nascita</p>
                  <p className="font-medium">
                    {selectedClient.birth_abroad 
                      ? `${selectedClient.birth_country || '-'} (Estero)` 
                      : selectedClient.birth_comune && selectedClient.birth_provincia 
                        ? `${selectedClient.birth_comune} (${selectedClient.birth_provincia})` 
                        : selectedClient.birth_place || '-'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Codice Fiscale</p>
                  <p className="font-medium">{selectedClient.fiscal_code || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Cellulare</p>
                  <p className="font-medium">{selectedClient.phone || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedClient.email || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">WhatsApp</p>
                  {selectedClient.whatsapp_contact ? (
                    <a 
                      href={`https://wa.me/${selectedClient.whatsapp_contact.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-green-600 hover:text-green-700 flex items-center gap-1"
                    >
                      <MessageCircle className="w-4 h-4" />
                      {selectedClient.whatsapp_contact}
                    </a>
                  ) : (
                    <p className="font-medium text-slate-400">-</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">WeChat</p>
                  {selectedClient.wechat_contact ? (
                    <p className="font-medium text-green-600 flex items-center gap-1">
                      <WeChatIcon className="w-4 h-4" />
                      {selectedClient.wechat_contact}
                    </p>
                  ) : (
                    <p className="font-medium text-slate-400">-</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Indirizzo</p>
                  <p className="font-medium">{selectedClient.address || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">N. Civico</p>
                  <p className="font-medium">{selectedClient.address_number || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Città</p>
                  <p className="font-medium">{selectedClient.city || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Provincia</p>
                  <p className="font-medium">{selectedClient.city_provincia || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">CAP</p>
                  <p className="font-medium">{selectedClient.postal_code || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Agente / Fonte</p>
                  <p className="font-medium">{selectedClient.agent_source || '-'}</p>
                </div>
                {selectedClient.notes && (
                  <div className="space-y-1 col-span-2 md:col-span-3">
                    <p className="text-sm text-slate-500">Note</p>
                    <p className="font-medium whitespace-pre-wrap">{selectedClient.notes}</p>
                  </div>
                )}
              </div>

              {/* BENI Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">BENI</h3>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800">
                        <Plus className="w-4 h-4 mr-1" />Aggiungi Beni
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openAddAssetDialog('casa')}>
                        <Home className="w-4 h-4 mr-2 text-blue-600" />Casa
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openAddAssetDialog('attivita')}>
                        <Briefcase className="w-4 h-4 mr-2 text-amber-600" />Attività
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openAddAssetDialog('auto')}>
                        <Car className="w-4 h-4 mr-2 text-emerald-600" />Veicolo
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openAddAssetDialog('salute')}>
                        <ShieldPlus className="w-4 h-4 mr-2 text-rose-600" />Salute
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {clientAssets.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Nessun bene registrato</p>
                ) : (
                  <div className="space-y-3">
                    {clientAssets.map((asset) => {
                      // Determina se il veicolo è inattivo (venduto o rottamato)
                      const isInactive = asset.asset_type === 'auto' && 
                        (asset.vehicle_status === 'venduto' || asset.vehicle_status === 'rottamato');
                      
                      return (
                      <div 
                        key={asset.id} 
                        className={`p-3 rounded-lg border transition-all ${
                          isInactive 
                            ? 'bg-gray-100 border-gray-300' 
                            : 'bg-slate-50 border-slate-200'
                        }`}
                        style={isInactive ? { opacity: 0.6, filter: 'grayscale(0.8)' } : {}}
                        data-testid={`asset-item-${asset.id}`}
                      >
                        {/* PRIMA RIGA - Header con Icona, Nome, Badge Stato e Bottoni */}
                        <div className="flex items-center justify-between mb-3">
                          {/* Sinistra: Icona + Nome */}
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${
                              isInactive ? 'bg-gray-200' : 'bg-white'
                            }`}>
                              {getAssetIcon(asset.asset_type)}
                            </div>
                            <p className={`font-semibold text-base ${isInactive ? 'text-gray-600' : 'text-slate-900'}`}>
                              {getAssetLabel(asset)}
                            </p>
                          </div>
                          
                          {/* Centro: Badge Stato (solo per veicoli venduti/rottamati) */}
                          {isInactive && (
                            <Badge 
                              variant="secondary" 
                              className="bg-gray-200 text-gray-700 border border-gray-400 px-3 py-1 text-xs font-semibold uppercase"
                            >
                              {asset.vehicle_status === 'venduto' ? 'VENDUTO' : 'ROTTAMATO'}
                            </Badge>
                          )}
                          
                          {/* Destra: Bottoni Azioni */}
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditAssetDialog(asset)} title="Modifica bene">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteAsset(asset.id)} className="text-red-600 hover:text-red-700" title="Elimina bene">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              onClick={() => openAssetDetails(asset)} 
                              title="Dettagli completi"
                              className="px-3 h-8 bg-slate-100 hover:bg-slate-200 border-slate-300"
                            >
                              <span className="text-xs text-slate-600 mr-1">Dettagli</span>
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* SECONDA RIGA - Dettagli Tecnici Veicolo (5 colonne fisse) */}
                        {asset.asset_type === 'auto' && (
                          <div className="bg-white rounded border border-slate-100 p-3">
                            <div className="grid grid-cols-5 gap-6">
                              <div className="flex flex-col">
                                <span className="text-xs text-slate-500 mb-1">Targa</span>
                                <span className="font-medium text-sm text-slate-900">{asset.targa || '-'}</span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs text-slate-500 mb-1">Marca</span>
                                <span className="font-medium text-sm text-slate-900">{asset.marca || '-'}</span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs text-slate-500 mb-1">Modello</span>
                                <span className="font-medium text-sm text-slate-900">{asset.modello || '-'}</span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs text-slate-500 mb-1">Immatricolazione</span>
                                <span className="font-medium text-sm text-slate-900">{asset.data_immatricolazione ? new Date(asset.data_immatricolazione).toLocaleDateString('it-IT') : '-'}</span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs text-slate-500 mb-1">Acquisto</span>
                                <span className="font-medium text-sm text-slate-900">{asset.data_acquisto ? new Date(asset.data_acquisto).toLocaleDateString('it-IT') : '-'}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Dati inline per casa */}
                        {asset.asset_type === 'casa' && (
                          <div className="ml-13 pl-13 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-white p-2 rounded border border-slate-100 select-text">
                            <div>
                              <span className="text-slate-500">Indirizzo:</span>
                              <span className="font-medium ml-1 text-slate-900">{asset.indirizzo || '-'}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Città:</span>
                              <span className="font-medium ml-1 text-slate-900">{asset.city || '-'} {asset.provincia && `(${asset.provincia})`}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">CAP:</span>
                              <span className="font-medium ml-1 text-slate-900">{asset.cap || '-'}</span>
                            </div>
                            {asset.dati_catastali && (
                              <div className="col-span-2 md:col-span-3">
                                <span className="text-slate-500">Dati Catastali:</span>
                                <span className="font-medium ml-1 text-slate-900">{asset.dati_catastali}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Dati inline per attività */}
                        {asset.asset_type === 'attivita' && (
                          <div className="ml-13 pl-13 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-white p-2 rounded border border-slate-100 select-text">
                            <div>
                              <span className="text-slate-500">P.IVA:</span>
                              <span className="font-mono font-medium ml-1 text-slate-900">{asset.partita_iva || '-'}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Tipo:</span>
                              <span className="font-medium ml-1 text-slate-900">{asset.tipo_attivita || '-'}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Indirizzo:</span>
                              <span className="font-medium ml-1 text-slate-900">{asset.indirizzo || '-'}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Dati inline per salute */}
                        {asset.asset_type === 'salute' && (
                          <div className="ml-13 pl-13 text-sm bg-white p-2 rounded border border-slate-100 select-text">
                            <div className="text-slate-500 mb-1">
                              Persone assicurate: <span className="font-medium text-slate-900">{asset.persone_assicurate?.length || 0}</span>
                            </div>
                            {asset.persone_assicurate && asset.persone_assicurate.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {asset.persone_assicurate.slice(0, 3).map((persona, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-1 bg-rose-50 text-rose-700 rounded text-xs">
                                    {persona.cognome} {persona.nome}
                                  </span>
                                ))}
                                {asset.persone_assicurate.length > 3 && (
                                  <span className="text-xs text-slate-500">+{asset.persone_assicurate.length - 3} altri</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                )}
              </div>

              {/* Firma Cliente Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Firma Cliente</h3>
                  <label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('description', 'FIRMA');
                        
                        setUploading(true);
                        axios.post(`${API}/clients/${selectedClient.id}/documents`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        }).then(async () => {
                          toast.success('Firma caricata');
                          const response = await axios.get(`${API}/clients/${selectedClient.id}`);
                          setSelectedClient(response.data);
                        }).catch(() => {
                          toast.error('Errore upload firma');
                        }).finally(() => {
                          setUploading(false);
                        });
                      }} 
                      className="hidden" 
                      disabled={uploading}
                      data-testid="upload-signature"
                    />
                    <Button size="sm" variant="outline" asChild disabled={uploading}>
                      <span><Upload className="w-4 h-4 mr-1" />{uploading ? 'Caricamento...' : 'Carica Firma'}</span>
                    </Button>
                  </label>
                </div>
                
                {/* Show existing signature */}
                {(() => {
                  const signatureDoc = selectedClient.documents?.find(doc => 
                    doc.description?.toLowerCase().includes('firma') || 
                    doc.filename?.toLowerCase().includes('firma')
                  );
                  
                  if (signatureDoc) {
                    return (
                      <div className="flex items-center gap-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="w-24 h-16 border rounded overflow-hidden bg-white">
                          {documentPreviews[signatureDoc.id]?.data ? (
                            <img 
                              src={documentPreviews[signatureDoc.id].data} 
                              alt="Firma" 
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <FileImage className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-green-700">Firma presente</p>
                          <p className="text-sm text-green-600">{signatureDoc.filename}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteDocument(signatureDoc.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                        Nessuna firma caricata. Carica un'immagine della firma del cliente per i documenti PDF.
                      </p>
                    );
                  }
                })()}
              </div>

              {/* MUTUI Section */}
              <MutuiClientSection clientId={selectedClient?.id} API={API} />

              {/* Documents Section - Due Livelli */}
              <div className="border-t pt-4 space-y-6">

                {/* SEZIONE 1: Documenti Anagrafici */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />
                      Documenti Anagrafici
                      <span className="text-xs font-normal text-slate-400">(CI, CF, Patente, Passaporto)</span>
                    </h3>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="doc-description-anagrafico"
                        placeholder="Descrizione..."
                        value={docDescription}
                        onChange={(e) => setDocDescription(e.target.value)}
                        className="w-40 h-8 text-sm"
                      />
                      <label>
                        <input type="file" accept="image/*,.pdf" onChange={(e) => handleUploadDocument(e, 'anagrafico')} className="hidden" disabled={uploading} />
                        <Button size="sm" variant="outline" asChild disabled={uploading} data-testid="upload-anagrafico-btn">
                          <span><Upload className="w-4 h-4 mr-1" />{uploading ? '...' : 'Carica'}</span>
                        </Button>
                      </label>
                    </div>
                  </div>

                  {(() => {
                    const anagrafici = (selectedClient.documents || []).filter(d => !d.category || d.category === 'anagrafico');
                    return anagrafici.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {anagrafici.map((doc) => (
                          <div key={doc.id} className="relative group border rounded-lg overflow-hidden bg-slate-50 cursor-pointer" onClick={() => handleOpenPreview(doc)}>
                            <div className="aspect-[3/4] flex items-center justify-center bg-slate-100">
                              {documentPreviews[doc.id]?.data ? (
                                <img src={documentPreviews[doc.id].data} alt={doc.filename} className="w-full h-full object-cover" data-testid={`doc-thumb-${doc.id}`} />
                              ) : documentPreviews[doc.id] === undefined ? (
                                <div className="flex flex-col items-center gap-2">
                                  <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
                                  <span className="text-xs text-slate-400">Caricamento...</span>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center gap-2">
                                  <File className="w-10 h-10 text-slate-400" />
                                  <span className="text-xs text-slate-400">{(doc.file_type || '').includes('pdf') ? 'PDF' : 'File'}</span>
                                </div>
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleOpenPreview(doc); }} data-testid={`doc-preview-${doc.id}`}><Eye className="w-4 h-4" /></Button>
                              <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDirectDownload(doc); }} data-testid={`doc-download-${doc.id}`}><Download className="w-4 h-4" /></Button>
                              <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }} data-testid={`doc-delete-${doc.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                            <div className="p-2 border-t bg-white">
                              <p className="text-xs font-medium truncate">{doc.description || doc.filename}</p>
                              <p className="text-xs text-slate-500">{doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('it-IT') : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-4 text-sm border border-dashed rounded-lg">Nessun documento anagrafico</p>
                    );
                  })()}
                </div>

                {/* SEZIONE 2: Documenti di Gestione */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-amber-600" />
                      Documenti di Gestione
                      <span className="text-xs font-normal text-slate-400">(Privacy, Mandato, Sospensioni firmate)</span>
                    </h3>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="doc-description-gestione"
                        placeholder="Descrizione..."
                        value={docDescriptionGestione}
                        onChange={(e) => setDocDescriptionGestione(e.target.value)}
                        className="w-40 h-8 text-sm"
                      />
                      <label>
                        <input type="file" accept="image/*,.pdf" onChange={(e) => handleUploadDocument(e, 'gestione')} className="hidden" disabled={uploading} />
                        <Button size="sm" variant="outline" asChild disabled={uploading} data-testid="upload-gestione-btn">
                          <span><Upload className="w-4 h-4 mr-1" />{uploading ? '...' : 'Carica'}</span>
                        </Button>
                      </label>
                    </div>
                  </div>

                  {(() => {
                    const gestione = (selectedClient.documents || []).filter(d => d.category === 'gestione');
                    return gestione.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {gestione.map((doc) => (
                          <div key={doc.id} className="relative group border rounded-lg overflow-hidden bg-slate-50 cursor-pointer" onClick={() => handleOpenPreview(doc)}>
                            <div className="aspect-[3/4] flex items-center justify-center bg-slate-100">
                              {documentPreviews[doc.id]?.data ? (
                                <img src={documentPreviews[doc.id].data} alt={doc.filename} className="w-full h-full object-cover" data-testid={`doc-thumb-${doc.id}`} />
                              ) : documentPreviews[doc.id] === undefined ? (
                                <div className="flex flex-col items-center gap-2">
                                  <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
                                  <span className="text-xs text-slate-400">Caricamento...</span>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center gap-2">
                                  <File className="w-10 h-10 text-slate-400" />
                                  <span className="text-xs text-slate-400">{(doc.file_type || '').includes('pdf') ? 'PDF' : 'File'}</span>
                                </div>
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleOpenPreview(doc); }} data-testid={`doc-preview-${doc.id}`}><Eye className="w-4 h-4" /></Button>
                              <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDirectDownload(doc); }} data-testid={`doc-download-${doc.id}`}><Download className="w-4 h-4" /></Button>
                              <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }} data-testid={`doc-delete-${doc.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                            <div className="p-2 border-t bg-white">
                              <p className="text-xs font-medium truncate">{doc.description || doc.filename}</p>
                              <p className="text-xs text-slate-500">{doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('it-IT') : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-4 text-sm border border-dashed rounded-lg">Nessun documento di gestione</p>
                    );
                  })()}
                </div>

              </div>

              {/* Action Buttons */}
              <div className="border-t pt-4 flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleEditFromDetails}
                  data-testid="details-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Modifica
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteFromDetails}
                  data-testid="details-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
              </div>

              {/* Delete Confirmation - Step 1: Conferma Base */}
              {deleteClientConfirm && deleteClientConfirm.clientId === selectedClient?.id && deleteStep === 1 && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-900 font-semibold mb-2">
                        Sei sicuro di voler eliminare questo cliente?
                      </p>
                      <p className="text-sm text-amber-800 mb-3">
                        L'operazione è <strong>irreversibile</strong>. Tutti i dati associati al cliente verranno eliminati definitivamente.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setDeleteClientConfirm(null); setDeleteStep(1); }}
                        >
                          Annulla
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDelete(deleteClientConfirm.clientId)}
                          data-testid="proceed-delete-client"
                        >
                          Procedi
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Confirmation - Step 2: Controllo Vincoli */}
              {deleteClientConfirm && deleteClientConfirm.clientId === selectedClient?.id && deleteStep === 2 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Ban className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-red-900 font-bold mb-2">
                        ⚠️ ATTENZIONE: Eliminazione a Cascata
                      </p>
                      <p className="text-sm text-red-800 mb-2">
                        Questo cliente ha i seguenti dati registrati che verranno <strong>eliminati definitivamente</strong>:
                      </p>
                      <div className="bg-red-100 rounded p-3 mb-3">
                        <ul className="text-sm text-red-800 space-y-1">
                          {deleteClientConfirm.linked_items?.policies > 0 && (
                            <li className="flex items-center gap-2">
                              <Shield className="w-4 h-4" />
                              <strong>{deleteClientConfirm.linked_items.policies}</strong> polizze (attive e storiche)
                            </li>
                          )}
                          {deleteClientConfirm.linked_items?.claims > 0 && (
                            <li className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              <strong>{deleteClientConfirm.linked_items.claims}</strong> sinistri (aperti e chiusi)
                            </li>
                          )}
                          {deleteClientConfirm.linked_items?.assets > 0 && (
                            <li className="flex items-center gap-2">
                              <Car className="w-4 h-4" />
                              <strong>{deleteClientConfirm.linked_items.assets}</strong> beni (veicoli, case, attività)
                            </li>
                          )}
                          {deleteClientConfirm.linked_items?.quotes > 0 && (
                            <li className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <strong>{deleteClientConfirm.linked_items.quotes}</strong> preventivi
                            </li>
                          )}
                          {deleteClientConfirm.linked_items?.telecom_contracts > 0 && (
                            <li className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <strong>{deleteClientConfirm.linked_items.telecom_contracts}</strong> contratti telefonia
                            </li>
                          )}
                          {deleteClientConfirm.linked_items?.consultings > 0 && (
                            <li className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              <strong>{deleteClientConfirm.linked_items.consultings}</strong> consulenze
                            </li>
                          )}
                          {deleteClientConfirm.documents_count > 0 && (
                            <li className="flex items-center gap-2">
                              <File className="w-4 h-4" />
                              <strong>{deleteClientConfirm.documents_count}</strong> documenti (PDF, immagini)
                            </li>
                          )}
                        </ul>
                      </div>
                      <p className="text-xs text-red-700 mb-3 italic">
                        Procedendo, l'anagrafica cliente, tutti i beni, polizze, sinistri e documenti allegati saranno rimossi permanentemente dal sistema.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setDeleteClientConfirm(null); setDeleteStep(1); }}
                        >
                          Annulla
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="bg-red-700 hover:bg-red-800"
                          onClick={() => handleDelete(deleteClientConfirm.clientId, true)}
                          data-testid="confirm-delete-client"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Elimina Definitivamente
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Asset Dialog */}
      <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
        <DialogContent 
          className="sm:max-w-lg"
          onInteractOutside={(e) => {
            // Prevent modal close when clicking on Google Maps autocomplete dropdown
            const target = e.target;
            if (target.closest('.pac-container') || target.classList?.contains('pac-item') || target.closest('.pac-item')) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Also block pointer down events on pac-container
            const target = e.target;
            if (target.closest('.pac-container') || target.classList?.contains('pac-item') || target.closest('.pac-item')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getAssetIcon(assetType)}
              {editingAsset ? 'Modifica' : 'Nuovo'} {assetType === 'casa' ? 'Casa' : assetType === 'attivita' ? 'Attività' : assetType === 'salute' ? 'Salute' : 'Veicolo'}
            </DialogTitle>
            <DialogDescription>Inserisci i dati del bene</DialogDescription>
          </DialogHeader>
          
          <div 
            className="space-y-4 mt-4"
            onKeyDown={(e) => {
              // Permetti nuova riga nei textarea
              if (e.target.tagName === 'TEXTAREA') return;
              // INVIO per salvare
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSaveAsset();
              }
            }}
          >
            {/* Casa Form */}
            {assetType === 'casa' && (
              <>
                {/* Indirizzo e Numero Civico - Layout identico a Nuovo Cliente */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2 col-span-3">
                    <Label>Indirizzo <span className="text-xs text-blue-600">(ricerca Google Maps)</span></Label>
                    {GOOGLE_MAPS_API_KEY ? (
                      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <AddressAutocomplete
                          placeholder="es. Via Roma, Piazza Duomo..."
                          value={assetFormData.indirizzo || ''}
                          defaultValue={assetFormData.indirizzo || ''}
                          onPlaceSelect={(addressData) => {
                            console.log('Casa onPlaceSelect called with:', addressData);
                            
                            // FORCE state update - separate indirizzo and numero_civico
                            setAssetFormData(prev => {
                              const newState = {
                                ...prev,
                                indirizzo: addressData.address || prev.indirizzo,
                                numero_civico: addressData.address_number || prev.numero_civico,
                                city: addressData.city || prev.city,
                                provincia: addressData.city_provincia || prev.provincia,
                                cap: addressData.postal_code || prev.cap,
                                latitude: addressData.latitude || prev.latitude,
                                longitude: addressData.longitude || prev.longitude
                              };
                              console.log('Casa state updated to:', newState);
                              return newState;
                            });
                            
                            // Show toast confirmation
                            const fullAddress = addressData.address_number 
                              ? `${addressData.address}, ${addressData.address_number}`
                              : addressData.address;
                            if (addressData.city || addressData.city_provincia) {
                              toast.success('Indirizzo compilato automaticamente', {
                                description: `${fullAddress}${addressData.city ? `, ${addressData.city}` : ''}${addressData.city_provincia ? ` (${addressData.city_provincia})` : ''}`
                              });
                            }
                          }}
                        />
                      </APIProvider>
                    ) : (
                      <Input 
                        value={assetFormData.indirizzo || ''} 
                        onChange={(e) => setAssetFormData({...assetFormData, indirizzo: e.target.value})}
                        placeholder="es. Via Roma"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>N. Civico</Label>
                    <Input 
                      value={assetFormData.numero_civico || ''} 
                      onChange={(e) => setAssetFormData({...assetFormData, numero_civico: e.target.value})}
                      placeholder="es. 10/A"
                      data-testid="asset-casa-numero-civico"
                    />
                  </div>
                </div>
                
                {/* Città, Provincia, CAP */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input 
                      value={assetFormData.city || ''} 
                      onChange={(e) => handleAssetCityChange(e.target.value)}
                      placeholder="es. Milano"
                      data-testid="asset-casa-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Select value={assetFormData.provincia || ''} onValueChange={handleAssetProvinciaChange}>
                      <SelectTrigger data-testid="asset-casa-provincia"><SelectValue placeholder="Prov." /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {PROVINCE.map((prov) => (
                          <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>CAP {assetCapLoading && <span className="text-slate-400 text-xs">(caricamento...)</span>}</Label>
                    {assetAvailableCaps.length > 1 ? (
                      <Select value={assetFormData.cap || ''} onValueChange={(v) => setAssetFormData({...assetFormData, cap: v})}>
                        <SelectTrigger data-testid="asset-casa-cap-select"><SelectValue placeholder="Seleziona CAP" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {assetAvailableCaps.map((cap) => (
                            <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        value={assetFormData.cap || ''} 
                        onChange={(e) => setAssetFormData({...assetFormData, cap: e.target.value})} 
                        placeholder={assetAvailableCaps.length === 1 ? assetAvailableCaps[0] : "CAP"}
                        data-testid="asset-casa-cap"
                      />
                    )}
                    {assetAvailableCaps.length > 1 && (
                      <p className="text-xs text-amber-600">Seleziona il CAP ({assetAvailableCaps.length} disponibili)</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dati Catastali</Label>
                  <Input 
                    value={assetFormData.dati_catastali || ''} 
                    onChange={(e) => setAssetFormData({...assetFormData, dati_catastali: e.target.value})}
                    placeholder="Foglio, particella, subalterno"
                    data-testid="asset-casa-catastali"
                  />
                </div>
              </>
            )}

            {/* Attività Form */}
            {assetType === 'attivita' && (
              <>
                <div className="space-y-2">
                  <Label>Ragione Sociale</Label>
                  <Input 
                    value={assetFormData.ragione_sociale || ''} 
                    onChange={(e) => setAssetFormData({...assetFormData, ragione_sociale: e.target.value})}
                    placeholder="Nome società o ditta"
                    data-testid="asset-attivita-ragione-sociale"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partita IVA</Label>
                  <Input 
                    value={assetFormData.partita_iva || ''} 
                    onChange={(e) => setAssetFormData({...assetFormData, partita_iva: e.target.value})}
                    placeholder="12345678901"
                    data-testid="asset-attivita-piva"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo di Attività</Label>
                  <Input 
                    value={assetFormData.tipo_attivita || ''} 
                    onChange={(e) => setAssetFormData({...assetFormData, tipo_attivita: e.target.value})}
                    placeholder="es. Commercio, Servizi, Artigianato"
                    data-testid="asset-attivita-tipo"
                  />
                </div>
                
                {/* Indirizzo e Numero Civico - Layout identico a Nuovo Cliente */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2 col-span-3">
                    <Label>Indirizzo <span className="text-xs text-blue-600">(ricerca Google Maps)</span></Label>
                    {GOOGLE_MAPS_API_KEY ? (
                      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <AddressAutocomplete
                          placeholder="es. Via Roma, Piazza Duomo..."
                          value={assetFormData.indirizzo || ''}
                          defaultValue={assetFormData.indirizzo || ''}
                          onPlaceSelect={(addressData) => {
                            console.log('Attività onPlaceSelect called with:', addressData);
                            
                            // FORCE state update - separate indirizzo and numero_civico
                            setAssetFormData(prev => {
                              const newState = {
                                ...prev,
                                indirizzo: addressData.address || prev.indirizzo,
                                numero_civico: addressData.address_number || prev.numero_civico,
                                city: addressData.city || prev.city,
                                provincia: addressData.city_provincia || prev.provincia,
                                cap: addressData.postal_code || prev.cap,
                                latitude: addressData.latitude || prev.latitude,
                                longitude: addressData.longitude || prev.longitude
                              };
                              console.log('Attività state updated to:', newState);
                              return newState;
                            });
                            
                            // Show toast confirmation
                            const fullAddress = addressData.address_number 
                              ? `${addressData.address}, ${addressData.address_number}`
                              : addressData.address;
                            if (addressData.city || addressData.city_provincia) {
                              toast.success('Indirizzo compilato automaticamente', {
                                description: `${fullAddress}${addressData.city ? `, ${addressData.city}` : ''}${addressData.city_provincia ? ` (${addressData.city_provincia})` : ''}`
                              });
                            }
                          }}
                        />
                      </APIProvider>
                    ) : (
                      <Input 
                        value={assetFormData.indirizzo || ''} 
                        onChange={(e) => setAssetFormData({...assetFormData, indirizzo: e.target.value})}
                        placeholder="es. Via Roma"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>N. Civico</Label>
                    <Input 
                      value={assetFormData.numero_civico || ''} 
                      onChange={(e) => setAssetFormData({...assetFormData, numero_civico: e.target.value})}
                      placeholder="es. 10/A"
                      data-testid="asset-attivita-numero-civico"
                    />
                  </div>
                </div>
                
                {/* Città, Provincia, CAP */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input 
                      value={assetFormData.city || ''} 
                      onChange={(e) => handleAssetCityChange(e.target.value)}
                      placeholder="es. Milano"
                      data-testid="asset-attivita-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Select value={assetFormData.provincia || ''} onValueChange={handleAssetProvinciaChange}>
                      <SelectTrigger data-testid="asset-attivita-provincia"><SelectValue placeholder="Prov." /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {PROVINCE.map((prov) => (
                          <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>CAP {assetCapLoading && <span className="text-slate-400 text-xs">(caricamento...)</span>}</Label>
                    {assetAvailableCaps.length > 1 ? (
                      <Select value={assetFormData.cap || ''} onValueChange={(v) => setAssetFormData({...assetFormData, cap: v})}>
                        <SelectTrigger data-testid="asset-attivita-cap-select"><SelectValue placeholder="Seleziona CAP" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {assetAvailableCaps.map((cap) => (
                            <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        value={assetFormData.cap || ''} 
                        onChange={(e) => setAssetFormData({...assetFormData, cap: e.target.value})} 
                        placeholder={assetAvailableCaps.length === 1 ? assetAvailableCaps[0] : "CAP"}
                        data-testid="asset-attivita-cap"
                      />
                    )}
                    {assetAvailableCaps.length > 1 && (
                      <p className="text-xs text-amber-600">Seleziona il CAP ({assetAvailableCaps.length} disponibili)</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Auto Form */}
            {assetType === 'auto' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Targa</Label>
                    <Input 
                      value={assetFormData.targa || ''} 
                      onChange={(e) => setAssetFormData({...assetFormData, targa: e.target.value.toUpperCase()})}
                      placeholder="AA000AA"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alimentazione</Label>
                    <Select value={assetFormData.alimentazione || ''} onValueChange={(v) => setAssetFormData({...assetFormData, alimentazione: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {ALIMENTAZIONI.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label>Marca</Label>
                    <Input 
                      value={assetFormData.marca || ''} 
                      onChange={(e) => handleMarcaInputChange(e.target.value.toUpperCase())}
                      onFocus={() => { if (assetFormData.marca) searchMarche(assetFormData.marca); }}
                      onBlur={() => setTimeout(() => setShowMarcaDropdown(false), 200)}
                      placeholder="Inizia a digitare..."
                      autoComplete="off"
                      data-testid="marca-input"
                    />
                    {showMarcaDropdown && marcaSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {marcaSuggestions.map((marca) => (
                          <div
                            key={marca}
                            className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm font-medium"
                            onMouseDown={() => selectMarca(marca)}
                          >
                            {marca}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Modello</Label>
                    <Input 
                      value={assetFormData.modello || ''} 
                      onChange={(e) => handleModelloInputChange(e.target.value.toUpperCase())}
                      onFocus={() => { if (assetFormData.marca) searchModelli(assetFormData.marca, assetFormData.modello || ''); }}
                      onBlur={() => setTimeout(() => setShowModelloDropdown(false), 200)}
                      placeholder={assetFormData.marca ? "Seleziona modello..." : "Prima seleziona marca"}
                      disabled={!assetFormData.marca}
                      autoComplete="off"
                      data-testid="modello-input"
                    />
                    {showModelloDropdown && modelloSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {modelloSuggestions.map((modello) => (
                          <div
                            key={modello}
                            className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm"
                            onMouseDown={() => selectModello(modello)}
                          >
                            {modello}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Immatricolazione</Label>
                    <DateInput 
                      value={assetFormData.data_immatricolazione || ''} 
                      onChange={(val) => setAssetFormData({...assetFormData, data_immatricolazione: val})}
                      placeholder="gg/mm/aaaa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Acquisto</Label>
                    <DateInput 
                      value={assetFormData.data_acquisto || ''} 
                      onChange={(val) => setAssetFormData({...assetFormData, data_acquisto: val})}
                      placeholder="gg/mm/aaaa"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Scadenza Copertura Attuale (Non Clienti)</Label>
                  <div className="flex gap-2">
                    <DateInput 
                      className="flex-1"
                      value={assetFormData.scadenza_copertura || ''} 
                      onChange={(val) => setAssetFormData({...assetFormData, scadenza_copertura: val})}
                      data-testid="scadenza-copertura"
                      placeholder="gg/mm/aaaa"
                    />
                    {assetFormData.scadenza_copertura && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon"
                        className="shrink-0 text-slate-400 hover:text-red-500 hover:border-red-300"
                        onClick={() => setAssetFormData({...assetFormData, scadenza_copertura: ''})}
                        title="Azzera data"
                        data-testid="clear-scadenza-copertura"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Data scadenza assicurazione attuale (per gestione rinnovi)</p>
                </div>
                <div className="space-y-2">
                  <Label>Stato Veicolo</Label>
                  <Select 
                    value={assetFormData.vehicle_status || 'in_possesso'} 
                    onValueChange={(v) => setAssetFormData({...assetFormData, vehicle_status: v})}
                  >
                    <SelectTrigger data-testid="vehicle-status-select">
                      <SelectValue placeholder="Seleziona stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_possesso">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          In Possesso
                        </span>
                      </SelectItem>
                      <SelectItem value="venduto">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                          Venduto
                        </span>
                      </SelectItem>
                      <SelectItem value="rottamato">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          Rottamato
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Salute Form */}
            {assetType === 'salute' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Persone Assicurate</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newPersona = { ...emptyPersonaAssicurata };
                      setAssetFormData({
                        ...assetFormData,
                        persone_assicurate: [...(assetFormData.persone_assicurate || []), newPersona]
                      });
                    }}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Aggiungi Persona
                  </Button>
                </div>
                
                {/* Pulsante copia dati contraente */}
                {selectedClient && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      const contraente = {
                        nome: selectedClient.first_name || '',
                        cognome: selectedClient.last_name || '',
                        data_nascita: selectedClient.birth_date || '',
                        sesso: selectedClient.gender || '',
                        codice_fiscale: selectedClient.fiscal_code || '',
                        luogo_nascita: selectedClient.birth_abroad ? selectedClient.birth_country : selectedClient.birth_comune,
                        provincia_nascita: selectedClient.birth_provincia || ''
                      };
                      const currentPersone = assetFormData.persone_assicurate || [];
                      // Verifica se il contraente è già presente
                      const alreadyExists = currentPersone.some(p => 
                        p.codice_fiscale && p.codice_fiscale === contraente.codice_fiscale
                      );
                      if (alreadyExists) {
                        toast.info('Il contraente è già presente nella lista');
                        return;
                      }
                      setAssetFormData({
                        ...assetFormData,
                        persone_assicurate: [...currentPersone, contraente]
                      });
                      toast.success('Dati contraente copiati');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copia dati Contraente
                  </Button>
                )}

                {/* Lista persone */}
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {(assetFormData.persone_assicurate || []).map((persona, index) => (
                    <div key={index} className="border rounded-lg p-3 bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Persona {index + 1}</span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={() => {
                            const updated = [...assetFormData.persone_assicurate];
                            updated.splice(index, 1);
                            setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Cognome</Label>
                          <Input
                            value={persona.cognome}
                            onChange={(e) => {
                              const updated = [...assetFormData.persone_assicurate];
                              updated[index] = { ...updated[index], cognome: e.target.value };
                              setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                            }}
                            placeholder="Cognome"
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Nome</Label>
                          <Input
                            value={persona.nome}
                            onChange={(e) => {
                              const updated = [...assetFormData.persone_assicurate];
                              updated[index] = { ...updated[index], nome: e.target.value };
                              setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                            }}
                            placeholder="Nome"
                            className="h-8"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Data Nascita</Label>
                          <DateInput
                            value={persona.data_nascita}
                            onChange={(val) => {
                              const updated = [...assetFormData.persone_assicurate];
                              updated[index] = { ...updated[index], data_nascita: val };
                              setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                            }}
                            className="h-8"
                            placeholder="gg/mm/aaaa"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sesso</Label>
                          <Select
                            value={persona.sesso}
                            onValueChange={(value) => {
                              const updated = [...assetFormData.persone_assicurate];
                              updated[index] = { ...updated[index], sesso: value };
                              setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                            }}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Sesso" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="M">Maschio</SelectItem>
                              <SelectItem value="F">Femmina</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 relative">
                          <Label className="text-xs">Luogo Nascita</Label>
                          <Input
                            value={persona.luogo_nascita}
                            onChange={(e) => {
                              const updated = [...assetFormData.persone_assicurate];
                              updated[index] = { ...updated[index], luogo_nascita: e.target.value };
                              setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                              // Cerca comuni nel database locale ISTAT (NO Google Maps)
                              searchComuniItaliani(e.target.value, index);
                            }}
                            onFocus={() => {
                              // Mostra dropdown se ci sono già suggerimenti
                              if (comuniSuggestions[index]?.length > 0) {
                                setShowComuniDropdown(prev => ({ ...prev, [index]: true }));
                              }
                            }}
                            onBlur={() => {
                              // Ritarda la chiusura per permettere il click sul dropdown
                              setTimeout(() => {
                                setShowComuniDropdown(prev => ({ ...prev, [index]: false }));
                              }, 200);
                            }}
                            onKeyDown={(e) => {
                              const suggestions = comuniSuggestions[index] || [];
                              if (!showComuniDropdown[index] || suggestions.length === 0) return;
                              
                              // Gestione navigazione tastiera
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const currentIdx = suggestions.findIndex(s => s.nome === persona.luogo_nascita);
                                const nextIdx = currentIdx < suggestions.length - 1 ? currentIdx + 1 : 0;
                                const updated = [...assetFormData.persone_assicurate];
                                updated[index] = { ...updated[index], luogo_nascita: suggestions[nextIdx].nome };
                                setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                const currentIdx = suggestions.findIndex(s => s.nome === persona.luogo_nascita);
                                const prevIdx = currentIdx > 0 ? currentIdx - 1 : suggestions.length - 1;
                                const updated = [...assetFormData.persone_assicurate];
                                updated[index] = { ...updated[index], luogo_nascita: suggestions[prevIdx].nome };
                                setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                              } else if (e.key === 'Enter') {
                                e.preventDefault();
                                const selectedSuggestion = suggestions.find(s => s.nome === persona.luogo_nascita);
                                if (selectedSuggestion) {
                                  selectComune(selectedSuggestion, index);
                                } else if (suggestions.length > 0) {
                                  selectComune(suggestions[0], index);
                                }
                              } else if (e.key === 'Escape') {
                                setShowComuniDropdown(prev => ({ ...prev, [index]: false }));
                              }
                            }}
                            placeholder="es. Roma"
                            className="h-8"
                            autoComplete="off"
                          />
                          {/* Dropdown suggerimenti comuni (database locale ISTAT) */}
                          {showComuniDropdown[index] && comuniSuggestions[index]?.length > 0 && (
                            <div 
                              className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
                              data-testid={`comuni-dropdown-${index}`}
                            >
                              {comuniSuggestions[index].map((comune, i) => (
                                <div
                                  key={`${comune.nome}-${comune.provincia}-${i}`}
                                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex justify-between items-center text-sm ${
                                    persona.luogo_nascita === comune.nome ? 'bg-blue-100' : ''
                                  }`}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Previene blur prima del click
                                    selectComune(comune, index);
                                  }}
                                  data-testid={`comune-option-${index}-${i}`}
                                >
                                  <span className="font-medium">{comune.nome}</span>
                                  <span className="text-slate-500 text-xs">({comune.provincia})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Codice Fiscale</Label>
                          <div className="flex gap-1">
                            <Input
                              value={persona.codice_fiscale}
                              onChange={(e) => {
                                const updated = [...assetFormData.persone_assicurate];
                                updated[index] = { ...updated[index], codice_fiscale: e.target.value.toUpperCase() };
                                setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                              }}
                              placeholder="RSSMRA80A01H501Z"
                              className="h-8 font-mono"
                              maxLength={16}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-xs"
                              onClick={async () => {
                                if (!persona.cognome || !persona.nome || !persona.data_nascita || !persona.sesso || !persona.luogo_nascita) {
                                  toast.error('Compila tutti i campi per calcolare il CF');
                                  return;
                                }
                                try {
                                  const response = await axios.post(`${API}/utils/calcola-cf`, {
                                    cognome: persona.cognome,
                                    nome: persona.nome,
                                    data_nascita: persona.data_nascita,
                                    sesso: persona.sesso,
                                    luogo_nascita: persona.luogo_nascita,
                                    provincia_nascita: persona.provincia_nascita || ''
                                  });
                                  if (response.data.valid) {
                                    const updated = [...assetFormData.persone_assicurate];
                                    updated[index] = { ...updated[index], codice_fiscale: response.data.codice_fiscale };
                                    setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                                    toast.success('Codice fiscale calcolato');
                                  } else {
                                    toast.error(response.data.error || 'Errore nel calcolo');
                                  }
                                } catch (err) {
                                  toast.error('Errore nel calcolo del CF');
                                }
                              }}
                            >
                              Calcola
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Prov. Nascita</Label>
                          <Input
                            value={persona.provincia_nascita}
                            onChange={(e) => {
                              const updated = [...assetFormData.persone_assicurate];
                              updated[index] = { ...updated[index], provincia_nascita: e.target.value.toUpperCase() };
                              setAssetFormData({ ...assetFormData, persone_assicurate: updated });
                            }}
                            placeholder="RM"
                            className="h-8"
                            maxLength={2}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!assetFormData.persone_assicurate || assetFormData.persone_assicurate.length === 0) && (
                    <div className="text-center py-6 text-slate-500 border-2 border-dashed rounded-lg">
                      <ShieldPlus className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm">Nessuna persona aggiunta</p>
                      <p className="text-xs">Clicca "Aggiungi Persona" o "Copia dati Contraente"</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Note - comune a tutti */}
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea 
                value={assetFormData.notes || ''} 
                onChange={(e) => setAssetFormData({...assetFormData, notes: e.target.value})}
                rows={2}
                placeholder="Note aggiuntive..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setAssetDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleSaveAsset} className="bg-slate-900 hover:bg-slate-800">
                {editingAsset ? 'Salva' : 'Aggiungi'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset Details Dialog with Quotes and Policies */}
      <Dialog open={assetDetailsOpen} onOpenChange={(open) => { if (!previewDoc) setAssetDetailsOpen(open); }}>
        <DialogContent 
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (previewDoc) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (previewDoc) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAsset && getAssetIcon(selectedAsset.asset_type)}
              Dettagli {selectedAsset?.asset_type === 'casa' ? 'Casa' : selectedAsset?.asset_type === 'attivita' ? 'Attività' : selectedAsset?.asset_type === 'salute' ? 'Salute' : 'Veicolo'}
            </DialogTitle>
            <DialogDescription>
              {selectedAsset && getAssetLabel(selectedAsset)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAsset && (
            <div className="space-y-6 mt-4">
              {/* Asset Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Informazioni Bene</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedAsset.asset_type === 'casa' && (
                    <>
                      <div><span className="text-slate-500">Indirizzo:</span> <span className="font-medium">{selectedAsset.indirizzo || '-'}</span></div>
                      <div><span className="text-slate-500">Città:</span> <span className="font-medium">{selectedAsset.city || '-'} ({selectedAsset.provincia || '-'})</span></div>
                      <div><span className="text-slate-500">CAP:</span> <span className="font-medium">{selectedAsset.cap || '-'}</span></div>
                      <div><span className="text-slate-500">Dati Catastali:</span> <span className="font-medium">{selectedAsset.dati_catastali || '-'}</span></div>
                      {/* Note in sola lettura */}
                      {selectedAsset.notes && (
                        <div className="col-span-2 mt-2">
                          <span className="text-slate-500">Note:</span>
                          <div className="mt-1 p-2 bg-slate-50 rounded-md text-sm text-slate-700 whitespace-pre-wrap">
                            {selectedAsset.notes}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {selectedAsset.asset_type === 'attivita' && (
                    <>
                      <div><span className="text-slate-500">Ragione Sociale:</span> <span className="font-medium">{selectedAsset.ragione_sociale || '-'}</span></div>
                      <div><span className="text-slate-500">P.IVA:</span> <span className="font-medium">{selectedAsset.partita_iva || '-'}</span></div>
                      <div><span className="text-slate-500">Tipo Attività:</span> <span className="font-medium">{selectedAsset.tipo_attivita || '-'}</span></div>
                      <div><span className="text-slate-500">Indirizzo:</span> <span className="font-medium">{selectedAsset.indirizzo || '-'}, {selectedAsset.city} ({selectedAsset.provincia}) {selectedAsset.cap}</span></div>
                      {/* Note in sola lettura */}
                      {selectedAsset.notes && (
                        <div className="col-span-2 mt-2">
                          <span className="text-slate-500">Note:</span>
                          <div className="mt-1 p-2 bg-slate-50 rounded-md text-sm text-slate-700 whitespace-pre-wrap">
                            {selectedAsset.notes}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {selectedAsset.asset_type === 'auto' && (
                    <>
                      <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                        <div className="text-slate-500 text-xs">Denominazione:</div>
                        <div className="font-bold text-amber-800">
                          {getClientNameForAssetLabel(selectedClient, true)} {selectedAsset.targa?.toUpperCase() || ''}
                        </div>
                      </div>
                      <div><span className="text-slate-500">Marca/Modello:</span> <span className="font-medium">{selectedAsset.marca} {selectedAsset.modello}</span></div>
                      <div><span className="text-slate-500">Targa:</span> <span className="font-medium">{selectedAsset.targa || '-'}</span></div>
                      <div><span className="text-slate-500">Alimentazione:</span> <span className="font-medium">{selectedAsset.alimentazione || '-'}</span></div>
                      <div><span className="text-slate-500">Immatricolazione:</span> <span className="font-medium">{selectedAsset.data_immatricolazione ? new Date(selectedAsset.data_immatricolazione).toLocaleDateString('it-IT') : '-'}</span></div>
                      <div><span className="text-slate-500">Data Acquisto:</span> <span className="font-medium">{selectedAsset.data_acquisto ? new Date(selectedAsset.data_acquisto).toLocaleDateString('it-IT') : '-'}</span></div>
                      <div><span className="text-slate-500">Scadenza Copertura (Non Clienti):</span> <span className={`font-medium ${selectedAsset.scadenza_copertura ? 'text-orange-600' : ''}`}>{selectedAsset.scadenza_copertura ? new Date(selectedAsset.scadenza_copertura).toLocaleDateString('it-IT') : '-'}</span></div>
                      {/* Note in sola lettura */}
                      {selectedAsset.notes && (
                        <div className="col-span-2 mt-2">
                          <span className="text-slate-500">Note:</span>
                          <div className="mt-1 p-2 bg-slate-50 rounded-md text-sm text-slate-700 whitespace-pre-wrap">
                            {selectedAsset.notes}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {selectedAsset.asset_type === 'salute' && (
                    <>
                      <div className="col-span-2">
                        <span className="text-slate-500">Persone Assicurate:</span>
                        <span className="font-medium ml-2">{selectedAsset.persone_assicurate?.length || 0}</span>
                      </div>
                      {selectedAsset.persone_assicurate && selectedAsset.persone_assicurate.length > 0 && (
                        <div className="col-span-2 mt-2 space-y-2">
                          {selectedAsset.persone_assicurate.map((persona, idx) => (
                            <div key={idx} className="bg-white border border-rose-100 rounded-lg p-2 text-xs">
                              <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-rose-500" />
                                <span className="font-semibold text-rose-800">{persona.cognome} {persona.nome}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-1 text-slate-600 ml-6">
                                <div>Data Nascita: <span className="text-slate-900">{persona.data_nascita || '-'}</span></div>
                                <div>Sesso: <span className="text-slate-900">{persona.sesso === 'M' ? 'Maschio' : persona.sesso === 'F' ? 'Femmina' : '-'}</span></div>
                                <div>Luogo: <span className="text-slate-900">{persona.luogo_nascita || '-'} {persona.provincia_nascita && `(${persona.provincia_nascita})`}</span></div>
                                <div>CF: <span className="text-slate-900 font-mono">{persona.codice_fiscale || '-'}</span></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Note in sola lettura */}
                      {selectedAsset.notes && (
                        <div className="col-span-2 mt-2">
                          <span className="text-slate-500">Note:</span>
                          <div className="mt-1 p-2 bg-slate-50 rounded-md text-sm text-slate-700 whitespace-pre-wrap">
                            {selectedAsset.notes}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Policies Section */}
              {assetPolicies.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-semibold text-slate-900">Polizze ({assetPolicies.length})</h4>
                  </div>
                  <div className="space-y-3">
                    {assetPolicies.map((policy) => {
                      const isSuspended = policy.status === 'sospesa';
                      const isInterrupted = policy.status === 'interrotta';
                      const isArchived = policy.status === 'archiviata';
                      const textClass = isArchived ? 'text-slate-600' : isInterrupted ? 'text-red-900' : isSuspended ? 'text-amber-900' : 'text-green-900';
                      const badgeClass = isArchived ? 'bg-slate-200 text-slate-700' : isInterrupted ? 'bg-red-100 text-red-800' : isSuspended ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
                      
                      // Colori sfondo e bordo per ogni stato
                      const cardBgColor = isArchived ? '#f1f5f9' : isInterrupted ? '#fef2f2' : isSuspended ? '#fffbeb' : '#F0F9F4';
                      const cardBorderColor = isArchived ? '#cbd5e1' : isInterrupted ? '#fecaca' : isSuspended ? '#fde68a' : '#86efac';
                      
                      // Determina il label dello stato
                      const getStatusLabel = () => {
                        if (isArchived) return 'Archiviata';
                        if (isInterrupted) return 'Interrotta';
                        if (isSuspended) return 'Sospesa';
                        return 'Attiva';
                      };
                      
                      return (
                        <div 
                          key={policy.id} 
                          className="rounded-lg"
                          style={{
                            backgroundColor: cardBgColor,
                            border: `2px solid ${cardBorderColor}`
                          }}
                        >
                          {/* Contenitore principale con sfondo solido */}
                          <div 
                            className="p-4 rounded-lg"
                            style={{ backgroundColor: cardBgColor }}
                          >
                            <div 
                              className="flex justify-between items-start gap-3"
                              style={{ backgroundColor: cardBgColor }}
                            >
                              {/* Colonna sinistra - Info polizza */}
                              <div 
                                className="flex-1 min-w-0"
                                style={{ backgroundColor: cardBgColor }}
                              >
                                <div className="flex items-center gap-2">
                                  <p 
                                    className={`font-medium ${textClass}`}
                                    style={{ backgroundColor: cardBgColor }}
                                  >
                                    {policy.policy_number ? policy.policy_number : (
                                      <span className="text-amber-600 italic text-sm">N. Polizza da inserire</span>
                                    )}
                                  </p>
                                  {/* Indicatore Stato Pagamento - SOLO VISIVO (non cliccabile) */}
                                  {policy.payment_status === 'da_incassare' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                                      <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>
                                      Da Incassare
                                    </span>
                                  ) : policy.payment_status === 'incassato' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                      <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
                                      Incassata
                                    </span>
                                  ) : null}
                                  {/* INDICATORE SINISTRI - cliccabile */}
                                  {policyClaims[policy.id]?.claims_count > 0 && (
                                    <span 
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300 cursor-pointer hover:bg-orange-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/claims?policy_id=${policy.id}`);
                                      }}
                                      title={`Ultimo sinistro: ${policyClaims[policy.id]?.last_claim_date ? new Date(policyClaims[policy.id].last_claim_date).toLocaleDateString('it-IT') : 'N/D'}`}
                                      data-testid={`claims-indicator-${policy.id}`}
                                    >
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Sinistri: {policyClaims[policy.id].claims_count}
                                    </span>
                                  )}
                                </div>
                                <p 
                                  className={`text-sm ${textClass} opacity-80`}
                                  style={{ backgroundColor: cardBgColor }}
                                >
                                  {policy.company} {policy.agent && `• ${policy.agent}`} • €{policy.premium_amount?.toLocaleString('it-IT')}{policy.payment_frequency === 'semestrale' ? '/semestrale' : '/anno'}
                                </p>
                                <p 
                                  className={`text-xs ${textClass} opacity-70 mt-1`}
                                  style={{ backgroundColor: cardBgColor }}
                                >
                                  {policy.payment_date && `Incasso: ${new Date(policy.payment_date).toLocaleDateString('it-IT')} • `}
                                  Decorrenza: {new Date(policy.start_date).toLocaleDateString('it-IT')}
                                </p>
                                {/* SCADENZA - Enfatizzata */}
                                <p 
                                  className={`mt-2 ${
                                    Math.ceil((new Date(policy.end_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 30 
                                      ? 'text-red-600' 
                                      : Math.ceil((new Date(policy.end_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 60 
                                        ? 'text-amber-600' 
                                        : textClass
                                  }`}
                                  style={{ 
                                    backgroundColor: cardBgColor,
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.01em'
                                  }}
                                >
                                  📅 Scadenza: {new Date(policy.end_date).toLocaleDateString('it-IT')}
                                  <span style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: '8px', opacity: 0.8 }}>
                                    ({Math.max(0, Math.ceil((new Date(policy.end_date) - new Date()) / (1000 * 60 * 60 * 24)))} giorni)
                                  </span>
                                </p>
                                {isSuspended && policy.suspension_end && (
                                  <p 
                                    className="text-xs text-amber-700 mt-1 font-medium"
                                    style={{ backgroundColor: cardBgColor }}
                                  >
                                    <PauseCircle className="w-3 h-3 inline mr-1" />
                                    Sospensione fino al: {new Date(policy.suspension_end).toLocaleDateString('it-IT')}
                                  </p>
                                )}
                                {isInterrupted && policy.interruption_date && (
                                  <p 
                                    className="text-xs text-red-700 mt-1 font-medium"
                                    style={{ backgroundColor: cardBgColor }}
                                  >
                                    <Ban className="w-3 h-3 inline mr-1" />
                                    Interrotta il: {new Date(policy.interruption_date).toLocaleDateString('it-IT')}
                                    {policy.refund_amount && ` • Rimborso: €${policy.refund_amount.toLocaleString('it-IT')}`}
                                  </p>
                                )}
                                <p 
                                  className={`text-xs ${textClass} opacity-60 mt-0.5`}
                                  style={{ backgroundColor: cardBgColor }}
                                >
                                  {selectedAsset?.asset_type === 'auto' && policy.driving_type && (
                                    <>{policy.driving_type === 'libera' ? 'Guida Libera' : 'Guida Esperta'} • </>
                                  )}
                                  {policy.coverage_details}
                                </p>
                              </div>
                              {/* Colonna destra - Badge e pulsanti */}
                              <div 
                                className="flex flex-col items-end gap-2 flex-shrink-0"
                                style={{ backgroundColor: cardBgColor }}
                              >
                                <Badge className={badgeClass}>
                                  {getStatusLabel()}
                                </Badge>
                                {/* Badge stato pagamento - cliccabile per aprire Centro Pagamenti */}
                                <div 
                                  className="flex gap-1 flex-wrap justify-end"
                                  style={{ backgroundColor: cardBgColor }}
                                >
                                  {isSuspended && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-100 bg-white"
                                      onClick={() => openReactivateDialog(policy)}
                                      data-testid={`reactivate-policy-${policy.id}`}
                                    >
                                      <PlayCircle className="w-4 h-4 mr-1" /> Riattiva
                                    </Button>
                                  )}
                                  {!isInterrupted && !isArchived && (
                                    <>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-slate-600 border-slate-300 hover:bg-slate-100 bg-white"
                                        onClick={() => openEditPolicyDialog(policy)}
                                        data-testid={`edit-policy-${policy.id}`}
                                      >
                                        <Pencil className="w-4 h-4 mr-1" /> Modifica
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-blue-600 border-blue-300 hover:bg-blue-100 bg-white"
                                        onClick={() => openPolicyManagement(policy)}
                                        data-testid={`manage-policy-${policy.id}`}
                                      >
                                        <Settings className="w-4 h-4 mr-1" /> Gestione
                                      </Button>
                                    </>
                                  )}
                                  {isArchived && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-red-600 border-red-300 hover:bg-red-100 bg-white"
                                      onClick={() => openPolicyManagement(policy)}
                                      data-testid={`manage-archived-policy-${policy.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" /> Elimina
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quotes Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-slate-900">Preventivi ({assetQuotes.length})</h4>
                  </div>
                  <Button size="sm" onClick={openAddQuoteDialog} data-testid="add-quote-btn">
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi Preventivo
                  </Button>
                </div>

                {assetQuotes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Nessun preventivo per questo bene</p>
                ) : (
                  <div className="space-y-2">
                    {[...assetQuotes]
                      .sort((a, b) => {
                        // Ordina per data creazione decrescente (più recente in alto)
                        const dateA = new Date(a.created_at || 0);
                        const dateB = new Date(b.created_at || 0);
                        return dateB - dateA;
                      })
                      .map((quote) => (
                      <div key={quote.id} className={`border rounded-lg p-3 ${
                        quote.status === 'convertito' ? 'bg-slate-100 border-slate-300' : 
                        quote.status === 'attesa_conferma' ? 'bg-yellow-50 border-yellow-300' : 
                        quote.status === 'proposto' ? 'bg-sky-50 border-sky-300' : 
                        'bg-white border-slate-200'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            {quote.options?.map((opt, idx) => (
                              <div key={idx} className="mb-2">
                                <p className="font-medium text-slate-900">
                                  {opt.company} {opt.agent && `• ${opt.agent}`}
                                </p>
                                <p className="text-lg font-bold text-blue-600">
                                  €{opt.premium?.toLocaleString('it-IT')}{quote.payment_frequency === 'semestrale' ? '/semestrale' : '/anno'}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {selectedAsset?.asset_type === 'auto' && (quote.driving_type === 'libera' ? 'Guida Libera' : 'Guida Esperta')}
                                </p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {opt.coverage_types?.map((cov, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{cov}</Badge>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {quote.notes && (
                              <p className="text-xs text-slate-500 mt-2">{quote.notes}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {/* Stato e Data Creazione */}
                            <div className="flex items-center gap-2">
                              {quote.status === 'convertito' ? (
                                <Badge className="bg-emerald-100 text-emerald-800">Convertito</Badge>
                              ) : (
                                <Select
                                  value={quote.status}
                                  onValueChange={async (newStatus) => {
                                    try {
                                      await axios.patch(`${API}/quotes/${quote.id}/status`, { status: newStatus });
                                      const quotesRes = await axios.get(`${API}/quotes?asset_id=${selectedAsset.id}`);
                                      setAssetQuotes(quotesRes.data);
                                      toast.success('Stato aggiornato');
                                    } catch (error) {
                                      toast.error('Errore nell\'aggiornamento dello stato');
                                    }
                                  }}
                                >
                                  <SelectTrigger className={`h-7 text-xs font-medium border-0 px-2.5 rounded-full w-auto min-w-0 ${
                                    quote.status === 'attesa_conferma' ? 'bg-yellow-100 text-yellow-800' : 
                                    quote.status === 'proposto' ? 'bg-sky-100 text-sky-700' : 
                                    'bg-slate-100 text-slate-800'
                                  }`} data-testid={`quote-status-${quote.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="bozza">Bozza</SelectItem>
                                    <SelectItem value="proposto">Proposto</SelectItem>
                                    <SelectItem value="attesa_conferma">Attesa Conferma</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                            {/* Data Creazione */}
                            <div className="text-xs text-slate-500 text-right">
                              <span className="font-medium">Creato:</span>{' '}
                              {quote.created_at ? new Date(quote.created_at).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </div>
                            {/* Data Ultima Modifica (se presente) */}
                            {quote.updated_at && (
                              <div className="text-xs text-slate-400 text-right">
                                <span className="font-medium">Modificato:</span>{' '}
                                {new Date(quote.updated_at).toLocaleString('it-IT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                onClick={() => openEditQuoteDialog(quote)}
                                data-testid={`edit-quote-${quote.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" /> Modifica
                              </Button>
                              {quote.status === 'convertito' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                  onClick={() => handleRevertConversion(quote.id)}
                                  data-testid={`revert-quote-${quote.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" /> Annulla Emissione
                                </Button>
                              )}
                              {quote.status !== 'convertito' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => openConversionDialog(quote)}
                                    data-testid={`convert-quote-${quote.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" /> Emetti Polizza
                                  </Button>
                                  <Button 
                                    size="icon" 
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteQuote(quote.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Storico Sospensioni Section (solo per auto) */}
              {selectedAsset?.asset_type === 'auto' && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-600" />
                      <h4 className="font-semibold text-slate-900">Storico Sospensioni ({assetSuspensions.length})</h4>
                    </div>
                    <Button size="sm" variant="outline" onClick={openAddSuspensionDialog} data-testid="add-suspension-btn">
                      <Plus className="w-4 h-4 mr-1" /> Registra Sospensione
                    </Button>
                  </div>

                  {assetSuspensions.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Nessuna sospensione registrata</p>
                  ) : (
                    <div className="space-y-2">
                      {assetSuspensions.map((suspension) => (
                        <div key={suspension.id} className="border border-orange-200 bg-orange-50 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {suspension.auto_generated && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">Auto</Badge>
                                )}
                                {!suspension.data_fine && (
                                  <Badge className="bg-orange-100 text-orange-700 text-xs">In corso</Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-500">Data Richiesta:</span>
                                  <p className="font-medium">{suspension.data_richiesta ? new Date(suspension.data_richiesta).toLocaleDateString('it-IT') : '-'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Data Decorrenza:</span>
                                  <p className="font-medium">{suspension.data_decorrenza ? new Date(suspension.data_decorrenza).toLocaleDateString('it-IT') : '-'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Data Fine:</span>
                                  <p className={`font-medium ${suspension.data_fine ? 'text-green-600' : 'text-orange-600'}`}>
                                    {suspension.data_fine ? new Date(suspension.data_fine).toLocaleDateString('it-IT') : '-'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditSuspensionDialog(suspension)} title="Modifica">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteSuspension(suspension.id)} className="text-red-600" title="Elimina">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Footer with Action Buttons - Under Storico Sospensioni */}
              <div className="border-t pt-4 mt-4 flex justify-between items-center">
                {/* Elimina - Bottom Left */}
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  onClick={async () => {
                    const policiesCount = assetPolicies.length;
                    const quotesCount = assetQuotes.length;
                    let confirmMsg = 'Sei sicuro di voler eliminare questo Bene?';
                    if (policiesCount > 0 || quotesCount > 0) {
                      const parts = [];
                      if (policiesCount > 0) parts.push(`${policiesCount} polizz${policiesCount === 1 ? 'a' : 'e'}`);
                      if (quotesCount > 0) parts.push(`${quotesCount} preventiv${quotesCount === 1 ? 'o' : 'i'}`);
                      confirmMsg = `Sei sicuro di voler eliminare questo Bene?\n\nVerranno eliminate anche: ${parts.join(' e ')}.`;
                    }
                    if (window.confirm(confirmMsg)) {
                      try {
                        await axios.delete(`${API}/assets/${selectedAsset.id}`);
                        toast.success('Bene eliminato con successo');
                        setAssetDetailsOpen(false);
                        setSelectedAsset(null);
                        fetchClientAssets(selectedClient.id);
                      } catch (error) {
                        toast.error('Errore durante l\'eliminazione del bene');
                      }
                    }
                  }}
                  data-testid="delete-asset-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>

                {/* Modifica e Chiudi - Bottom Right */}
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                    onClick={() => {
                      setAssetDetailsOpen(false);
                      openEditAssetDialog(selectedAsset);
                    }}
                    data-testid="edit-asset-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifica
                  </Button>
                  <Button variant="outline" onClick={() => setAssetDetailsOpen(false)}>
                    Chiudi
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={(open) => { setQuoteDialogOpen(open); if (!open) setEditingQuote(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {editingQuote ? 'Modifica Preventivo' : 'Nuovo Preventivo'}
            </DialogTitle>
            <DialogDescription>
              {editingQuote 
                ? `Modifica i dati del preventivo per ${selectedAsset && getAssetLabel(selectedAsset)}`
                : `Inserisci i dati del preventivo per ${selectedAsset && getAssetLabel(selectedAsset)}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div 
            className="space-y-4 mt-4"
            onKeyDown={(e) => {
              // Permetti nuova riga nei textarea
              if (e.target.tagName === 'TEXTAREA') return;
              // INVIO per salvare
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSaveQuote();
              }
            }}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Agenzia</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs h-6 px-2 text-blue-600"
                    onClick={() => setAgenciesDialogOpen(true)}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Gestione
                  </Button>
                </div>
                <Select 
                  value={quoteFormData.agent || "__none__"} 
                  onValueChange={(v) => setQuoteFormData({...quoteFormData, agent: v === "__none__" ? "" : v})}
                >
                  <SelectTrigger data-testid="quote-agent">
                    <SelectValue placeholder="Seleziona agenzia" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="__none__">Nessuna</SelectItem>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.name}>{agency.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Compagnia *</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs h-6 px-2 text-blue-600"
                    onClick={() => setCompaniesDialogOpen(true)}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Gestione
                  </Button>
                </div>
                <Select value={quoteFormData.company} onValueChange={(v) => setQuoteFormData({...quoteFormData, company: v})}>
                  <SelectTrigger data-testid="quote-company">
                    <SelectValue placeholder="Seleziona compagnia" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {insuranceCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.name}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Premio (€) *</Label>
                <Input 
                  type="number"
                  value={quoteFormData.premium} 
                  onChange={(e) => setQuoteFormData({...quoteFormData, premium: e.target.value})}
                  placeholder="0.00"
                  data-testid="quote-premium"
                />
              </div>
              <div className="space-y-2">
                <Label>Frazionamento Pagamento *</Label>
                <Select 
                  value={quoteFormData.payment_frequency} 
                  onValueChange={(v) => setQuoteFormData({...quoteFormData, payment_frequency: v})}
                >
                  <SelectTrigger data-testid="quote-payment-frequency">
                    <SelectValue placeholder="Seleziona frazionamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annuale">Annuale</SelectItem>
                    <SelectItem value="semestrale">Semestrale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Tipo Guida - Solo per Veicoli */}
              {selectedAsset?.asset_type === 'auto' && (
                <div className="space-y-2">
                  <Label>Tipo Guida *</Label>
                  <Select 
                    value={quoteFormData.driving_type} 
                    onValueChange={(v) => setQuoteFormData({...quoteFormData, driving_type: v})}
                  >
                    <SelectTrigger data-testid="quote-driving-type">
                      <SelectValue placeholder="Seleziona tipo guida" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="esperta">Guida Esperta</SelectItem>
                      <SelectItem value="libera">Guida Libera</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Coperture</Label>
              {selectedAsset && (
                <>
                  {/* Check if asset type has grouped coverages */}
                  {COVERAGE_TYPES[selectedAsset.asset_type]?.groups ? (
                    <div className="space-y-4">
                      {COVERAGE_TYPES[selectedAsset.asset_type].groups.map((group, idx) => (
                        <div key={group.name}>
                          {idx > 0 && <div className="border-t my-3"></div>}
                          <p className="text-sm font-medium text-slate-700 mb-2">{group.name}</p>
                          <div className="flex flex-wrap gap-2">
                            {group.coverages.map((cov) => (
                              <Badge
                                key={cov}
                                variant={quoteFormData.coverage_types.includes(cov) ? 'default' : 'outline'}
                                className={`cursor-pointer transition-colors ${
                                  quoteFormData.coverage_types.includes(cov) 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'hover:bg-slate-100'
                                }`}
                                onClick={() => toggleCoverage(cov)}
                                data-testid={`coverage-${cov}`}
                              >
                                {cov}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Simple array of coverages (Attività, Veicolo) */
                    <div className="flex flex-wrap gap-2">
                      {COVERAGE_TYPES[selectedAsset.asset_type]?.map((cov) => (
                        <Badge
                          key={cov}
                          variant={quoteFormData.coverage_types.includes(cov) ? 'default' : 'outline'}
                          className={`cursor-pointer transition-colors ${
                            quoteFormData.coverage_types.includes(cov) 
                              ? 'bg-blue-600 text-white hover:bg-blue-700' 
                              : 'hover:bg-slate-100'
                          }`}
                          onClick={() => toggleCoverage(cov)}
                          data-testid={`coverage-${cov}`}
                        >
                          {cov}
                        </Badge>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea 
                value={quoteFormData.notes} 
                onChange={(e) => setQuoteFormData({...quoteFormData, notes: e.target.value})}
                rows={2}
                placeholder="Note aggiuntive..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => { setQuoteDialogOpen(false); setEditingQuote(null); }}>Annulla</Button>
              <Button 
                onClick={handleSaveQuote} 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!quoteFormData.company || !quoteFormData.premium}
                data-testid="save-quote-btn"
              >
                {editingQuote ? 'Salva Modifiche' : 'Salva Preventivo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Suspension Dialog */}
      <Dialog open={suspensionDialogOpen} onOpenChange={(open) => { setSuspensionDialogOpen(open); if (!open) setEditingSuspension(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              {editingSuspension ? 'Modifica Sospensione' : 'Registra Sospensione'}
            </DialogTitle>
            <DialogDescription>
              {editingSuspension 
                ? 'Modifica i dati della sospensione'
                : 'Inserisci i dati della sospensione per questo veicolo'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Data Richiesta *</Label>
              <DateInput 
                value={suspensionFormData.data_richiesta} 
                onChange={(val) => setSuspensionFormData({...suspensionFormData, data_richiesta: val})}
                data-testid="suspension-data-richiesta"
                placeholder="gg/mm/aaaa"
              />
              <p className="text-xs text-slate-500">Data in cui è stata inoltrata la pratica</p>
            </div>
            <div className="space-y-2">
              <Label>Data Decorrenza *</Label>
              <DateInput 
                value={suspensionFormData.data_decorrenza} 
                onChange={(val) => setSuspensionFormData({...suspensionFormData, data_decorrenza: val})}
                data-testid="suspension-data-decorrenza"
                placeholder="gg/mm/aaaa"
              />
              <p className="text-xs text-slate-500">Inizio effettivo del periodo di fermo</p>
            </div>
            <div className="space-y-2">
              <Label>Data Fine Sospensione</Label>
              <DateInput 
                value={suspensionFormData.data_fine} 
                onChange={(val) => setSuspensionFormData({...suspensionFormData, data_fine: val})}
                data-testid="suspension-data-fine"
                placeholder="gg/mm/aaaa"
              />
              <p className="text-xs text-slate-500">Data di riattivazione della polizza (lasciare vuoto se ancora in corso)</p>
            </div>

            {/* Calcolo giorni di sospensione */}
            {suspensionFormData.data_decorrenza && suspensionFormData.data_fine && (
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-center">
                <p className="text-sm text-orange-700">
                  <span className="font-semibold">Totale: {calculateDaysBetween(suspensionFormData.data_decorrenza, suspensionFormData.data_fine)} giorni</span> di sospensione
                </p>
              </div>
            )}
            {suspensionFormData.data_decorrenza && !suspensionFormData.data_fine && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-sm text-amber-700">
                  <span className="font-semibold">In corso da {calculateDaysBetween(suspensionFormData.data_decorrenza, new Date().toISOString().split('T')[0])} giorni</span>
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setSuspensionDialogOpen(false)}>Annulla</Button>
              <Button 
                onClick={handleSaveSuspension}
                className="bg-orange-600 hover:bg-orange-700"
                disabled={!suspensionFormData.data_richiesta || !suspensionFormData.data_decorrenza}
                data-testid="save-suspension-btn"
              >
                {editingSuspension ? 'Salva Modifiche' : 'Registra'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Riattivazione Polizza con Aggiornamento Scadenza */}
      <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-emerald-600" />
              Riattiva Polizza
            </DialogTitle>
            <DialogDescription>
              Aggiorna la data di scadenza e riattiva la polizza dopo il periodo di sospensione.
            </DialogDescription>
          </DialogHeader>
          
          {reactivatingPolicy && (
            <div className="space-y-4 mt-4">
              {/* Info polizza */}
              <div className="bg-orange-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-500">Polizza:</span> <span className="font-medium">{reactivatingPolicy.policy_number || <span className="text-amber-600 italic">N. Polizza da inserire</span>}</span></p>
                <p><span className="text-slate-500">Compagnia:</span> <span className="font-medium">{reactivatingPolicy.company}</span></p>
                {reactivatingPolicy.suspension_start && (
                  <p>
                    <span className="text-slate-500">Sospesa dal:</span>{' '}
                    <span className="font-medium">{new Date(reactivatingPolicy.suspension_start).toLocaleDateString('it-IT')}</span>
                    {' '}
                    <span className="text-orange-600 font-semibold">
                      ({calculateDaysBetween(reactivatingPolicy.suspension_start, new Date().toISOString().split('T')[0])} giorni)
                    </span>
                  </p>
                )}
                <p><span className="text-slate-500">Scadenza attuale:</span> <span className="font-medium">{new Date(reactivatingPolicy.end_date).toLocaleDateString('it-IT')}</span></p>
              </div>

              {/* Calcolo giorni */}
              {reactivatingPolicy.suspension_start && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-sm text-blue-700">
                    I <span className="font-bold">{calculateDaysBetween(reactivatingPolicy.suspension_start, new Date().toISOString().split('T')[0])} giorni</span> di sospensione verranno aggiunti alla scadenza originale
                  </p>
                </div>
              )}
              
              {/* Campo nuova data */}
              <div className="space-y-2">
                <Label>Nuova Data Scadenza *</Label>
                <DateInput
                  value={newEndDateForReactivation}
                  onChange={(val) => setNewEndDateForReactivation(val)}
                  data-testid="new-end-date-reactivate"
                  placeholder="gg/mm/aaaa"
                />
                <p className="text-xs text-slate-500">
                  La data è stata calcolata automaticamente sommando i giorni di fermo. Puoi modificarla se necessario.
                </p>
              </div>
              
              {/* Pulsanti */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setReactivateDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleReactivatePolicy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!newEndDateForReactivation}
                  data-testid="confirm-reactivate"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salva e Riattiva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Agent Sources Management Dialog */}
      <Dialog open={agentSourcesDialogOpen} onOpenChange={setAgentSourcesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Gestione Agente / Fonte</DialogTitle>
            <DialogDescription>Aggiungi o rimuovi fonti cliente dal sistema</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Add new source */}
            <div className="flex gap-2">
              <Input 
                placeholder="Nome nuova fonte..."
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSource()}
                data-testid="new-source-input"
              />
              <Button onClick={handleAddSource} className="bg-slate-900 hover:bg-slate-800" data-testid="add-source-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Sources list */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {agents.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Nessuna fonte configurata</p>
              ) : (
                agents.map((source) => (
                  <div key={source.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{source.name}</p>
                      {source.clients_count > 0 && (
                        <p className="text-xs text-slate-500">{source.clients_count} clienti associati</p>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteSource(source)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-source-${source.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            {/* Delete confirmation */}
            {deleteConfirmSource && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 mb-2">
                  <strong>Attenzione:</strong> "{deleteConfirmSource.name}" è associata a {deleteConfirmSource.clients_count} clienti. 
                  Eliminando questa fonte, il campo verrà svuotato per tutti i clienti associati.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setDeleteConfirmSource(null)}>Annulla</Button>
                  <Button 
                    size="sm" 
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => handleDeleteSource(deleteConfirmSource, true)}
                    data-testid="confirm-delete-source"
                  >
                    Conferma Eliminazione
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agencies Management Dialog */}
      <Dialog open={agenciesDialogOpen} onOpenChange={setAgenciesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Gestione Agenzie</DialogTitle>
            <DialogDescription>Aggiungi o rimuovi agenzie assicurative dal sistema</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Add new agency */}
            <div className="flex gap-2">
              <Input 
                placeholder="Nome nuova agenzia..."
                value={newAgencyName}
                onChange={(e) => setNewAgencyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAgency()}
                data-testid="new-agency-input"
              />
              <Button onClick={handleAddAgency} className="bg-slate-900 hover:bg-slate-800" data-testid="add-agency-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Agencies list */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {agencies.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Nessuna agenzia configurata</p>
              ) : (
                agencies.map((agency) => (
                  <div key={agency.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                    {editingAgencyId === agency.id ? (
                      // Edit mode
                      <div className="flex-1 flex items-center gap-2">
                        <Input
                          value={editingAgencyName}
                          onChange={(e) => setEditingAgencyName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditAgency(agency.id, editingAgencyName);
                            if (e.key === 'Escape') { setEditingAgencyId(null); setEditingAgencyName(''); }
                          }}
                          className="flex-1"
                          autoFocus
                        />
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleEditAgency(agency.id, editingAgencyName)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => { setEditingAgencyId(null); setEditingAgencyName(''); }}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      // View mode
                      <>
                        <div>
                          <p className="font-medium text-slate-900">{agency.name}</p>
                          {agency.quotes_count > 0 && (
                            <p className="text-xs text-slate-500">{agency.quotes_count} preventivi associati</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => { setEditingAgencyId(agency.id); setEditingAgencyName(agency.name); }}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            title="Modifica agenzia"
                            data-testid={`edit-agency-${agency.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteAgency(agency.id)}
                            className={`${agency.quotes_count > 0 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                            disabled={agency.quotes_count > 0}
                            title={agency.quotes_count > 0 ? 'Non eliminabile: associata a preventivi' : 'Elimina agenzia'}
                            data-testid={`delete-agency-${agency.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Companies Management Dialog */}
      <Dialog open={companiesDialogOpen} onOpenChange={setCompaniesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Gestione Compagnie Assicurative</DialogTitle>
            <DialogDescription>Aggiungi o rimuovi compagnie assicurative dal sistema</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Add new company */}
            <div className="flex gap-2">
              <Input 
                placeholder="Nome nuova compagnia..."
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCompany()}
                data-testid="new-company-input"
              />
              <Button onClick={handleAddCompany} className="bg-slate-900 hover:bg-slate-800" data-testid="add-company-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Companies list */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {insuranceCompanies.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Nessuna compagnia configurata</p>
              ) : (
                insuranceCompanies.map((company) => (
                  <div key={company.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                    {editingCompanyId === company.id ? (
                      // Edit mode
                      <div className="flex-1 flex items-center gap-2">
                        <Input
                          value={editingCompanyName}
                          onChange={(e) => setEditingCompanyName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditCompany(company.id, editingCompanyName);
                            if (e.key === 'Escape') { setEditingCompanyId(null); setEditingCompanyName(''); }
                          }}
                          className="flex-1"
                          autoFocus
                        />
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleEditCompany(company.id, editingCompanyName)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => { setEditingCompanyId(null); setEditingCompanyName(''); }}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      // View mode
                      <>
                        <div>
                          <p className="font-medium text-slate-900">{company.name}</p>
                          {company.quotes_count > 0 && (
                            <p className="text-xs text-slate-500">{company.quotes_count} preventivi associati</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => { setEditingCompanyId(company.id); setEditingCompanyName(company.name); }}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            title="Modifica compagnia"
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteCompany(company.id)}
                            className={`${company.quotes_count > 0 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                            disabled={company.quotes_count > 0}
                            title={company.quotes_count > 0 ? 'Non eliminabile: associata a preventivi' : 'Elimina compagnia'}
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy Edit Dialog */}
      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Polizza</DialogTitle>
            <DialogDescription>Modifica i dati della polizza</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Numero Polizza</Label>
                <Input 
                  value={policyFormData.policy_number} 
                  onChange={(e) => setPolicyFormData({...policyFormData, policy_number: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select value={policyFormData.status} onValueChange={(v) => setPolicyFormData({...policyFormData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attiva">Attiva</SelectItem>
                    <SelectItem value="sospesa">Sospesa</SelectItem>
                    <SelectItem value="scaduta">Scaduta</SelectItem>
                    <SelectItem value="disdetta">Disdetta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Compagnia</Label>
                <Input 
                  value={policyFormData.company} 
                  onChange={(e) => setPolicyFormData({...policyFormData, company: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label>Agenzia</Label>
                <Input 
                  value={policyFormData.agent} 
                  onChange={(e) => setPolicyFormData({...policyFormData, agent: e.target.value})} 
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Data Incasso</Label>
                <DateInput 
                  value={policyFormData.payment_date} 
                  onChange={(val) => setPolicyFormData({...policyFormData, payment_date: val})} 
                  placeholder="gg/mm/aaaa"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Decorrenza</Label>
                <DateInput 
                  value={policyFormData.start_date} 
                  onChange={(val) => setPolicyFormData({...policyFormData, start_date: val})} 
                  placeholder="gg/mm/aaaa"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Scadenza</Label>
                <DateInput 
                  value={policyFormData.end_date} 
                  onChange={(val) => setPolicyFormData({...policyFormData, end_date: val})} 
                  placeholder="gg/mm/aaaa"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Premio Annuo (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={policyFormData.premium_amount} 
                  onChange={(e) => setPolicyFormData({...policyFormData, premium_amount: e.target.value})} 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Coperture</Label>
              <Input 
                value={policyFormData.coverage_details} 
                onChange={(e) => setPolicyFormData({...policyFormData, coverage_details: e.target.value})}
                placeholder="es. RC Auto, Furto, Incendio" 
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setPolicyDialogOpen(false)}>Annulla</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSavePolicy}>
                Salva Modifiche
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy Management Dialog */}
      <Dialog open={policyManagementOpen} onOpenChange={(open) => { if (!previewDoc) setPolicyManagementOpen(open); }}>
        <DialogContent 
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (previewDoc) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (previewDoc) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Gestione Polizza
            </DialogTitle>
            <DialogDescription>
              {managingPolicy?.policy_number || <span className="text-amber-600 italic">N. Polizza da inserire</span>} - {managingPolicy?.company}
            </DialogDescription>
          </DialogHeader>

          {!managementAction ? (
            <div className="space-y-3 mt-4">
              {/* Sinistro - disponibile per tutti */}
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 px-4"
                onClick={() => setManagementAction('sinistro')}
                data-testid="policy-management-claim"
              >
                <AlertTriangle className="w-5 h-5 mr-3 text-red-500" />
                <div className="text-left">
                  <p className="font-medium">Sinistro</p>
                  <p className="text-xs text-slate-500">Inserisci un nuovo sinistro per questa polizza</p>
                </div>
              </Button>

              {/* Sospensione - solo per veicoli */}
              {selectedAsset?.asset_type === 'auto' && managingPolicy?.status !== 'sospesa' && (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3 px-4"
                  onClick={() => setManagementAction('sospensione')}
                  data-testid="policy-management-suspend"
                >
                  <PauseCircle className="w-5 h-5 mr-3 text-amber-500" />
                  <div className="text-left">
                    <p className="font-medium">Sospensione</p>
                    <p className="text-xs text-slate-500">Sospendi temporaneamente la polizza</p>
                  </div>
                </Button>
              )}

              {/* Sostituzione - solo per veicoli */}
              {selectedAsset?.asset_type === 'auto' && (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3 px-4"
                  onClick={() => setManagementAction('sostituzione')}
                  data-testid="policy-management-replace"
                >
                  <Replace className="w-5 h-5 mr-3 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">Sostituzione</p>
                    <p className="text-xs text-slate-500">Cambio targa o cambio veicolo</p>
                  </div>
                </Button>
              )}

              {/* Interruzione - disponibile per tutti */}
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 px-4"
                onClick={() => setManagementAction('interruzione')}
                data-testid="policy-management-interrupt"
              >
                <Ban className="w-5 h-5 mr-3 text-red-600" />
                <div className="text-left">
                  <p className="font-medium">Interruzione</p>
                  <p className="text-xs text-slate-500">Interrompi definitivamente la polizza</p>
                </div>
              </Button>

              {/* Separatore */}
              <div className="border-t border-slate-200 my-3 pt-3">
                <p className="text-xs text-slate-400 mb-3 uppercase tracking-wider">Ciclo di vita</p>
              </div>

              {/* Rinnova - disponibile per polizze attive */}
              {managingPolicy?.status === 'attiva' && (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3 px-4"
                  onClick={handleRenewPolicy}
                  data-testid="policy-management-renew"
                >
                  <RefreshCw className="w-5 h-5 mr-3 text-emerald-500" />
                  <div className="text-left">
                    <p className="font-medium">Rinnova Polizza</p>
                    <p className="text-xs text-slate-500">Crea nuova polizza con date aggiornate</p>
                  </div>
                </Button>
              )}

              {/* Archivia - disponibile per polizze attive */}
              {managingPolicy?.status === 'attiva' && (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3 px-4"
                  onClick={handleArchivePolicy}
                  data-testid="policy-management-archive"
                >
                  <Archive className="w-5 h-5 mr-3 text-slate-500" />
                  <div className="text-left">
                    <p className="font-medium">Archivia Polizza</p>
                    <p className="text-xs text-slate-500">Sposta nello storico senza eliminare</p>
                  </div>
                </Button>
              )}

              {/* Elimina - disponibile per tutti */}
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 px-4 border-red-200 hover:bg-red-50"
                onClick={handleDeletePolicy}
                data-testid="policy-management-delete"
              >
                <Trash2 className="w-5 h-5 mr-3 text-red-500" />
                <div className="text-left">
                  <p className="font-medium text-red-600">Elimina Polizza</p>
                  <p className="text-xs text-slate-500">Cancella definitivamente il record</p>
                </div>
              </Button>

              {/* Separatore Economia */}
              <div className="border-t border-slate-200 my-3 pt-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Economia e Contabilità</p>
                  {/* Status pagamento */}
                  <span className={`text-xs px-2 py-1 rounded-full ${getPolicyPaymentStatus().color}`}>
                    {getPolicyPaymentStatus().icon} {getPolicyPaymentStatus().label}
                  </span>
                </div>
              </div>

              {/* Pulsante unico Centro Pagamenti e Provvigioni */}
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 px-4 border-emerald-200 hover:bg-emerald-50"
                onClick={openPaymentsModal}
                data-testid="policy-payments-center"
              >
                <CreditCard className="w-5 h-5 mr-3 text-emerald-600" />
                <div className="text-left">
                  <p className="font-medium">Pagamenti e Provvigioni</p>
                  <p className="text-xs text-slate-500">Gestisci incassi, pagamenti e provvigioni</p>
                </div>
              </Button>
            </div>
          ) : managementAction === 'sinistro' ? (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><span className="text-slate-500">Cliente:</span> <span className="font-medium">{managingPolicy?.client_name}</span></p>
                <p><span className="text-slate-500">Polizza:</span> <span className="font-medium">{managingPolicy?.policy_number || <span className="text-amber-600 italic">N. Polizza da inserire</span>}</span></p>
                <p><span className="text-slate-500">Compagnia:</span> <span className="font-medium">{managingPolicy?.company}</span></p>
                <p><span className="text-slate-500">Agenzia:</span> <span className="font-medium">{managingPolicy?.agent || '-'}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Data Sinistro *</Label>
                <DateInput
                  value={claimFormData.claim_date}
                  onChange={(val) => setClaimFormData({...claimFormData, claim_date: val})}
                  data-testid="claim-date-input"
                  placeholder="gg/mm/aaaa"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione <span className="text-slate-400 text-xs font-normal">(facoltativa)</span></Label>
                <Textarea
                  value={claimFormData.description}
                  onChange={(e) => setClaimFormData({...claimFormData, description: e.target.value})}
                  rows={3}
                  placeholder="Descrivi il sinistro (opzionale)..."
                  data-testid="claim-description-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setManagementAction(null)}>Indietro</Button>
                <Button onClick={handleCreateClaimFromPolicy} data-testid="create-claim-btn">
                  <AlertTriangle className="w-4 h-4 mr-2" /> Crea Sinistro
                </Button>
              </div>
            </div>
          ) : managementAction === 'sospensione' ? (
            <div className="space-y-4 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-800">Sospensione Polizza</p>
                <p className="text-amber-700 text-xs mt-1">La polizza passerà in stato "Sospesa". 7 giorni prima della fine sospensione verrà generata un'attività nel calendario.</p>
              </div>
              <div className="space-y-2">
                <Label>Data Decorrenza Sospensione *</Label>
                <DateInput
                  value={suspendFormData.suspension_start}
                  onChange={(val) => setSuspendFormData({...suspendFormData, suspension_start: val})}
                  data-testid="suspend-start-input"
                  placeholder="gg/mm/aaaa"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fine Sospensione *</Label>
                <DateInput
                  value={suspendFormData.suspension_end}
                  onChange={(val) => setSuspendFormData({...suspendFormData, suspension_end: val})}
                  data-testid="suspend-end-input"
                  placeholder="gg/mm/aaaa"
                />
              </div>
              <div className="flex justify-between pt-2">
                <Button 
                  variant="outline"
                  className="bg-slate-700 text-white hover:bg-slate-800 border-slate-700"
                  disabled={!suspendFormData.suspension_start || !suspendFormData.suspension_end}
                  data-testid="generate-suspension-pdf-btn"
                  onClick={async () => {
                    if (!managingPolicy) return;
                    if (!suspendFormData.suspension_start || !suspendFormData.suspension_end) {
                      toast.error('Compila le date di sospensione prima di generare il PDF');
                      return;
                    }
                    try {
                      const response = await axios.post(`${API}/policies/${managingPolicy.id}/generate-suspension-pdf`, {
                        suspension_start: suspendFormData.suspension_start,
                        suspension_end: suspendFormData.suspension_end
                      }, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      const targa = selectedAsset?.plate || 'POLIZZA';
                      link.setAttribute('download', `Sospensione_${targa}_${new Date().toLocaleDateString('it-IT').replace(/\//g, '')}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('PDF Sospensione generato');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Errore generazione PDF');
                    }
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" /> GENERA PDF
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setManagementAction(null)}>Indietro</Button>
                  <Button onClick={handleSuspendPolicy} className="bg-amber-600 hover:bg-amber-700" data-testid="suspend-policy-btn">
                    <PauseCircle className="w-4 h-4 mr-2" /> Sospendi Polizza
                  </Button>
                </div>
              </div>
            </div>
          ) : managementAction === 'sostituzione' ? (
            <div className="space-y-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800">Sostituzione Veicolo</p>
                <p className="text-blue-700 text-xs mt-1">Lo storico delle modifiche verrà mantenuto. La polizza non verrà eliminata.</p>
              </div>
              <div className="space-y-2">
                <Label>Tipo Sostituzione</Label>
                <Select
                  value={replaceFormData.replace_type}
                  onValueChange={(value) => setReplaceFormData({...replaceFormData, replace_type: value, new_targa: '', new_asset_id: ''})}
                >
                  <SelectTrigger data-testid="replace-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="targa">Cambio Targa</SelectItem>
                    <SelectItem value="veicolo">Cambio Veicolo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {replaceFormData.replace_type === 'targa' ? (
                <div className="space-y-2">
                  <Label>Nuova Targa *</Label>
                  <Input
                    value={replaceFormData.new_targa}
                    onChange={(e) => setReplaceFormData({...replaceFormData, new_targa: e.target.value.toUpperCase()})}
                    placeholder="AA000AA"
                    data-testid="new-targa-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Nuovo Veicolo *</Label>
                  <Select
                    value={replaceFormData.new_asset_id}
                    onValueChange={(value) => setReplaceFormData({...replaceFormData, new_asset_id: value})}
                  >
                    <SelectTrigger data-testid="new-vehicle-select">
                      <SelectValue placeholder="Seleziona veicolo" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableVehicles.length === 0 ? (
                        <SelectItem value="none" disabled>Nessun altro veicolo disponibile</SelectItem>
                      ) : (
                        availableVehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.marca} {v.modello} - {v.targa}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setManagementAction(null)}>Indietro</Button>
                <Button onClick={handleReplaceVehicle} data-testid="replace-vehicle-btn">
                  <Replace className="w-4 h-4 mr-2" /> Conferma Sostituzione
                </Button>
              </div>
            </div>
          ) : managementAction === 'interruzione' ? (
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-red-800">Interruzione Polizza</p>
                <p className="text-red-700 text-xs mt-1">La polizza verrà interrotta definitivamente e non potrà essere riattivata.</p>
              </div>
              <div className="space-y-2">
                <Label>Data Interruzione *</Label>
                <DateInput
                  value={interruptFormData.interruption_date}
                  onChange={(val) => setInterruptFormData({...interruptFormData, interruption_date: val})}
                  data-testid="interrupt-date-input"
                  placeholder="gg/mm/aaaa"
                />
              </div>
              <div className="space-y-2">
                <Label>Importo Rimborso (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={interruptFormData.refund_amount}
                  onChange={(e) => setInterruptFormData({...interruptFormData, refund_amount: e.target.value})}
                  placeholder="Opzionale"
                  data-testid="refund-amount-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setManagementAction(null)}>Indietro</Button>
                <Button onClick={handleInterruptPolicy} className="bg-red-600 hover:bg-red-700" data-testid="interrupt-policy-btn">
                  <Ban className="w-4 h-4 mr-2" /> Interrompi Polizza
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Centro Pagamenti e Provvigioni Modal */}
      <Dialog open={paymentsModalOpen} onOpenChange={(open) => { setPaymentsModalOpen(open); if (!open) { resetAccountingForm(); setPaymentsModalTab('unificato'); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Centro Pagamenti e Provvigioni
            </DialogTitle>
            <DialogDescription>
              {managingPolicy?.policy_number || <span className="text-amber-600 italic">N. Polizza da inserire</span>} - {managingPolicy?.company} | Cliente: {managingPolicy?.client_name}
            </DialogDescription>
          </DialogHeader>

          {/* Info Polizza Header */}
          <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-slate-500 text-xs">Premio</p>
              <p className="font-semibold text-emerald-700">€{managingPolicy?.premium_amount?.toLocaleString('it-IT') || 0}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Agenzia</p>
              <p className="font-medium">{managingPolicy?.agent || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Compagnia</p>
              <p className="font-medium">{managingPolicy?.company || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Status</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getPolicyPaymentStatus().color}`}>
                {getPolicyPaymentStatus().icon} {getPolicyPaymentStatus().label}
              </span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
            <button
              onClick={() => { setPaymentsModalTab('unificato'); resetAccountingForm(); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                paymentsModalTab === 'unificato' 
                  ? 'border-indigo-500 text-indigo-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Registrazione Unificata
            </button>
            <button
              onClick={() => { setPaymentsModalTab('incasso'); resetAccountingForm(); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                paymentsModalTab === 'incasso' 
                  ? 'border-emerald-500 text-emerald-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Incasso
            </button>
            <button
              onClick={() => { setPaymentsModalTab('uscita'); resetAccountingForm(); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                paymentsModalTab === 'uscita' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Uscita
            </button>
            <button
              onClick={() => { setPaymentsModalTab('provv_entrata'); resetAccountingForm(); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                paymentsModalTab === 'provv_entrata' 
                  ? 'border-cyan-500 text-cyan-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Provv. Entrata
            </button>
            <button
              onClick={() => { setPaymentsModalTab('provv_uscita'); resetAccountingForm(); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                paymentsModalTab === 'provv_uscita' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Provv. Uscita
            </button>
          </div>

          {/* Tab Content - Registrazione Unificata */}
          {paymentsModalTab === 'unificato' && (
            <div className="space-y-4">
              <div className="bg-indigo-50 rounded-lg p-3 text-sm border border-indigo-200">
                <p className="font-medium text-indigo-800">Registrazione One-Shot</p>
                <p className="text-indigo-600 text-xs mt-1">Registra contemporaneamente incassi, uscite e provvigioni in un unico salvataggio</p>
              </div>
              
              {/* Data comune */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Operazione *</Label>
                  <DateInput
                    value={accountingFormData.payment_date}
                    onChange={(val) => setAccountingFormData({...accountingFormData, payment_date: val})}
                    data-testid="unificato-date"
                    placeholder="gg/mm/aaaa"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Note (opzionale)</Label>
                  <Input
                    value={accountingFormData.notes}
                    onChange={(e) => setAccountingFormData({...accountingFormData, notes: e.target.value})}
                    placeholder="Note comuni..."
                    data-testid="unificato-notes"
                  />
                </div>
              </div>

              {/* Griglia 2x2 per le 4 voci */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Incasso Cliente */}
                <div className="p-4 border border-emerald-200 rounded-lg bg-emerald-50/50">
                  <p className="font-medium text-emerald-800 text-sm mb-3">Incasso Cliente</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Importo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={accountingFormData.amount}
                        onChange={(e) => setAccountingFormData({...accountingFormData, amount: e.target.value})}
                        className="h-8"
                        data-testid="unificato-incasso-amount"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Metodo</Label>
                      <Select value={accountingFormData.payment_method} onValueChange={(v) => setAccountingFormData({...accountingFormData, payment_method: v})}>
                        <SelectTrigger className="h-8" data-testid="unificato-incasso-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_header_incasso" disabled className="font-bold text-slate-500 bg-slate-100">--- SEZIONE INCASSO ---</SelectItem>
                          {PAYMENT_METHODS_INCASSO.map(method => (
                            <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Pagamento Fornitore */}
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50/50">
                  <p className="font-medium text-blue-800 text-sm mb-3">Pagamento Fornitore</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Importo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={accountingFormData.uscita_amount || 0}
                        onChange={(e) => setAccountingFormData({...accountingFormData, uscita_amount: e.target.value})}
                        className="h-8"
                        data-testid="unificato-uscita-amount"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Metodo</Label>
                      <Select value={accountingFormData.uscita_method || 'bonifico_drop'} onValueChange={(v) => setAccountingFormData({...accountingFormData, uscita_method: v})}>
                        <SelectTrigger className="h-8" data-testid="unificato-uscita-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_header_uscita" disabled className="font-bold text-slate-500 bg-slate-100">--- SEZIONE USCITA ---</SelectItem>
                          {PAYMENT_METHODS_USCITA.map(method => (
                            <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Provvigione Entrata */}
                <div className="p-4 border border-cyan-200 rounded-lg bg-cyan-50/50">
                  <p className="font-medium text-cyan-800 text-sm mb-2">Provvigione Entrata</p>
                  <div className="bg-cyan-100/50 rounded px-2 py-1 mb-3">
                    <p className="text-xs text-cyan-600">Collegata a:</p>
                    <p className="font-semibold text-cyan-800">{managingPolicy?.agent || 'N/A'}</p>
                    <p className="text-xs text-cyan-500">(Agenzia - non modificabile)</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Importo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={accountingFormData.commission_in}
                        onChange={(e) => setAccountingFormData({...accountingFormData, commission_in: e.target.value})}
                        className="h-8"
                        placeholder="0 = da definire"
                        data-testid="unificato-provv-in-amount"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Stato</Label>
                      <Select value={accountingFormData.commission_in_status} onValueChange={(v) => setAccountingFormData({...accountingFormData, commission_in_status: v})}>
                        <SelectTrigger className="h-8" data-testid="unificato-provv-in-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="da_definire">Da Definire</SelectItem>
                          <SelectItem value="incassato">Incassato</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Provvigione Uscita */}
                <div className="p-4 border border-purple-200 rounded-lg bg-purple-50/50">
                  <p className="font-medium text-purple-800 text-sm mb-2">Provvigione Uscita</p>
                  <div className="bg-purple-100/50 rounded px-2 py-1 mb-3">
                    <p className="text-xs text-purple-600">Collegata a:</p>
                    <p className="font-semibold text-purple-800">{selectedClient?.agent_source || 'N/A'}</p>
                    <p className="text-xs text-purple-500">(Agente/Fonte - non modificabile)</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Importo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={accountingFormData.commission_out}
                        onChange={(e) => setAccountingFormData({...accountingFormData, commission_out: e.target.value})}
                        className="h-8"
                        placeholder="0 = da definire"
                        data-testid="unificato-provv-out-amount"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Stato</Label>
                      <Select value={accountingFormData.commission_out_status} onValueChange={(v) => setAccountingFormData({...accountingFormData, commission_out_status: v})}>
                        <SelectTrigger className="h-8" data-testid="unificato-provv-out-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="da_definire">Da Definire</SelectItem>
                          <SelectItem value="saldato">Saldato</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentsModalOpen(false)} className="flex-1">
                  Chiudi
                </Button>
                <Button onClick={handleRegisterUnificato} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="register-unificato-btn">
                  <DollarSign className="w-4 h-4 mr-2" /> Registra Tutto
                </Button>
              </div>
            </div>
          )}

          {/* Tab Content */}
          {paymentsModalTab === 'incasso' && (
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-200">
                <p className="font-medium text-emerald-800">Registra Incasso Cliente</p>
                <p className="text-emerald-600 text-xs mt-1">Registra il pagamento ricevuto dal cliente per questa polizza</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={accountingFormData.amount}
                    onChange={(e) => setAccountingFormData({...accountingFormData, amount: e.target.value})}
                    data-testid="incasso-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Incasso *</Label>
                  <DateInput
                    value={accountingFormData.payment_date}
                    onChange={(val) => setAccountingFormData({...accountingFormData, payment_date: val})}
                    data-testid="incasso-date"
                    placeholder="gg/mm/aaaa"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Metodo Pagamento</Label>
                <Select value={accountingFormData.payment_method} onValueChange={(v) => setAccountingFormData({...accountingFormData, payment_method: v})}>
                  <SelectTrigger data-testid="incasso-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_header_incasso" disabled className="font-bold text-slate-500 bg-slate-100">--- SEZIONE INCASSO ---</SelectItem>
                    {PAYMENT_METHODS_INCASSO.map(method => (
                      <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={accountingFormData.notes}
                  onChange={(e) => setAccountingFormData({...accountingFormData, notes: e.target.value})}
                  rows={2}
                  placeholder="Note opzionali..."
                  data-testid="incasso-notes"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentsModalOpen(false)} className="flex-1">
                  Chiudi
                </Button>
                {editingPayment ? (
                  <Button onClick={handleUpdatePayment} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    <Pencil className="w-4 h-4 mr-2" /> Salva Modifiche
                  </Button>
                ) : (
                  <Button onClick={handleRegisterIncasso} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="register-incasso-btn">
                    <DollarSign className="w-4 h-4 mr-2" /> Registra Incasso
                  </Button>
                )}
              </div>
            </div>
          )}

          {paymentsModalTab === 'uscita' && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm border border-blue-200">
                <p className="font-medium text-blue-800">Registra Pagamento Fornitore</p>
                <p className="text-blue-600 text-xs mt-1">Registra il pagamento verso agenzia o compagnia assicurativa</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={accountingFormData.amount}
                    onChange={(e) => setAccountingFormData({...accountingFormData, amount: e.target.value})}
                    data-testid="uscita-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Pagamento *</Label>
                  <DateInput
                    value={accountingFormData.payment_date}
                    onChange={(val) => setAccountingFormData({...accountingFormData, payment_date: val})}
                    data-testid="uscita-date"
                    placeholder="gg/mm/aaaa"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Metodo Pagamento</Label>
                <Select value={accountingFormData.payment_method} onValueChange={(v) => setAccountingFormData({...accountingFormData, payment_method: v})}>
                  <SelectTrigger data-testid="uscita-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_header_uscita" disabled className="font-bold text-slate-500 bg-slate-100">--- SEZIONE USCITA ---</SelectItem>
                    {PAYMENT_METHODS_USCITA.map(method => (
                      <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={accountingFormData.notes}
                  onChange={(e) => setAccountingFormData({...accountingFormData, notes: e.target.value})}
                  rows={2}
                  placeholder="Note opzionali..."
                  data-testid="uscita-notes"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentsModalOpen(false)} className="flex-1">
                  Chiudi
                </Button>
                {editingPayment ? (
                  <Button onClick={handleUpdatePayment} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    <Pencil className="w-4 h-4 mr-2" /> Salva Modifiche
                  </Button>
                ) : (
                  <Button onClick={handleRegisterUscita} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="register-uscita-btn">
                    <Banknote className="w-4 h-4 mr-2" /> Registra Pagamento
                  </Button>
                )}
              </div>
            </div>
          )}

          {paymentsModalTab === 'provv_entrata' && (
            <div className="space-y-4">
              <div className="bg-cyan-50 rounded-lg p-3 text-sm border border-cyan-200">
                <p className="font-medium text-cyan-800">Registra Provvigione Entrata</p>
                <p className="text-cyan-600 text-xs mt-1">Margine/guadagno dell'agenzia su questa polizza</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo Provvigione (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={accountingFormData.commission_in}
                    onChange={(e) => setAccountingFormData({...accountingFormData, commission_in: e.target.value})}
                    data-testid="provv-in-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <DateInput
                    value={accountingFormData.payment_date}
                    onChange={(val) => setAccountingFormData({...accountingFormData, payment_date: val})}
                    data-testid="provv-in-date"
                    placeholder="gg/mm/aaaa"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Stato Provvigione</Label>
                <Select value={accountingFormData.commission_in_status} onValueChange={(v) => setAccountingFormData({...accountingFormData, commission_in_status: v})}>
                  <SelectTrigger data-testid="provv-in-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_definire">Da Definire</SelectItem>
                    <SelectItem value="incassato">Incassato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={accountingFormData.notes}
                  onChange={(e) => setAccountingFormData({...accountingFormData, notes: e.target.value})}
                  rows={2}
                  placeholder="Note opzionali..."
                  data-testid="provv-in-notes"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentsModalOpen(false)} className="flex-1">
                  Chiudi
                </Button>
                {editingPayment ? (
                  <Button onClick={handleUpdatePayment} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                    <Pencil className="w-4 h-4 mr-2" /> Salva Modifiche
                  </Button>
                ) : (
                  <Button onClick={handleRegisterProvvigioneEntrata} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="register-provv-in-btn">
                    <DollarSign className="w-4 h-4 mr-2" /> Registra Provvigione
                  </Button>
                )}
              </div>
            </div>
          )}

          {paymentsModalTab === 'provv_uscita' && (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-3 text-sm border border-purple-200">
                <p className="font-medium text-purple-800">Registra Provvigione Uscita</p>
                <p className="text-purple-600 text-xs mt-1">Storno o pagamento a collaboratori/agenti esterni</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo Provvigione (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={accountingFormData.commission_out}
                    onChange={(e) => setAccountingFormData({...accountingFormData, commission_out: e.target.value})}
                    data-testid="provv-out-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <DateInput
                    value={accountingFormData.payment_date}
                    onChange={(val) => setAccountingFormData({...accountingFormData, payment_date: val})}
                    data-testid="provv-out-date"
                    placeholder="gg/mm/aaaa"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Stato Provvigione</Label>
                <Select value={accountingFormData.commission_out_status} onValueChange={(v) => setAccountingFormData({...accountingFormData, commission_out_status: v})}>
                  <SelectTrigger data-testid="provv-out-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_definire">Da Definire</SelectItem>
                    <SelectItem value="saldato">Saldato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Collaboratore/Agente</Label>
                <Input
                  value={accountingFormData.collaboratore}
                  onChange={(e) => setAccountingFormData({...accountingFormData, collaboratore: e.target.value})}
                  placeholder="Nome collaboratore..."
                  data-testid="provv-out-collaboratore"
                />
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={accountingFormData.notes}
                  onChange={(e) => setAccountingFormData({...accountingFormData, notes: e.target.value})}
                  rows={2}
                  placeholder="Note opzionali..."
                  data-testid="provv-out-notes"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentsModalOpen(false)} className="flex-1">
                  Chiudi
                </Button>
                {editingPayment ? (
                  <Button onClick={handleUpdatePayment} className="flex-1 bg-purple-600 hover:bg-purple-700">
                    <Pencil className="w-4 h-4 mr-2" /> Salva Modifiche
                  </Button>
                ) : (
                  <Button onClick={handleRegisterProvvigioneUscita} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="register-provv-out-btn">
                    <Banknote className="w-4 h-4 mr-2" /> Registra Provvigione
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Lista movimenti esistenti */}
          {policyPayments.length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-700">Movimenti Registrati ({policyPayments.length})</p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setResetPaymentsConfirmOpen(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                  data-testid="btn-reset-payments"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Azzera Movimenti
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {policyPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        payment.type === 'incasso' ? 'bg-emerald-500' :
                        payment.type === 'uscita' ? 'bg-blue-500' :
                        payment.type === 'provvigione_entrata' ? 'bg-cyan-500' :
                        'bg-purple-500'
                      }`} />
                      <div>
                        <p className="font-medium">
                          {payment.type === 'incasso' ? 'Incasso' :
                           payment.type === 'uscita' ? 'Uscita Fornitore' :
                           payment.type === 'provvigione_entrata' ? 'Provv. Entrata' :
                           payment.type === 'provvigione_uscita' ? 'Provv. Uscita' : payment.type}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(payment.payment_date).toLocaleDateString('it-IT')}
                          {payment.collaboratore && ` - ${payment.collaboratore}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold ${
                        payment.type === 'incasso' || payment.type === 'provvigione_entrata' ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {payment.type === 'incasso' || payment.type === 'uscita' 
                          ? `€${payment.amount?.toLocaleString('it-IT')}` 
                          : payment.type === 'provvigione_entrata'
                            ? `€${payment.commission_in?.toLocaleString('it-IT')}`
                            : `€${payment.commission_out?.toLocaleString('it-IT')}`
                        }
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => handleEditPayment(payment)} className="h-7 w-7">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeletePayment(payment.id)} className="h-7 w-7 text-red-500 hover:text-red-700">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Modal: Azzera Movimenti */}
      <Dialog open={resetPaymentsConfirmOpen} onOpenChange={setResetPaymentsConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Conferma Azzeramento
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              Sei sicuro di voler cancellare <strong>TUTTI i movimenti registrati</strong> per questa polizza?
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <p className="text-sm text-red-800">
              <strong>Attenzione:</strong> Questa operazione non è annullabile. Verranno eliminati tutti gli incassi, uscite e provvigioni associati a questa polizza.
            </p>
          </div>
          
          {managingPolicy && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p><span className="text-slate-500">Polizza:</span> <span className="font-medium">{managingPolicy.policy_number}</span></p>
              <p><span className="text-slate-500">Movimenti da eliminare:</span> <span className="font-medium text-red-600">{policyPayments.length}</span></p>
            </div>
          )}
          
          <DialogFooter className="mt-4 gap-2">
            <Button 
              variant="outline" 
              onClick={() => setResetPaymentsConfirmOpen(false)}
              disabled={resettingPayments}
            >
              Annulla
            </Button>
            <Button 
              variant="destructive"
              onClick={handleResetAllPayments}
              disabled={resettingPayments}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirm-reset-payments"
            >
              {resettingPayments ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Eliminazione...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Conferma Azzeramento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Converti in Polizza */}
      <Dialog open={conversionDialogOpen} onOpenChange={setConversionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Converti in Polizza
            </DialogTitle>
            <DialogDescription>
              Conferma la data di decorrenza per creare la polizza dal preventivo.
            </DialogDescription>
          </DialogHeader>
          
          {quoteToConvert && (
            <div className="space-y-4 mt-4">
              {/* Riepilogo preventivo */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-500">Preventivo:</span> <span className="font-medium">{quoteToConvert.quote_number || `PRV-${quoteToConvert.id?.slice(0, 8).toUpperCase() || 'N/A'}`}</span></p>
                <p><span className="text-slate-500">Compagnia:</span> <span className="font-medium">{quoteToConvert.options?.[0]?.company || quoteToConvert.company || 'N/A'}</span></p>
                <p><span className="text-slate-500">Premio:</span> <span className="font-medium">€{quoteToConvert.options?.[0]?.premium?.toLocaleString('it-IT') || '0'}</span></p>
              </div>

              {/* Data Decorrenza */}
              <div className="space-y-2">
                <Label htmlFor="conversion-start-date">Data Decorrenza *</Label>
                <DateInput
                  id="conversion-start-date"
                  value={conversionStartDate}
                  onChange={(val) => setConversionStartDate(val)}
                  data-testid="conversion-start-date"
                  placeholder="gg/mm/aaaa"
                />
                <p className="text-xs text-slate-500">
                  Data di inizio copertura della polizza.
                </p>
              </div>

              {/* Azioni */}
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setConversionDialogOpen(false);
                    setQuoteToConvert(null);
                  }}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleConvertToPolicy}
                  disabled={!conversionStartDate}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-conversion-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Conferma Conversione
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Anteprima Documento - Portal per evitare intercettazione eventi DialogOverlay */}
      {previewDoc && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-black/80 flex flex-col" 
          style={{ pointerEvents: 'auto' }}
          data-testid="document-preview-modal"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0" style={{ pointerEvents: 'auto' }}>
            <div className="flex items-center gap-3">
              <span className="font-medium text-sm truncate max-w-[300px]">{previewDoc.description || previewDoc.filename}</span>
              <span className="text-xs text-slate-400">
                {(previewDoc.file_type || '').startsWith('image/') ? 'Immagine' : 'PDF'}
              </span>
            </div>
            <div className="flex items-center gap-1" style={{ pointerEvents: 'auto' }}>
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); setPreviewZoom(z => Math.max(0.25, z - 0.25)); }} data-testid="preview-zoom-out">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs w-14 text-center">{Math.round(previewZoom * 100)}%</span>
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); setPreviewZoom(z => Math.min(3, z + 0.25)); }} data-testid="preview-zoom-in">
                <ZoomIn className="w-4 h-4" />
              </Button>
              <div className="w-px h-5 bg-slate-600 mx-1" />
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); handleDownloadDoc(); }} data-testid="preview-download">
                <Download className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); handlePrintDoc(); }} data-testid="preview-print">
                <Printer className="w-4 h-4" />
              </Button>
              <div className="w-px h-5 bg-slate-600 mx-1" />
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700" onClick={(e) => handleClosePreview(e)} data-testid="preview-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }} onClick={(e) => handleClosePreview(e)}>
            {previewLoading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
                <span className="text-white text-sm">Caricamento anteprima...</span>
              </div>
            ) : previewBlobUrl ? (
              <div onClick={(e) => e.stopPropagation()} className="w-full h-full flex items-center justify-center">
                {(previewDoc.file_type || '').startsWith('image/') ? (
                  <img
                    src={previewBlobUrl}
                    alt={previewDoc.filename}
                    style={{ transform: `scale(${previewZoom})`, transformOrigin: 'center center', transition: 'transform 0.2s' }}
                    className="max-w-full max-h-[calc(100vh-80px)] rounded shadow-lg"
                    data-testid="preview-image"
                  />
                ) : (
                  <embed
                    src={previewBlobUrl}
                    type="application/pdf"
                    style={{ 
                      width: `${Math.min(95, Math.round(80 * previewZoom))}vw`, 
                      height: `${Math.min(95, Math.round(85 * previewZoom))}vh`,
                      transition: 'all 0.2s' 
                    }}
                    className="rounded shadow-lg"
                    data-testid="preview-pdf"
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>
      , document.body)}

    </div>
  );
};

export default Clients;
